import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useFormContext, useForm, FormProvider } from "react-hook-form";
import { Button, Col, Row } from "reactstrap";
import {
  Button as AntdButton,
  Card,
  Table,
  Space,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Divider,
  Tag,
  Tooltip,
  Select,
  Drawer,
  Upload,
  Popover,
  Badge,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckOutlined,
  InfoCircleOutlined,
  ScheduleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import FormSelect from "../../form-components/SelectComponent";
import FormInput from "../../form-components/InputComponent";
import {
  WorkOrderFormData,
  WorkOrderLineSource,
  WorkOrderLine,
  ProjectTask,
  WorkBoqLine,
  TaxApplicationLevel,
  WorkType,
} from "../types/index";
import dropdownApis from "../../../api/WorkOrderManagement/DropdownApis";
import { indianCurrencyFormatter, indianCurrencyParser } from "../../../utils/Utils";
import workOrderApis from "../../../api/WorkOrderManagement/WorkOrderApis";
import workBoqApis from "../../../api/WorkOrderManagement/WorkBOQ";
import workRequestApis from "../../../api/WorkOrderManagement/WorkRequestApis";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import WorkOrderLineBulkUpload from "./WorkOrderLineBulkUpload";
import TextArea from "antd/es/input/TextArea";
import { render } from "@testing-library/react";
import StageBreakdown, { Stage } from "./StageBreakdown";
import FormDatePicker from "../../form-components/DatePicker";
import dayjs from "dayjs";

interface SelectOptions {
  label: string;
  value: string;
}

const WorkOrderDetails: React.FC<any> = (props: any) => {
  const {
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<WorkOrderFormData>();

  const location = useLocation();
  const flowTypeParam = (() => {
    const raw = new URLSearchParams(location.search).get("flowType") || "";
    if (raw.toLowerCase() === "client") return "Client";
    if (raw.toLowerCase() === "contractor") return "Contractor";
    return raw || undefined;
  })();

  // Watch form values
  const siteIdWatch = watch("siteId");
  const projectIdWatch = watch("projectId");
  const selectedSourceWatch = watch("selectedSource");
  const workOrderLinesWatch = watch("workOrderLines") || [];
  const enableTaxWatch = watch("enableTax");
  const taxApplicationLevelWatch = watch("taxApplicationLevel");
  let workLevelWatch = watch("workLevel");
  const formTypeWatch = watch("type"); // Watch for "create" or "edit" mode

  // Check if there are existing New Work Line entries
  const hasNewWorkLineEntries = workOrderLinesWatch.some(
    (line: any) => line.source === WorkOrderLineSource.NewWorkLine
  );

  // Initialize budget allocation state for edit mode
  useEffect(() => {
    if (workOrderLinesWatch.length > 0) {
      console.log(
        "Initializing budget allocations for edit mode:",
        workOrderLinesWatch
      );

      // Initialize task budget allocations for Project Management lines
      const newTaskBudgetAllocations: { [key: string]: number } = {};
      // Initialize BOQ line budget allocations for Work BOQ lines
      const newBoqLineBudgetAllocations: { [key: string]: number } = {};

      workOrderLinesWatch.forEach((line: any) => {
        if (line.source === "Project Management" && line.sourceId) {
          newTaskBudgetAllocations[line.sourceId] = line.budgetAllocation || 0;
          console.log(
            `Initialized task ${line.sourceId} budget allocation:`,
            line.budgetAllocation
          );
        } else if (line.source === "Work BOQ") {
          // For Work BOQ lines, use sourceId if available, otherwise use id
          const boqLineKey = line.sourceId || line.id;
          if (boqLineKey) {
            newBoqLineBudgetAllocations[boqLineKey] =
              line.budgetAllocation || 0;
            console.log(
              `Initialized BOQ line ${boqLineKey} budget allocation:`,
              line.budgetAllocation
            );
          }
        }
      });

      setTaskBudgetAllocations(newTaskBudgetAllocations);
      setBoqLineBudgetAllocations(newBoqLineBudgetAllocations);
      console.log("Initialized budget allocations:", {
        taskBudgetAllocations: newTaskBudgetAllocations,
        boqLineBudgetAllocations: newBoqLineBudgetAllocations,
      });
    }
  }, [workOrderLinesWatch]);

  // State for previous source to track changes
  const [previousSource, setPreviousSource] =
    useState<WorkOrderLineSource | null>(null);
  const [isSourceChanging, setIsSourceChanging] = useState(false);

  // State for previous project to track changes
  const [previousProjectId, setPreviousProjectId] = useState<string | null>(
    null
  );

  // State tracking for GST calculation
  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const [currentUserState, setCurrentUserState] = useState<string>("");
  const [contractorState, setContractorState] = useState<string>("");

  // State for budget header ID
  const [budgetHeaderId, setBudgetHeaderId] = useState<number | null>(null);

  // State for New Work Line budget functionality
  const [selectedBudgetId, setSelectedBudgetId] = useState<string>("");
  const [budgetOptions, setBudgetOptions] = useState<SelectOptions[]>([]);
  const [budgetLines, setBudgetLines] = useState<any[]>([]);
  const [budgetLinesLoading, setBudgetLinesLoading] = useState(false);

  // Helper function to validate if task dates are within work order dates
  const validateTaskDates = (startDate: any, endDate: any): { valid: boolean; message?: string } => {
    const workOrderStartDate = getValues("plannedStartDate");
    const workOrderEndDate = getValues("plannedEndDate");

    // If no dates are provided, validation passes (optional fields)
    if (!startDate || !endDate) {
      return { valid: true };
    }

    const taskStart = dayjs(startDate);
    const taskEnd = dayjs(endDate);

    // Validate start date <= end date
    if (taskEnd.isBefore(taskStart, 'day')) {
      return {
        valid: false,
        message: "Planned End Date must be after Planned Start Date"
      };
    }

    // If work order dates are set, validate against them
    if (workOrderStartDate && taskStart.isBefore(dayjs(workOrderStartDate), 'day')) {
      return {
        valid: false,
        message: `Planned Start Date cannot be before Work Order Start Date (${dayjs(workOrderStartDate).format('DD-MMM-YYYY')})`
      };
    }

    if (workOrderEndDate && taskEnd.isAfter(dayjs(workOrderEndDate), 'day')) {
      return {
        valid: false,
        message: `Planned End Date cannot be after Work Order End Date (${dayjs(workOrderEndDate).format('DD-MMM-YYYY')})`
      };
    }

    return { valid: true };
  };

  // Initialize with default values if not set
  React.useEffect(() => {
    // Don't set default source - let user choose
    if (!getValues("workOrderLines")) {
      setValue("workOrderLines", []);
    }

    // Set initial previous source only if one exists
    const currentSource = getValues("selectedSource");
    if (currentSource && !previousSource) {
      setPreviousSource(currentSource);
    }

    // If project is already selected and source is PM, load activities
    if (
      projectIdWatch &&
      selectedSourceWatch === WorkOrderLineSource.ProjectManagement
    ) {
      console.log(
        "Component mount: Loading activities for existing project:",
        projectIdWatch
      );
      fetchProjectActivities();
    }
  }, [setValue, getValues]);

  // State for options
  const [activityOptions, setActivityOptions] = useState<SelectOptions[]>([]);
  const [workCategoryOptions, setWorkCategoryOptions] = useState<
    SelectOptions[]
  >([]);
  const [workBoqOptions, setWorkBoqOptions] = useState<SelectOptions[]>([]);
  const [uomOptions, setUomOptions] = useState<SelectOptions[]>([]);
  const [taxOptions, setTaxOptions] = useState<SelectOptions[]>([]);
  const [projectOptions, setProjectOptions] = useState<SelectOptions[]>([]);

  // State for data
  const [availableTasks, setAvailableTasks] = useState<ProjectTask[]>([]);
  const [availableBoqLines, setAvailableBoqLines] = useState<WorkBoqLine[]>([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [selectedBoqLineIds, setSelectedBoqLineIds] = useState<string[]>([]);

  // State for task rates and weightages
  const [taskRates, setTaskRates] = useState<{ [key: string]: number }>({});
  const [taskWeightages, setTaskWeightages] = useState<{
    [key: string]: number;
  }>({});
  const [taskRequestedQuantities, setTaskRequestedQuantities] = useState<{
    [key: string]: number;
  }>({});
  const [taskTaxTypes, setTaskTaxTypes] = useState<{ [key: string]: string }>(
    {}
  );
  const [taskBudgetAllocations, setTaskBudgetAllocations] = useState<{
    [key: string]: number;
  }>({});
  const [taskRateRemarks, setTaskRateRemarks] = useState<{
    [key: string]: string;
  }>({});
  // State for storing files per task (taskId -> fileList)
  const [taskFiles, setTaskFiles] = useState<{
    [key: string]: any[];
  }>({});
  // State for storing files per BOQ line (lineId -> fileList)
  const [boqLineFiles, setBoqLineFiles] = useState<{
    [key: string]: any[];
  }>({});

  // State for BOQ line rates and weightages
  const [boqLineRates, setBoqLineRates] = useState<{ [key: string]: number }>(
    {}
  );
  const [boqLineWeightages, setBoqLineWeightages] = useState<{
    [key: string]: number;
  }>({});
  const [boqLineRequestedQuantities, setBoqLineRequestedQuantities] = useState<{
    [key: string]: number;
  }>({});
  const [boqLineTaxTypes, setBoqLineTaxTypes] = useState<{
    [key: string]: string;
  }>({});
  const [boqLineBudgetAllocations, setBoqLineBudgetAllocations] = useState<{
    [key: string]: number;
  }>({});
  const [boqLineRateRemarks, setBoqLineRateRemarks] = useState<{
    [key: string]: string;
  }>({});

  // Modal states
  const [isTaskSelectionModalVisible, setIsTaskSelectionModalVisible] =
    useState(false);
  const [isBoqSelectionModalVisible, setIsBoqSelectionModalVisible] =
    useState(false);
  const [isNewWorkLineModalVisible, setIsNewWorkLineModalVisible] =
    useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingLineIndex, setEditingLineIndex] = useState<number>(-1);

  // State for location dropdown options (Tower/Block, Floor, Flat, Location)
  const [towerVillaOptions, setTowerVillaOptions] = useState<SelectOptions[]>(
    []
  );
  const [floorOptions, setFloorOptions] = useState<SelectOptions[]>([]);
  const [flatOptions, setFlatOptions] = useState<SelectOptions[]>([]);
  const [locationOptions, setLocationOptions] = useState<SelectOptions[]>([]);

  // Refs to track previous values for cascading dropdown logic
  const prevTowerVillaRef = React.useRef<string>("");
  const prevFloorNameRef = React.useRef<string>("");
  const [isInitializingEdit, setIsInitializingEdit] = useState(false);

  // Form for new/edit work line using react-hook-form
  const newWorkLineFormMethods = useForm({
    defaultValues: {
      workCategory: "",
      subCategory: "",
      taskTitle: "",
      taskDescription: "",
      quantity: "",
      uom: "",
      weightagePercentage: "",
      rate: "",
      taxType: "",
      taxPercentage: "",
      budgetLineId: "",
      budgetAllocation: "",
      towerVilla: "",
      floorName: "",
      flatName: "",
      locationId: "",
      scheduledStartDate: "",
      scheduledEndDate: "",
    },
    mode: "onChange",
  });

  // State for schedule breakdown in New Work Line
  const [newLineStages, setNewLineStages] = useState<Stage[]>([]);
  const [newLineScheduleEnabled, setNewLineScheduleEnabled] = useState(false);
  const [newLineDeletedStageIds, setNewLineDeletedStageIds] = useState<
    number[]
  >([]);

  // State for schedule breakdown drawer (for PM/BOQ/New Work Lines in table)
  const [scheduleDrawerVisible, setScheduleDrawerVisible] = useState(false);
  const [scheduleEditingLineIndex, setScheduleEditingLineIndex] =
    useState<number>(-1);
  const [scheduleLineStages, setScheduleLineStages] = useState<Stage[]>([]);
  const [scheduleDeletedStageIds, setScheduleDeletedStageIds] = useState<
    number[]
  >([]);

  // State for file uploads
  const [projectManagementFileList, setProjectManagementFileList] = useState<
    any[]
  >([]);
  const [workBoqFileList, setWorkBoqFileList] = useState<any[]>([]);
  const [newWorkLineFileList, setNewWorkLineFileList] = useState<any[]>([]);
  const [uploadLoading, setUploadLoading] = useState(false);

  // Load files from form context when editing (files loaded from API)
  useEffect(() => {
    const formProjectFiles = (getValues() as any).projectManagementFiles || [];
    const formWorkBoqFiles = (getValues() as any).workBoqFiles || [];
    const formNewWorkLineFiles = (getValues() as any).newWorkLineFiles || [];

    // Only update if we have files from form context and state is empty
    // This prevents overwriting newly uploaded files
    if (formProjectFiles.length > 0 && projectManagementFileList.length === 0) {
      // Check if files are from API (have apiFileData) or are File objects
      const formattedFiles = formProjectFiles.map((file: any) => {
        if (file.apiFileData) {
          // File from API - already in correct format
          return file;
        } else if (file instanceof File) {
          // New File object - convert to fileList format
          return {
            uid: `${Date.now()}-${Math.random()}`,
            name: file.name,
            status: "done" as const,
            url: URL.createObjectURL(file),
            originFileObj: file,
          };
        } else {
          // Already in fileList format
          return file;
        }
      });
      setProjectManagementFileList(formattedFiles);
    }

    if (formWorkBoqFiles.length > 0 && workBoqFileList.length === 0) {
      const formattedFiles = formWorkBoqFiles.map((file: any) => {
        if (file.apiFileData) {
          return file;
        } else if (file instanceof File) {
          return {
            uid: `${Date.now()}-${Math.random()}`,
            name: file.name,
            status: "done" as const,
            url: URL.createObjectURL(file),
            originFileObj: file,
          };
        } else {
          return file;
        }
      });
      setWorkBoqFileList(formattedFiles);
    }

    if (formNewWorkLineFiles.length > 0 && newWorkLineFileList.length === 0) {
      const formattedFiles = formNewWorkLineFiles.map((file: any) => {
        if (file.apiFileData) {
          return file;
        } else if (file instanceof File) {
          return {
            uid: `${Date.now()}-${Math.random()}`,
            name: file.name,
            status: "done" as const,
            url: URL.createObjectURL(file),
            originFileObj: file,
          };
        } else {
          return file;
        }
      });
      setNewWorkLineFileList(formattedFiles);
    }
  }, [formTypeWatch]); // Run when form type changes (edit mode)

  // Watch workType from main form (header level)
  const workTypeWatch = watch("workType");

  // Auto-set quantity to 0 when workType is Open
  useEffect(() => {
    if (workTypeWatch === WorkType.Open) {
      newWorkLineFormMethods.setValue("quantity", "0", {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [workTypeWatch, newWorkLineFormMethods]);

  // Auto-calculate budget allocation based on Qty * Rate + GST
  useEffect(() => {
    const subscription = newWorkLineFormMethods.watch((value, { name }) => {
      // Only recalculate if quantity, rate, taxType, or budgetLineId changes
      if (
        name === "quantity" ||
        name === "rate" ||
        name === "taxType" ||
        name === "budgetLineId"
      ) {
        const workCategory = value.workCategory;
        const taskTitle = value.taskTitle;
        const workType = getValues("workType"); // Get from main form
        const quantity = parseFloat(value.quantity || "0");
        const uom = value.uom;
        const rate = parseFloat(value.rate || "0");
        const budgetLineId = value.budgetLineId;
        const taxTypeId = value.taxType;

        // Check if all required fields are filled based on workType
        // For Open workType, quantity is not required (set to 0)
        const allRequiredFieldsFilled =
          workCategory &&
          taskTitle &&
          (workType === WorkType.Open || quantity > 0) &&
          uom &&
          rate > 0 &&
          budgetLineId;

        if (allRequiredFieldsFilled) {
          // For Open workType, use quantity as 0 for budget calculation
          const effectiveQuantity = workType === WorkType.Open ? 0 : quantity;
          const lineAmount = effectiveQuantity * rate;
          let totalWithTax = lineAmount;

          // Calculate tax if tax is enabled and tax type is selected
          if (
            enableTaxWatch === "true" &&
            (taxApplicationLevelWatch === TaxApplicationLevel.Line ||
              taxApplicationLevelWatch === TaxApplicationLevel.Both) &&
            taxTypeId
          ) {
            // Find tax percentage from tax options
            const selectedTax = taxOptions.find(
              (tax: any) => tax.value === taxTypeId
            );
            if (selectedTax) {
              // Extract percentage from label (e.g., "GST - 18%" -> 18)
              const taxPercentage = parseFloat(
                selectedTax.label.match(/(\d+(\.\d+)?)/)?.[0] || "0"
              );
              if (taxPercentage > 0) {
                const taxAmount = (lineAmount * taxPercentage) / 100;
                totalWithTax = lineAmount + taxAmount;
              }
            }
          }

          // Auto-populate budget allocation with calculated value only if all required fields are filled
          newWorkLineFormMethods.setValue(
            "budgetAllocation",
            totalWithTax.toFixed(2),
            {
              shouldValidate: false,
              shouldDirty: true,
            }
          );
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [
    newWorkLineFormMethods,
    enableTaxWatch,
    taxApplicationLevelWatch,
    taxOptions,
  ]);

  // Source options as per FSD
  const sourceOptions: SelectOptions[] = [
    {
      label: "Project Management",
      value: WorkOrderLineSource.ProjectManagement,
    },
    { label: "Work BOQ", value: WorkOrderLineSource.WorkBOQ },
    { label: "New Work Line", value: WorkOrderLineSource.NewWorkLine },
  ];

  useEffect(() => {
    const initializeMasterData = async () => {
      console.log("Loading master data...");
      await loadMasterData();

      // Also check if we need to load activities after master data
      setTimeout(() => {
        console.log("Post master data check:", {
          projectId: projectIdWatch,
          source: selectedSourceWatch,
          activityCount: activityOptions.length,
        });

        if (
          projectIdWatch &&
          selectedSourceWatch === WorkOrderLineSource.ProjectManagement &&
          activityOptions.length === 0
        ) {
          console.log("Post-load: Triggering activity fetch");
          fetchProjectActivities();
        }
      }, 500);
    };

    initializeMasterData();
  }, []);

  useEffect(() => {
    if (siteIdWatch) {
      console.log("Site changed, fetching dependent data:", siteIdWatch);
      fetchDependentData();
    }
  }, [siteIdWatch]);

  // Set current user state for GST calculation
  useEffect(() => {
    if (currentUser?.registeredAddress?.state?.stateId) {
      setCurrentUserState(
        currentUser.registeredAddress.state.stateId.toString()
      );
      console.log(
        "Current user state ID for GST:",
        currentUser.registeredAddress.state.stateId
      );
    }
  }, [currentUser]);

  // Restore Budget Selection from form state when returning to this step
  useEffect(() => {
    const formBudgetHeaderId = getValues("budgetHeaderId");
    if (formBudgetHeaderId && !selectedBudgetId) {
      console.log("Restoring budget selection from form:", formBudgetHeaderId);
      setSelectedBudgetId(formBudgetHeaderId);
      // Re-fetch budget lines for the restored budget
      fetchBudgetLines(parseInt(formBudgetHeaderId));
    }
  }, []); // Run once on mount

  // Set contractor state from form data
  useEffect(() => {
    const getContractorState = async () => {
      const formData = getValues();
      if (formData.contractorId && formData.siteId) {
        try {
          // Get contractor state from existing contractors API
          const response = await dropdownApis.fetchContractorsBySiteIdDropdown({
            id: formData.siteId.toString(),
          });

          if (response?.status && response?.data) {
            const contractor = response.data.find((c: any) => {
              const contractorId = (
                c.contractorID ||
                c.contractorId ||
                c.id
              )?.toString();
              return contractorId === formData.contractorId;
            });

            if (contractor) {
              const stateId = contractor.state;

              if (stateId) {
                setContractorState(stateId.toString());
                console.log(
                  "Contractor state ID for GST:",
                  stateId,
                  "Name:",
                  contractor.companyName || contractor.name
                );
              }
            }
          }
        } catch (error) {
          console.error("Error fetching contractor state:", error);
        }
      }
    };

    getContractorState();
  }, [getValues]);

  useEffect(() => {
    if (projectIdWatch) {
      console.log("Project changed, fetching activities:", projectIdWatch);
      fetchProjectActivities();
    }
  }, [projectIdWatch]);

  // Fetch projects when site changes and source is Project Management
  useEffect(() => {
    if (
      siteIdWatch &&
      selectedSourceWatch === WorkOrderLineSource.ProjectManagement
    ) {
      console.log("Site and PM source available, fetching projects");
      fetchProjects();
    }
  }, [siteIdWatch, selectedSourceWatch]);

  // Initialize and track previous project ID for change detection
  useEffect(() => {
    if (projectIdWatch) {
      // Initialize previousProjectId if it's null (first time or on mount)
      if (previousProjectId === null) {
        console.log("Initializing previousProjectId:", projectIdWatch);
        setPreviousProjectId(projectIdWatch);
      } else if (projectIdWatch !== previousProjectId) {
        // Track changes for debugging
        console.log("Tracking project change:", {
          previous: previousProjectId,
          current: projectIdWatch,
        });
      }
    }
  }, [projectIdWatch, previousProjectId]);

  // Additional effect to handle when both project and source are available
  useEffect(() => {
    if (
      projectIdWatch &&
      selectedSourceWatch === WorkOrderLineSource.ProjectManagement
    ) {
      console.log(
        "Both project and PM source available, ensuring activities are loaded:",
        {
          projectId: projectIdWatch,
          source: selectedSourceWatch,
          currentActivities: activityOptions.length,
        }
      );

      // Only fetch if we don't have activities yet
      if (activityOptions.length === 0) {
        console.log("No activities found, fetching...");
        fetchProjectActivities();
      }
    }
  }, [projectIdWatch, selectedSourceWatch, activityOptions.length]);

  // Handle source changes with confirmation if there are existing work order lines
  useEffect(() => {
    if (
      selectedSourceWatch &&
      previousSource &&
      selectedSourceWatch !== previousSource &&
      !isSourceChanging
    ) {
      console.log(
        "Source changed from",
        previousSource,
        "to",
        selectedSourceWatch
      );
      console.log("Current work order lines:", workOrderLinesWatch.length);

      // Check if there are existing work order lines
      if (workOrderLinesWatch.length > 0) {
        setIsSourceChanging(true);

        Swal.fire({
          title: "Change Source?",
          text: "Changing the source will delete all added work lines. Do you want to continue?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: "Yes, Continue",
          cancelButtonText: "Cancel",
        }).then((result) => {
          if (result.isConfirmed) {
            // User confirmed - clear data and proceed
            console.log("User confirmed source change - clearing data");
            setValue("workOrderLines", []);
            clearSourceFields();
            resetSelections();
            setPreviousSource(selectedSourceWatch);
            fetchSourceData();
            // Set default Work Type to Quantity for New Work Line
            if (selectedSourceWatch === WorkOrderLineSource.NewWorkLine) {
              setValue("workType", WorkType.QTY);
            }
            message.success("Source changed and existing data cleared");
          } else {
            // User cancelled - revert to previous source
            console.log("User cancelled source change - reverting");
            setValue("selectedSource", previousSource);
          }
          setIsSourceChanging(false);
        });
      } else {
        // No existing lines - proceed with change
        console.log("No existing lines - proceeding with source change");
        clearSourceFields();
        resetSelections();
        setPreviousSource(selectedSourceWatch);
        fetchSourceData();
        // Set default Work Type to Quantity for New Work Line
        if (selectedSourceWatch === WorkOrderLineSource.NewWorkLine) {
          setValue("workType", WorkType.QTY);
        }
      }
    } else if (selectedSourceWatch && !previousSource) {
      // Initial source setting
      setPreviousSource(selectedSourceWatch);
      fetchSourceData();
      // Set default Work Type to Quantity for New Work Line
      // But only if not in edit mode (preserve API value in edit mode)
      if (selectedSourceWatch === WorkOrderLineSource.NewWorkLine) {
        const currentWorkType = getValues("workType");
        const isEditMode = getValues("type") === "edit";
        // Only set default if not in edit mode or if workType is not already set
        if (!isEditMode && !currentWorkType) {
          setValue("workType", WorkType.QTY);
        }
      }
    }
  }, [
    selectedSourceWatch,
    previousSource,
    workOrderLinesWatch.length,
    isSourceChanging,
  ]);

  // Separate effect for dependent field changes
  useEffect(() => {
    if (selectedSourceWatch && !isSourceChanging) {
      console.log("Dependent fields changed, refetching source data");
      fetchSourceData();
    }
  }, [
    watch("activityId"),
    watch("workCategoryId"),
    watch("workBoqId"),
    workTypeWatch,
  ]);

  // Effect to handle Work BOQ selection and update work categories
  useEffect(() => {
    const workBoqId = watch("workBoqId");
    if (selectedSourceWatch === WorkOrderLineSource.WorkBOQ && workBoqId) {
      console.log("Work BOQ selected, updating work categories:", workBoqId);

      // Find the selected BOQ and its categories
      const selectedBoq: any = workBoqOptions.find(
        (boq) => boq.value === workBoqId
      );
      if (selectedBoq && selectedBoq.workCategories) {
        const categoryOptions = selectedBoq.workCategories.map(
          (category: any) => ({
            label: category.workCategoryName || category.name,
            value:
              category.workCategoryId?.toString() || category.id?.toString(),
          })
        );
        console.log("Setting Work BOQ specific categories:", categoryOptions);
        setWorkCategoryOptions(categoryOptions);

        // Clear existing work category selection as options have changed
        setValue("workCategoryId", "");
      }
    }
  }, [watch("workBoqId"), selectedSourceWatch, workBoqOptions]);

  // Effect to reload all work categories and clear fields when switching to New Work Line source
  useEffect(() => {
    const reloadAllWorkCategories = async () => {
      if (
        selectedSourceWatch === WorkOrderLineSource.NewWorkLine &&
        previousSource !== null
      ) {
        console.log(
          "New Work Line source selected, reloading all work categories and clearing fields"
        );

        try {
          // Reload all work categories
          const categoryResponse = await dropdownApis.workCategoryDropDown();
          if (categoryResponse && categoryResponse.data) {
            const categoryOptions = categoryResponse.data.map(
              (category: any) => ({
                label: category.workCategoryName,
                value: category.workCategoryId?.toString(),
              })
            );
            setWorkCategoryOptions(categoryOptions);
            console.log(
              "Reloaded all work categories for New Work Line:",
              categoryOptions
            );
          }

          // Clear Budget Selection fields from other sources
          if (getValues("budgetHeaderId")) {
            console.log("Clearing budget header ID from previous source");
            setValue("budgetHeaderId", "");
            setValue("budgetHeaderName", "");
            setSelectedBudgetId("");
            setBudgetLines([]);
          }

          // Re-fetch Work Level Settings from site configuration
          const currentSiteId = getValues("siteId");
          if (currentSiteId) {
            try {
              const { data: segmentData, status: segmentStatus } =
                await dropdownApis.fetchSegmentLevelBySiteId(
                  parseInt(currentSiteId)
                );
              if (segmentStatus && segmentData) {
                const level =
                  segmentData === "Tower"
                    ? "Tower / Block"
                    : segmentData || "Site";
                setValue("workLevel", level);
                console.log("Restored work level from site config:", level);
              } else {
                setValue("workLevel", "Site");
              }
            } catch (err) {
              console.log("Error re-fetching segment level:", err);
              setValue("workLevel", "Site");
            }
          }

          // Set default Work Type to Quantity
          setValue("workType", WorkType.QTY);
        } catch (error) {
          console.error("Error reloading work categories:", error);
        }
      }
    };

    reloadAllWorkCategories();
  }, [selectedSourceWatch]);

  // Handle budget allocation change in work order lines
  const handleWorkOrderLineBudgetChange = (index: number, value: number) => {
    const currentLines = getValues("workOrderLines") || [];
    const updatedLines = [...currentLines];
    updatedLines[index] = {
      ...updatedLines[index],
      budgetAllocation: value,
    };
    setValue("workOrderLines", updatedLines);
    console.log("Updated budget allocation for line:", index, value);
  };

  // Helper function to calculate GST rates based on state comparison
  const calculateGSTRates = (taxPercentage: number) => {
    const isSameState =
      currentUserState &&
      contractorState &&
      currentUserState === contractorState;

    console.log("Current user state:", currentUserState);
    console.log("Contractor state:", contractorState);
    console.log("Same state:", isSameState);
    console.log("Tax percentage:", taxPercentage);

    if (isSameState) {
      // Same state: Split into SGST and CGST (50% each)
      const halfRate = taxPercentage / 2;
      return {
        sgstRate: halfRate,
        cgstRate: halfRate,
        igstRate: 0,
      };
    } else {
      // Different states: Use IGST (full rate)
      return {
        sgstRate: 0,
        cgstRate: 0,
        igstRate: taxPercentage,
      };
    }
  };

  const loadMasterData = async () => {
    try {
      console.log("Getting master data from APIs...");

      // Load work categories
      const categoriesResponse = await dropdownApis.workCategoryDropDown();
      if (categoriesResponse?.data) {
        const categoryOptions = categoriesResponse.data.map(
          (category: any) => ({
            label: category.workCategoryName,
            value: category.workCategoryId?.toString(),
          })
        );
        setWorkCategoryOptions(categoryOptions);
        console.log("Work categories loaded:", categoryOptions);
      }

      // Load UOM options
      const uomResponse = await dropdownApis.workUomDropDown();
      if (uomResponse?.data) {
        const uomOpts = uomResponse.data.map((uom: any) => ({
          label: `${uom.uomName} (${uom.uomShortCode})`,
          value: uom.workUomId?.toString(),
        }));
        setUomOptions(uomOpts);
        console.log("UOM options loaded:", uomOpts);
      }

      // For tax options, we'll use a basic set until tax API is available
      // Fetch tax options from API
      const taxResponse = await dropdownApis.fetchTaxDropdown("INDIRECT,CESS");
      if (taxResponse?.data && Array.isArray(taxResponse.data)) {
        const taxOpts = taxResponse.data.map((tax: any) => ({
          label: `${tax.taxName || tax.name} - ${tax.taxRate || tax.rate || 0
            }%`,
          value: tax.taxId?.toString() || tax.id?.toString(),
        }));
        setTaxOptions(taxOpts);
        console.log("Tax options set:", taxOpts);
      } else {
        // Fallback to static options if API fails
        const taxOpts = [
          { label: "GST (18%)", value: "GST" },
          { label: "VAT (12%)", value: "VAT" },
          { label: "Service Tax (15%)", value: "ServiceTax" },
        ];
        setTaxOptions(taxOpts);
        console.log("Tax options set (fallback):", taxOpts);
      }
    } catch (error) {
      console.error("Error loading master data:", error);
      message.error("Failed to load master data");
    }
  };

  const fetchDependentData = async () => {
    try {
      if (!siteIdWatch) {
        console.log("No site selected, clearing BOQ options");
        setWorkBoqOptions([]);
        return;
      }

      console.log("Fetching Work BOQs for site:", siteIdWatch);

      // Get Work BOQs for the site using workBoqDropdownAlongWithCategory API
      const boqResponse: any =
        await workBoqApis.workBoqDropdownAlongWithCategory(
          Number(siteIdWatch),
          "Work Order",
          flowTypeParam
        );
      console.log("Work BOQ API response:", boqResponse);

      if (
        boqResponse?.status &&
        boqResponse?.data &&
        Array.isArray(boqResponse.data)
      ) {
        // Store the full response data for later use
        const workBoqsData = boqResponse.data;

        const boqOptions = workBoqsData.map((boq: any) => ({
          label: boq.boqTitle,
          value: boq.workBoqId?.toString(),
          workCategories: boq.workCategories || [], // Store categories for later filtering
        }));
        setWorkBoqOptions(boqOptions);
        console.log("Work BOQ options loaded:", boqOptions);
      } else {
        setWorkBoqOptions([]);
        console.log("No Work BOQs found for site");
      }
    } catch (error) {
      console.error("Error fetching dependent data:", error);
      message.error("Failed to load Work BOQ data");
      setWorkBoqOptions([]);
    }
  };

  const fetchProjects = async () => {
    try {
      if (!siteIdWatch) {
        console.log("No site selected, clearing project options");
        setProjectOptions([]);
        return;
      }

      console.log("Fetching projects for site:", siteIdWatch);
      const { data: projects, status } = await dropdownApis.getProjectsDropdown(
        parseInt(siteIdWatch)
      );

      if (status && projects) {
        const projectOpts = projects.map((project: any) => ({
          label: project.projectTitle,
          value: project.projectId?.toString(),
        }));
        setProjectOptions(projectOpts);
        console.log("Project options set:", projectOpts.length, "options");
      } else {
        setProjectOptions([]);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setProjectOptions([]);
    }
  };

  const handleProjectChange = async (projectId: string) => {
    console.log("Project changed:", projectId, "Previous:", previousProjectId);

    const currentLines = getValues("workOrderLines") || [];

    // Check if there are existing work order lines from Project Management source
    const pmLines = currentLines.filter(
      (line: any) => line.source === WorkOrderLineSource.ProjectManagement
    );
    console.log("currentLines", previousProjectId, projectId, pmLines.length);

    // If project is changing (not initial selection) and there are PM lines, ask for confirmation
    if (
      previousProjectId &&
      previousProjectId !== projectId &&
      pmLines.length > 0
    ) {
      const result = await Swal.fire({
        title: "Change Project?",
        html: `You have <strong>${pmLines.length}</strong> work order line(s) from Project Management.<br/><br/>Changing the project will remove all existing Project Management lines.<br/><br/>Do you want to continue?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, change project",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        // User confirmed - remove PM lines and update project
        const remainingLines = currentLines.filter(
          (line: any) => line.source !== WorkOrderLineSource.ProjectManagement
        );
        setValue("workOrderLines", remainingLines, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });

        // Save project name
        const selectedProject = projectOptions.find(
          (p) => p.value === projectId
        );
        if (selectedProject) {
          setValue("projectName", selectedProject.label);
        }

        // Clear activity selection
        setValue("activityId", "");
        setActivityOptions([]);

        // Update previous project ID
        setPreviousProjectId(projectId);

        message.success(
          `Project changed. ${pmLines.length} Project Management line(s) removed.`
        );
      } else {
        // User cancelled - revert to previous project
        setValue("projectId", previousProjectId);
        return;
      }
    } else {
      // No confirmation needed - just update project
      const selectedProject = projectOptions.find((p) => p.value === projectId);
      if (selectedProject) {
        setValue("projectName", selectedProject.label);
      }

      // Clear activity selection when project changes
      setValue("activityId", "");
      setActivityOptions([]);

      // Update previous project ID for future change detection
      setPreviousProjectId(projectId);
    }
  };

  const fetchProjectActivities = async () => {
    try {
      if (!projectIdWatch) {
        console.log("No project ID available, clearing activity options");
        setActivityOptions([]);
        return;
      }

      console.log("fetchProjectActivities called for project:", projectIdWatch);

      // Fetch activities using the activity dropdown API
      const activitiesResponse = await dropdownApis.fetchActivityDropdown(
        Number(projectIdWatch)
      );
      console.log("Activities API response:", activitiesResponse);

      if (!activitiesResponse?.data || activitiesResponse.data.length === 0) {
        console.warn("No activities found for project:", projectIdWatch);
        setActivityOptions([]);
        message.warning("No activities found for the selected project");
        return;
      }

      const activityOpts = activitiesResponse.data.map((activity: any) => ({
        label: activity.activityName,
        value: activity.activityId?.toString(),
      }));

      console.log("Setting activity options:", activityOpts);
      setActivityOptions(activityOpts);

      // Force a small delay to ensure state update
      setTimeout(() => {
        console.log("Activity options after setState:", activityOpts.length);
      }, 100);
    } catch (error) {
      console.error("Error fetching activities:", error);
      message.error("Failed to load activities");
      setActivityOptions([]);
    }
  };

  const clearSourceFields = () => {
    console.log("Clearing source-specific fields");

    const fieldsCleared = [];

    // Clear Project Management fields
    if (getValues("activityId")) {
      setValue("activityId", "");
      fieldsCleared.push("Activity");
    }

    // Clear Work BOQ fields
    if (getValues("workBoqId")) {
      setValue("workBoqId", "");
      fieldsCleared.push("Work BOQ");
    }

    // Clear common fields
    if (getValues("workCategoryId")) {
      setValue("workCategoryId", "");
      fieldsCleared.push("Work Category");
    }

    if (fieldsCleared.length > 0) {
      console.log("Fields cleared:", fieldsCleared);
    }
  };

  const fetchSourceData = async () => {
    try {
      const activityId = getValues("activityId");
      const workCategoryId = getValues("workCategoryId");
      const workBoqId = getValues("workBoqId");
      const contractorId = getValues("contractorId")

      console.log("Fetching source data:", {
        source: selectedSourceWatch,
        activityId,
        workCategoryId,
        workBoqId,
        contractorId
      });

      if (selectedSourceWatch === WorkOrderLineSource.ProjectManagement) {
        if (projectIdWatch && activityId && workTypeWatch) {
          console.log("Getting project tasks from API...");

          // Use the real taskListByActivityId API
          const params = {
            page: 1,
            pageSize: 2000,
            direction: "P",
            sortOrder: "DESC",
            pageDiff: 0,
            projectIds: parseInt(projectIdWatch),
            activityIds: parseInt(activityId),
            contractorIds: parseInt(contractorId),
            derivedProjectStatus: "Active",
            workType: workTypeWatch,
          };

          const { data, status }: any =
            await workRequestApis.taskListByActivityId(params);

          if (status && data && data.list) {
            const tasks = data.list.map((task: any) => ({
              taskId: task.taskId?.toString() || task.id?.toString(),
              taskName: task.taskName || task.name,
              description: task.taskDescription || task.description,
              workCategoryId: task.workCategoryId,
              workCategory: task.workCategoryName || "", // Default, can be enhanced
              quantity: task.workQuantity || 0,
              workQuantity: task.workQuantity || 0,
              workOrderQty: task.workOrderQty || 0,
              availableForWorkOrderQty: task.availableForWorkOrderQty || 0,
              workUomId: task.taskBoqs && task.taskBoqs.length > 0
                ? task.taskBoqs[0].uomId
                : task.workUomId || null,
              uom: task.workUomName || (task.taskBoqs && task.taskBoqs.length > 0
                ? task.taskBoqs[0].uomName
                : null),
              taskRate: task.rate,
              rate: 0, // Will be input field
              // Budget allocation fields for Project Management source
              budgetHeaderId: task.budgetHeaderId || 0,
              budgetLineId: task.budgetLineId || 0,
              budgetValue: task.budgetValue || 0,
              budgetLineName: task.budgetLineName || "",
              WorkOrderAllocatedBudget: task.WorkOrderAllocatedBudget || 0,
              weightagePercentage: 0, // Will be input field
              // Location fields from task data (for Project Management tasks)
              siteStructureUnitId: task.siteStructureUnitId || null,
              siteStructureUnitName: task.siteStructureUnitName || undefined,
              siteStructureSubUnitId: task.siteStructureSubUnitId || null,
              siteStructureSubUnitName:
                task.siteStructureSubUnitName || undefined,
              floorNo: task.floorNo || null,
              locationId: task.locationId || null,
              locationName: task.locationName || undefined,
              // taxType: "GST",
              // taxPercentage: 18,
              activityName: task.activityName || "",
              scheduledStartDate: task.scheduledStartDate,
              scheduledEndDate: task.scheduledEndDate,
            }));
            setAvailableTasks(tasks);

            // Store budgetHeaderId from Project Management tasks response for submission
            if (tasks.length > 0 && tasks[0].budgetHeaderId) {
              setBudgetHeaderId(tasks[0].budgetHeaderId);
              setValue("budgetHeaderId", tasks[0].budgetHeaderId.toString()); // Store in form data
              console.log(
                "Stored Project Management budgetHeaderId in form:",
                tasks[0].budgetHeaderId
              );
            }

            // Initialize rates, weightages, requested quantities, tax types, and budget allocations for all loaded tasks
            const newRates = { ...taskRates };
            const newWeightages = { ...taskWeightages };
            const newRequestedQuantities = { ...taskRequestedQuantities };
            const newTaxTypes = { ...taskTaxTypes };
            const newTaskBudgetAllocations = { ...taskBudgetAllocations };

            tasks.forEach((task: any) => {
              if (newRates[task.taskId] === undefined) {
                newRates[task.taskId] = 0;
              }
              if (newWeightages[task.taskId] === undefined) {
                newWeightages[task.taskId] = 0;
              }
              if (newRequestedQuantities[task.taskId] === undefined) {
                newRequestedQuantities[task.taskId] = 0;
              }
              if (newTaxTypes[task.taskId] === undefined) {
                newTaxTypes[task.taskId] = "";
              }
              if (newTaskBudgetAllocations[task.taskId] === undefined) {
                newTaskBudgetAllocations[task.taskId] = 0;
              }
            });

            setTaskRates(newRates);
            setTaskWeightages(newWeightages);
            setTaskRequestedQuantities(newRequestedQuantities);
            setTaskTaxTypes(newTaxTypes);
            setTaskBudgetAllocations(newTaskBudgetAllocations);

            console.log("Project tasks loaded from API:", tasks);
            console.log(
              "Initialized rates and weightages for tasks:",
              tasks.length
            );
          } else {
            setAvailableTasks([]);
            console.log("No tasks found for activity:", activityId);
          }
        } else {
          console.log("Missing projectId or activityId for PM source");
          setAvailableTasks([]);
        }
      } else if (selectedSourceWatch === WorkOrderLineSource.WorkBOQ) {
        if (workBoqId) {
          console.log("Getting Work BOQ lines from API...");

          // Use the getWorkBoqByIdWithCategory API to get BOQ lines
          let apiCall;
          if (workCategoryId) {
            apiCall = workBoqApis.getWorkBoqByIdWithCategory(
              Number(workBoqId),
              workCategoryId
            );
          } else {
            apiCall = workBoqApis.getWorkBoqById(Number(workBoqId));
          }

          const { data, status }: any = await apiCall;

          if (status && data && data.workBoq && data.workBoq.workBoqLines) {
            let lines = data.workBoq.workBoqLines;
            setValue("workLevel", data.workBoq.segmentLevel);
            // Filter by category if specified
            if (workCategoryId) {
              lines = lines.filter(
                (line: any) =>
                  line.workCategoryId?.toString() === workCategoryId
              );
            }

            const boqLines = lines.map((line: any) => ({
              lineId: line.workBoqLineId?.toString() || line.id?.toString(),
              taskName: line.taskTitle || line.taskName,
              workCategoryId: line.workCategoryId?.toString(),
              workCategory: line.workCategoryName,
              quantity: line.quantity || 0, // BOQ Quantity mapping
              allocatedQuantity: line.allocatedQuantity || 0, // Allocated Qty mapping
              quantityAvailableForWorkOrder:
                line.quantityAvailableForWorkOrder || 0, // Pending Qty mapping
              approvedQuantity: line.quantity || 0, // Keep for backward compatibility
              pendingQuantity: line.quantityAvailableForWorkOrder || 0, // Keep for backward compatibility
              uom: line.uomName,
              uomId: line.uomId,
              rate: line.rate, // Will be input field
              weightagePercentage: 0, // Will be input field
              // taxType: "GST",
              // taxPercentage: 18,
              segmentLevel: data.segmentLevel,
              towerVillaId: line.siteStructureUnitId,
              towerVillaName: line.siteStructureUnitName,
              floorName: line.floorNo,
              flatId: line.siteStructureSubUnitId,
              flatName: line.siteStructureSubUnitName,
              locationId: line.locationId || "",
              locationName: line.locationName,
              // towerFlatInfo:
              //   `${line.siteStructureUnitName || ""} ${line.floorNo || ""} ${
              //     line.siteStructureSubUnitName || ""
              //   }`.trim() || "-",
              documents: line.dms || [],
              materialAllocation: line.workBoqLineMaterials || [],
              // Budget allocation fields for Work BOQ source
              budgetAllocation: line.budgetAllocation || 0,
              budgetLineId: line.budgetLineId || 0,
              budgetLineName: line.budgetLineName || "",
              workOrderAllocatedBudget: line.workOrderAllocatedBudget || 0,
            }));

            console.log("Work BOQ lines loaded from API:", boqLines);
            setAvailableBoqLines(boqLines);

            // Store budgetHeaderId from Work BOQ response for submission
            if (data?.workBoq?.budgetHeaderId) {
              setBudgetHeaderId(data.workBoq.budgetHeaderId);
              setValue(
                "budgetHeaderId",
                data.workBoq.budgetHeaderId.toString()
              ); // Store in form data
              console.log(
                "Stored Work BOQ budgetHeaderId in form:",
                data.workBoq.budgetHeaderId
              );
            }

            // Initialize rates, weightages, requested quantities, tax types, and budget allocations for all loaded BOQ lines
            const newBoqLineRates = { ...boqLineRates };
            const newBoqLineWeightages = { ...boqLineWeightages };
            const newBoqLineRequestedQuantities = {
              ...boqLineRequestedQuantities,
            };
            const newBoqLineTaxTypes = { ...boqLineTaxTypes };
            const newBoqLineBudgetAllocations = { ...boqLineBudgetAllocations };

            boqLines.forEach((line: any) => {
              if (newBoqLineRates[line.lineId] === undefined) {
                newBoqLineRates[line.lineId] = 0;
              }
              if (newBoqLineWeightages[line.lineId] === undefined) {
                newBoqLineWeightages[line.lineId] = 0;
              }
              if (newBoqLineRequestedQuantities[line.lineId] === undefined) {
                newBoqLineRequestedQuantities[line.lineId] = 0;
              }
              if (newBoqLineTaxTypes[line.lineId] === undefined) {
                newBoqLineTaxTypes[line.lineId] = "";
              }
              if (newBoqLineBudgetAllocations[line.lineId] === undefined) {
                newBoqLineBudgetAllocations[line.lineId] = 0;
              }
            });

            setBoqLineRates(newBoqLineRates);
            setBoqLineWeightages(newBoqLineWeightages);
            setBoqLineRequestedQuantities(newBoqLineRequestedQuantities);
            setBoqLineTaxTypes(newBoqLineTaxTypes);
            setBoqLineBudgetAllocations(newBoqLineBudgetAllocations);
            console.log(
              "Initialized rates and requested quantities for BOQ lines:",
              boqLines.length
            );
          } else {
            setAvailableBoqLines([]);
            console.log("No Work BOQ lines found");
          }
        } else {
          console.log("Missing workBoqId for BOQ source");
          setAvailableBoqLines([]);
        }
      }
    } catch (error) {
      console.error("Error fetching source data:", error);
      message.error("Failed to load source data");
      setAvailableTasks([]);
      setAvailableBoqLines([]);
    }
  };

  // Budget-related functions for New Work Line
  const fetchBudgetOptions = async () => {
    try {
      if (!siteIdWatch) {
        console.log("No site ID available, clearing budget options");
        setBudgetOptions([]);
        return;
      }

      console.log("Fetching budget options for site:", siteIdWatch);
      const response = await dropdownApis.getBudgetDropdownBySiteId(
        parseInt(siteIdWatch)
      );

      if (response?.status && response?.data) {
        const budgetOpts = response.data.map((budget: any) => ({
          label: budget.budgetName,
          value: budget.headerId?.toString(),
        }));
        setBudgetOptions(budgetOpts);
        console.log("Budget options loaded:", budgetOpts);
      }
    } catch (error) {
      console.log("Error fetching budget options:", error);
      message.error("Failed to fetch budget options");
    }
  };

  const fetchBudgetLines = async (budgetHeaderId: number) => {
    try {
      setBudgetLinesLoading(true);
      console.log("Fetching budget lines for budget header:", budgetHeaderId);

      const response = await dropdownApis.getBudgetLinesDropdown(
        budgetHeaderId
      );

      if (response?.status && response?.data) {
        setBudgetLines(response.data);
        console.log("Budget lines loaded:", response.data);
      }
    } catch (error) {
      console.log("Error fetching budget lines:", error);
      message.error("Failed to fetch budget lines");
    } finally {
      setBudgetLinesLoading(false);
    }
  };

  // Fetch Tower/Block/Villa options based on siteId
  const fetchTowerVillaOptions = async () => {
    try {
      if (!siteIdWatch) return [];
      const { data, status } =
        await dropdownApis.fetchSiteStructureUnitsBySiteId(
          parseInt(siteIdWatch)
        );
      if (status && data) {
        const options = data.map((item: any) => ({
          label: item?.unitName,
          value: item?.id?.toString(),
        }));
        setTowerVillaOptions(options);
        return options; // Return options for immediate use
      }
      return [];
    } catch (error) {
      console.log("Error fetching tower/block/villa options:", error);
      message.error("Failed to fetch tower/block/villa options");
      return [];
    }
  };

  // Fetch Floor options based on selected tower/block
  const fetchFloorOptions = async (unitId: number) => {
    try {
      if (!siteIdWatch || !unitId) {
        setFloorOptions([]);
        return;
      }
      const { data, status } = await dropdownApis.fetchFloorNumbersByUnitId(
        parseInt(siteIdWatch),
        unitId
      );
      if (status && data) {
        const options = data.map((item: any) => ({
          label: item?.floorNo,
          value: item?.floorNo,
        }));
        setFloorOptions(options);
      }
    } catch (error) {
      console.log("Error fetching floor options:", error);
      message.error("Failed to fetch floor options");
    }
  };

  // Fetch Flat options based on selected tower and floor
  const fetchFlatOptions = async (unitId: number, floorNo: string) => {
    try {
      if (!siteIdWatch || !unitId || !floorNo) {
        setFlatOptions([]);
        return;
      }
      const { data, status } = await dropdownApis.fetchFlatsBySiteIdUnitIdFloor(
        parseInt(siteIdWatch),
        unitId,
        floorNo
      );
      if (status && data) {
        const options = data.map((item: any) => ({
          label: item?.subUnitName,
          value: item?.id?.toString(),
        }));
        setFlatOptions(options);
      }
    } catch (error) {
      console.log("Error fetching flat options:", error);
      message.error("Failed to fetch flat options");
    }
  };

  // Fetch Location options based on siteId
  const fetchLocationOptions = async () => {
    try {
      if (!siteIdWatch) return;
      const { data, status } = await dropdownApis.fetchLocationsBySiteId(
        parseInt(siteIdWatch)
      );
      if (status && data) {
        const options = data.map((item: any) => ({
          label: item?.locationName || item?.name,
          value: item?.locationId?.toString() || item?.id?.toString(),
        }));
        setLocationOptions(options);
      }
    } catch (error) {
      console.log("Error fetching location options:", error);
      message.error("Failed to fetch location options");
    }
  };

  // Effect to fetch budget options when site changes and source is New Work Line
  React.useEffect(() => {
    if (
      siteIdWatch &&
      selectedSourceWatch === WorkOrderLineSource.NewWorkLine
    ) {
      console.log("Fetching budget options for New Work Line source");
      fetchBudgetOptions();
    }
  }, [siteIdWatch, selectedSourceWatch]);

  // Effect to fetch tower/villa and location options when site changes
  // Fetch for New Work Line source, or when editing (to populate edit modal dropdowns)
  React.useEffect(() => {
    if (siteIdWatch) {
      // Always fetch when site changes, as these options are needed for edit modal
      // regardless of source type
      if (
        selectedSourceWatch === WorkOrderLineSource.NewWorkLine ||
        formTypeWatch === "edit"
      ) {
        console.log(
          "Fetching tower/villa and location options (for New Work Line or Edit mode)"
        );
        fetchTowerVillaOptions();
        fetchLocationOptions();
      }
    }
  }, [siteIdWatch, selectedSourceWatch, formTypeWatch]);

  // Watch tower/villa selection from the new work line form for cascading floor dropdown
  const newWorkLineTowerVillaWatch = newWorkLineFormMethods.watch("towerVilla");
  const newWorkLineFloorNameWatch = newWorkLineFormMethods.watch("floorName");

  // Effect to fetch floor options when tower/villa changes (cascading dropdown)
  React.useEffect(() => {
    if (isInitializingEdit) {
      prevTowerVillaRef.current = newWorkLineTowerVillaWatch;
      return;
    }

    const hasChanged = prevTowerVillaRef.current !== newWorkLineTowerVillaWatch;
    prevTowerVillaRef.current = newWorkLineTowerVillaWatch;

    if (!hasChanged) return;

    if (newWorkLineTowerVillaWatch && siteIdWatch) {
      fetchFloorOptions(parseInt(newWorkLineTowerVillaWatch));
      // Reset floor and flat when tower changes
      newWorkLineFormMethods.setValue("floorName", "");
      newWorkLineFormMethods.setValue("flatName", "");
      setFlatOptions([]);
    } else {
      setFloorOptions([]);
      setFlatOptions([]);
    }
  }, [newWorkLineTowerVillaWatch, siteIdWatch, isInitializingEdit]);

  // Effect to fetch flat options when floor changes (cascading dropdown)
  React.useEffect(() => {
    if (isInitializingEdit) {
      prevFloorNameRef.current = newWorkLineFloorNameWatch;
      return;
    }

    const hasChanged = prevFloorNameRef.current !== newWorkLineFloorNameWatch;
    prevFloorNameRef.current = newWorkLineFloorNameWatch;

    if (!hasChanged) return;

    if (
      newWorkLineFloorNameWatch &&
      newWorkLineTowerVillaWatch &&
      siteIdWatch
    ) {
      fetchFlatOptions(
        parseInt(newWorkLineTowerVillaWatch),
        newWorkLineFloorNameWatch
      );
      // Reset flat when floor changes
      newWorkLineFormMethods.setValue("flatName", "");
    } else if (!newWorkLineFloorNameWatch) {
      setFlatOptions([]);
    }
  }, [
    newWorkLineFloorNameWatch,
    newWorkLineTowerVillaWatch,
    siteIdWatch,
    isInitializingEdit,
  ]);

  const resetSelections = () => {
    console.log("Resetting selections and available data");
    setSelectedTaskIds([]);
    setSelectedBoqLineIds([]);
    setAvailableTasks([]);
    setAvailableBoqLines([]);
  };

  // Handler functions for inline editing
  const handleRateChange = (index: number, value: number) => {
    const currentLines = getValues("workOrderLines") || [];
    const updatedLines = [...currentLines];

    // Calculate line amount
    const lineAmount = (updatedLines[index].quantity || 0) * value;

    // Calculate tax amount if tax is enabled at line level
    const taxAmount =
      enableTaxWatch === "true" &&
        (taxApplicationLevelWatch === TaxApplicationLevel.Line ||
          taxApplicationLevelWatch === TaxApplicationLevel.Both) &&
        (updatedLines[index].taxPercentage || 0) > 0
        ? (lineAmount * (updatedLines[index].taxPercentage || 0)) / 100
        : 0;

    updatedLines[index] = {
      ...updatedLines[index],
      rate: value,
      taxAmount: taxAmount,
      totalAmount: lineAmount + taxAmount,
    };

    setValue("workOrderLines", updatedLines);
  };

  const handleQuantityChange = (index: number, value: number) => {
    console.log(`Changing quantity for line ${index} to ${value}`);
    const currentLines = getValues("workOrderLines") || [];
    const updatedLines = [...currentLines];
    const lineAmount = value * (updatedLines[index].rate || 0);
    const taxAmount =
      (updatedLines[index].taxPercentage || 0) > 0
        ? (lineAmount * (updatedLines[index].taxPercentage || 0)) / 100
        : 0;

    updatedLines[index] = {
      ...updatedLines[index],
      quantity: value,
      taxAmount: taxAmount,
      totalAmount: lineAmount + taxAmount,
      remainingQuantity: value - (updatedLines[index].executedQuantity || 0),
    };

    setValue("workOrderLines", updatedLines, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    console.log("Updated line:", updatedLines[index]);
  };

  const handleWeightageChange = (index: number, value: number) => {
    console.log(`Changing weightage for line ${index} to ${value}%`);
    const currentLines = getValues("workOrderLines") || [];
    const updatedLines = [...currentLines];
    updatedLines[index] = {
      ...updatedLines[index],
      weightagePercentage: value,
    };
    setValue("workOrderLines", updatedLines, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    console.log("Updated line:", updatedLines[index]);
  };

  // Helper function to get tax type ID from tax type name
  const getWorkOrderLineTaxTypeId = (taxTypeName: string) => {
    if (!taxTypeName) return "";
    const taxOption = taxOptions.find((tax) => tax.label === taxTypeName);
    return taxOption ? taxOption.value : "";
  };

  // Handle tax change in work order lines
  const handleWorkOrderLineTaxChange = (index: number, taxTypeId: string) => {
    console.log(`Changing tax for line ${index} to ${taxTypeId}`);
    const currentLines = getValues("workOrderLines") || [];
    const updatedLines = [...currentLines];

    // Get tax information from selected tax type
    const selectedTaxOption = taxOptions.find((tax) => tax.value === taxTypeId);
    const taxTypeName = selectedTaxOption ? selectedTaxOption.label : "";
    const taxPercentage = selectedTaxOption
      ? parseFloat(
        selectedTaxOption.label.split(" - ")[1]?.replace("%", "") || "0"
      )
      : 0;

    // Calculate new tax amount
    const lineAmount =
      (updatedLines[index].quantity || 0) * (updatedLines[index].rate || 0);
    const taxAmount =
      enableTaxWatch === "true" &&
        (taxApplicationLevelWatch === TaxApplicationLevel.Line ||
          taxApplicationLevelWatch === TaxApplicationLevel.Both) &&
        taxPercentage > 0
        ? (lineAmount * taxPercentage) / 100
        : 0;

    updatedLines[index] = {
      ...updatedLines[index],
      taxType: taxTypeId, // Store ID for API submission
      taxTypeName: taxTypeName, // Store label for display
      taxPercentage: taxPercentage,
      taxAmount: taxAmount,
      totalAmount: lineAmount + taxAmount,
    };

    setValue("workOrderLines", updatedLines, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    console.log("Updated line with tax:", updatedLines[index]);
  };

  // Project Management Task Selection
  const openTaskSelectionModal = () => {
    console.log("Opening task selection modal");

    if (!projectIdWatch || !getValues("activityId") || !getValues("contractorId")) {
      console.log("Missing required fields:", {
        projectId: projectIdWatch,
        activityId: getValues("activityId"),
        contractorId: getValues("contractorId")
      });
      message.warning("Please select Project and Activity, and Contractor first");
      return;
    }

    console.log("Fetching tasks before opening modal...");
    fetchSourceData().then(() => {
      // Initialize rates and weightages for all available tasks after they're loaded
      setTimeout(() => {
        const newRates = { ...taskRates };
        const newWeightages = { ...taskWeightages };
        const newRequestedQuantities = { ...taskRequestedQuantities };

        availableTasks.forEach((task) => {
          if (!newRates[task.taskId]) {
            newRates[task.taskId] = 0;
          }
          if (!newWeightages[task.taskId]) {
            newWeightages[task.taskId] = 0;
          }
          if (!newRequestedQuantities[task.taskId]) {
            newRequestedQuantities[task.taskId] = 0;
          }
        });

        setTaskRates(newRates);
        setTaskWeightages(newWeightages);
        setTaskRequestedQuantities(newRequestedQuantities);
        console.log(
          "Initialized rates and weightages for",
          availableTasks.length,
          "tasks"
        );
      }, 100);
    });
    setIsTaskSelectionModalVisible(true);
  };

  const handleTaskSelectionChange = (selectedRowKeys: React.Key[]) => {
    console.log("Task selection changed:", selectedRowKeys);
    setSelectedTaskIds(selectedRowKeys as string[]);

    // Initialize rates and weightages for newly selected tasks
    const newTaskIds = selectedRowKeys.filter(
      (id) => !selectedTaskIds.includes(id as string)
    );
    if (newTaskIds.length > 0) {
      const newRates = { ...taskRates };
      const newWeightages = { ...taskWeightages };
      const newRequestedQuantities = { ...taskRequestedQuantities };

      newTaskIds.forEach((taskId) => {
        if (!newRates[taskId as string]) {
          newRates[taskId as string] = 0;
        }
        if (!newWeightages[taskId as string]) {
          newWeightages[taskId as string] = 0;
        }
        if (!newRequestedQuantities[taskId as string]) {
          newRequestedQuantities[taskId as string] = 0;
        }
      });

      setTaskRates(newRates);
      setTaskWeightages(newWeightages);
      setTaskRequestedQuantities(newRequestedQuantities);
    }
  };

  const handleTaskRateChange = (taskId: string, value: number) => {
    setTaskRates((prev) => ({ ...prev, [taskId]: value || 0 }));
  };

  const handleTaskWeightageChange = (taskId: string, value: number) => {
    setTaskWeightages((prev) => ({ ...prev, [taskId]: value || 0 }));
  };

  const handleTaskRequestedQuantityChange = (taskId: string, value: number) => {
    // If value is null/undefined, set to 0
    if (value === null || value === undefined) {
      setTaskRequestedQuantities((prev) => ({ ...prev, [taskId]: 0 }));
      return;
    }

    // Find the task to get its pending quantity
    const task = availableTasks.find((t: any) => t.taskId === taskId) as any;
    const pendingQty = task?.availableForWorkOrderQty || 0;

    // Validate if requested quantity exceeds pending quantity
    if (value > pendingQty) {
      message.error(
        `Requested Qty (${value}) cannot be greater than Pending Qty (${pendingQty})`
      );
      // Keep the previous valid value instead of setting to 0
      return;
    }

    setTaskRequestedQuantities((prev) => ({ ...prev, [taskId]: value }));
  };

  const handleTaskTaxTypeChange = (taskId: string, value: string) => {
    setTaskTaxTypes((prev) => ({ ...prev, [taskId]: value }));

    // Update the workOrderLines if this task is already added
    const currentLines = getValues("workOrderLines") || [];
    const lineIndex = currentLines.findIndex(
      (line: any) =>
        line.sourceId === taskId && line.source === "Project Management"
    );

    if (lineIndex >= 0) {
      const updatedLines = [...currentLines];
      const line = updatedLines[lineIndex];

      // Get tax information from selected tax type
      const selectedTaxOption = taxOptions.find((tax) => tax.value === value);
      const taxTypeName = selectedTaxOption ? selectedTaxOption.label : "";
      const taxPercentage = selectedTaxOption
        ? parseFloat(
          selectedTaxOption.label.split(" - ")[1]?.replace("%", "") || "0"
        )
        : 0;

      const lineAmount = (line.quantity || 0) * (line.rate || 0);
      const taxAmount =
        enableTaxWatch === "true" &&
          (taxApplicationLevelWatch === TaxApplicationLevel.Line ||
            taxApplicationLevelWatch === TaxApplicationLevel.Both) &&
          taxPercentage > 0
          ? (lineAmount * taxPercentage) / 100
          : 0;

      updatedLines[lineIndex] = {
        ...line,
        taxType: value,
        taxTypeName: taxTypeName,
        taxPercentage: taxPercentage,
        taxAmount: taxAmount,
        totalAmount: lineAmount + taxAmount,
      };

      setValue("workOrderLines", updatedLines, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      console.log(
        "Updated work order line with new tax:",
        updatedLines[lineIndex]
      );
    }
  };

  const handleTaskBudgetAllocationChange = (taskId: string, value: number) => {
    setTaskBudgetAllocations((prev) => ({ ...prev, [taskId]: value }));
  };

  const handleTaskRateRemarksChange = (taskId: string, value: string) => {
    setTaskRateRemarks((prev) => ({ ...prev, [taskId]: value }));
  };

  // Auto-calculate budget allocation for tasks when qty, rate, or tax changes
  useEffect(() => {
    const newAllocations = { ...taskBudgetAllocations };
    let hasChanges = false;

    availableTasks.forEach((task: any) => {
      const taskId = task.taskId;
      const qty = taskRequestedQuantities[taskId] || 0;
      const rate = taskRates[taskId] || 0;
      const taxTypeId = taskTaxTypes[taskId];

      if (qty > 0 && rate > 0) {
        const lineAmount = qty * rate;
        let totalWithTax = lineAmount;

        // Calculate tax if enabled and tax type selected
        if (
          enableTaxWatch === "true" &&
          (taxApplicationLevelWatch === TaxApplicationLevel.Line ||
            taxApplicationLevelWatch === TaxApplicationLevel.Both) &&
          taxTypeId
        ) {
          const selectedTax = taxOptions.find(
            (tax: any) => tax.value === taxTypeId
          );
          if (selectedTax) {
            const taxPercentage = parseFloat(
              selectedTax.label.match(/(\d+(\.\d+)?)/)?.[0] || "0"
            );
            if (taxPercentage > 0) {
              const taxAmount = (lineAmount * taxPercentage) / 100;
              totalWithTax = lineAmount + taxAmount;
            }
          }
        }

        // Only update if value changed
        if (newAllocations[taskId] !== totalWithTax) {
          newAllocations[taskId] = totalWithTax;
          hasChanges = true;
        }
      }
    });

    if (hasChanges) {
      setTaskBudgetAllocations(newAllocations);
    }
  }, [
    taskRequestedQuantities,
    taskRates,
    taskTaxTypes,
    availableTasks,
    enableTaxWatch,
    taxApplicationLevelWatch,
    taxOptions,
  ]);

  const handleBoqLineRateChange = (lineId: string, value: number) => {
    setBoqLineRates((prev) => ({ ...prev, [lineId]: value || 0 }));
  };

  const handleBoqLineWeightageChange = (lineId: string, value: number) => {
    setBoqLineWeightages((prev) => ({ ...prev, [lineId]: value || 0 }));
  };

  const handleBoqLineRequestedQuantityChange = (
    lineId: string,
    value: number
  ) => {
    // If value is null/undefined, set to 0
    if (value === null || value === undefined) {
      setBoqLineRequestedQuantities((prev) => ({
        ...prev,
        [lineId]: 0,
      }));
      return;
    }

    // Find the BOQ line to get its pending quantity
    const boqLine = availableBoqLines.find(
      (line: any) => line.lineId === lineId
    ) as any;
    const pendingQty = boqLine?.quantityAvailableForWorkOrder || 0;

    // Validate if requested quantity exceeds pending quantity
    if (value > pendingQty) {
      message.error(
        `Requested Qty (${value}) cannot be greater than Pending Qty (${pendingQty})`
      );
      // Keep the previous valid value instead of setting to 0
      return;
    }

    setBoqLineRequestedQuantities((prev) => ({
      ...prev,
      [lineId]: value,
    }));
  };

  const handleBoqLineTaxTypeChange = (lineId: string, value: string) => {
    setBoqLineTaxTypes((prev) => ({ ...prev, [lineId]: value }));

    // Update the workOrderLines if this BOQ line is already added
    const currentLines = getValues("workOrderLines") || [];
    const lineIndex = currentLines.findIndex(
      (line: any) => line.sourceId === lineId && line.source === "Work BOQ"
    );

    if (lineIndex >= 0) {
      const updatedLines = [...currentLines];
      const line = updatedLines[lineIndex];

      // Get tax information from selected tax type
      const selectedTaxOption = taxOptions.find((tax) => tax.value === value);
      const taxTypeName = selectedTaxOption ? selectedTaxOption.label : "";
      const taxPercentage = selectedTaxOption
        ? parseFloat(
          selectedTaxOption.label.split(" - ")[1]?.replace("%", "") || "0"
        )
        : 0;

      const lineAmount = (line.quantity || 0) * (line.rate || 0);
      const taxAmount =
        enableTaxWatch === "true" &&
          (taxApplicationLevelWatch === TaxApplicationLevel.Line ||
            taxApplicationLevelWatch === TaxApplicationLevel.Both) &&
          taxPercentage > 0
          ? (lineAmount * taxPercentage) / 100
          : 0;

      updatedLines[lineIndex] = {
        ...line,
        taxType: value,
        taxTypeName: taxTypeName,
        taxPercentage: taxPercentage,
        taxAmount: taxAmount,
        totalAmount: lineAmount + taxAmount,
      };

      setValue("workOrderLines", updatedLines, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      console.log(
        "Updated work order line with new tax:",
        updatedLines[lineIndex]
      );
    }
  };

  const handleBoqLineBudgetAllocationChange = (
    lineId: string,
    value: number
  ) => {
    setBoqLineBudgetAllocations((prev) => ({ ...prev, [lineId]: value }));
  };

  const handleBoqLineRateRemarksChange = (lineId: string, value: string) => {
    setBoqLineRateRemarks((prev) => ({ ...prev, [lineId]: value }));
  };

  // Auto-calculate budget allocation for BOQ lines when qty, rate, or tax changes
  useEffect(() => {
    const newAllocations = { ...boqLineBudgetAllocations };
    let hasChanges = false;

    availableBoqLines.forEach((boqLine: any) => {
      const lineId = boqLine.lineId;
      const qty = boqLineRequestedQuantities[lineId] || 0;
      const rate = boqLineRates[lineId] || 0;
      const taxTypeId = boqLineTaxTypes[lineId];

      if (qty > 0 && rate > 0) {
        const lineAmount = qty * rate;
        let totalWithTax = lineAmount;

        // Calculate tax if enabled and tax type selected
        if (
          enableTaxWatch === "true" &&
          (taxApplicationLevelWatch === TaxApplicationLevel.Line ||
            taxApplicationLevelWatch === TaxApplicationLevel.Both) &&
          taxTypeId
        ) {
          const selectedTax = taxOptions.find(
            (tax: any) => tax.value === taxTypeId
          );
          if (selectedTax) {
            const taxPercentage = parseFloat(
              selectedTax.label.match(/(\d+(\.\d+)?)/)?.[0] || "0"
            );
            if (taxPercentage > 0) {
              const taxAmount = (lineAmount * taxPercentage) / 100;
              totalWithTax = lineAmount + taxAmount;
            }
          }
        }

        // Only update if value changed
        if (newAllocations[lineId] !== totalWithTax) {
          newAllocations[lineId] = totalWithTax;
          hasChanges = true;
        }
      }
    });

    if (hasChanges) {
      setBoqLineBudgetAllocations(newAllocations);
    }
  }, [
    boqLineRequestedQuantities,
    boqLineRates,
    boqLineTaxTypes,
    availableBoqLines,
    enableTaxWatch,
    taxApplicationLevelWatch,
    taxOptions,
  ]);

  const addSelectedTasks = () => {
    console.log("Adding selected tasks:", selectedTaskIds);
    console.log(
      "Available tasks with taskRate:",
      availableTasks.map((t: any) => ({
        taskId: t.taskId,
        taskName: t.taskName,
        rate: t.rate,
        taskRate: t.taskRate,
      }))
    );
    console.log("Current taskRates state:", taskRates);
    console.log("Current taskRateRemarks state:", taskRateRemarks);

    if (selectedTaskIds.length === 0) {
      message.warning("Please select at least one task");
      return;
    }

    const workType = workTypeWatch;

    // Note: Location fields for Project Management tasks are automatically extracted from task data
    // (task.siteStructureUnitId, task.siteStructureSubUnitId, task.floorNo)
    // If task doesn't have location, it falls back to form values or existing lines
    // So no validation needed here for Project Management source

    // Validate rates and requested quantities
    const invalidRateTasks = selectedTaskIds.filter((taskId) => {
      const rate = taskRates[taskId];
      return rate === undefined || rate <= 0;
    });

    if (invalidRateTasks.length > 0) {
      message.error("Please specify valid rates for all selected tasks");
      return;
    }

    // Validate rate change remarks
    const tasksNeedingRemarks = selectedTaskIds.filter((taskId) => {
      const task: any = availableTasks.find((t: any) => t.taskId === taskId);
      const taskRate = Number(task?.taskRate) || 0;
      const enteredRate = Number(taskRates[taskId]) || 0;

      console.log(`Validation check for task ${taskId}:`, {
        taskRate,
        enteredRate,
        hasRemarks: !!taskRateRemarks[taskId],
        remarks: taskRateRemarks[taskId],
      });

      // Only require remarks if task has a rate AND entered rate is different
      // Use Math.abs for floating point comparison
      const isRateChanged =
        taskRate > 0 &&
        enteredRate > 0 &&
        Math.abs(enteredRate - taskRate) > 0.01;
      return isRateChanged && !taskRateRemarks[taskId];
    });

    if (tasksNeedingRemarks.length > 0) {
      message.error(
        "Please provide remarks for all tasks where rate has been changed from the task rate"
      );
      return;
    }
    if (workType !== WorkType.Open) {
      const invalidQuantityTasks = selectedTaskIds.filter((taskId) => {
        const requestedQty = taskRequestedQuantities[taskId];
        return requestedQty === undefined || requestedQty <= 0;
      });

      if (invalidQuantityTasks.length > 0) {
        message.error(
          "Please specify valid requested quantities for all selected tasks"
        );
        return;
      }
    }

    const totalWeightage = selectedTaskIds.reduce((sum, taskId) => {
      return sum + (taskWeightages[taskId] || 0);
    }, 0);

    if (totalWeightage > 100) {
      message.error(
        `Total weightage (${totalWeightage.toFixed(1)}%) cannot exceed 100%`
      );
      return;
    }

    // Validate budget allocations for Project Management source
    const invalidBudgetTasks = selectedTaskIds.filter((taskId) => {
      const task = availableTasks.find((t) => t.taskId === taskId);
      const allocatedBudget = taskBudgetAllocations[taskId] || 0;
      const availableBudget =
        (task?.budgetValue || 0) - (task?.WorkOrderAllocatedBudget || 0);

      return allocatedBudget > availableBudget;
    });

    if (invalidBudgetTasks.length > 0) {
      message.error(
        "Budget allocation exceeds available budget for some tasks. Please check and adjust."
      );
      return;
    }

    try {
      const currentLines = getValues("workOrderLines") || [];
      console.log("Current lines:", currentLines);

      const selectedTasks = availableTasks.filter((task) =>
        selectedTaskIds.includes(task.taskId)
      );
      console.log("Selected tasks to add:", selectedTasks);
      selectedTasks.forEach((task: any) => {
        console.log(`Task ${task.taskId} location data:`, {
          siteStructureUnitId: task.siteStructureUnitId,
          siteStructureSubUnitId: task.siteStructureSubUnitId,
          floorNo: task.floorNo,
          siteStructureUnitName: task.siteStructureUnitName,
          siteStructureSubUnitName: task.siteStructureSubUnitName,
        });
      });

      if (selectedTasks.length === 0) {
        message.error("No valid tasks found to add");
        return;
      }

      const newLines: WorkOrderLine[] = selectedTasks.map(
        (task: any, index) => {
          // Use the rates, weightages, and requested quantities inputted in the modal
          const rate = taskRates[task.taskId] || 0;
          const weightage = taskWeightages[task.taskId] || 0;
          const requestedQuantity = workType === WorkType.Open ? 0 : (taskRequestedQuantities[task.taskId] || 0);
          const lineAmount = requestedQuantity * rate;

          // Get tax information from selected tax type
          const selectedTaxTypeId = taskTaxTypes[task.taskId] || "";
          const selectedTaxOption = taxOptions.find(
            (tax) => tax.value === selectedTaxTypeId
          );
          const taxTypeName = selectedTaxOption ? selectedTaxOption.label : "";

          // Extract tax percentage from label (e.g., "GST - 18%" -> 18)
          const taxPercentage = selectedTaxOption
            ? parseFloat(
              selectedTaxOption.label.split(" - ")[1]?.replace("%", "") || "0"
            )
            : 0;

          const taxAmount =
            enableTaxWatch === "true" &&
              (taxApplicationLevelWatch === TaxApplicationLevel.Line ||
                taxApplicationLevelWatch === TaxApplicationLevel.Both) &&
              taxPercentage > 0
              ? (lineAmount * taxPercentage) / 100
              : 0;

          // Check if rate was changed and get remarks
          const taskOriginalRate = Number(task.taskRate) || 0;
          // Only consider it a rate change if task has a rate AND entered rate is different
          // Use Math.abs for floating point comparison
          const isRateChanged =
            taskOriginalRate > 0 &&
            rate > 0 &&
            Math.abs(rate - taskOriginalRate) > 0.01;
          const rateRemarks = isRateChanged
            ? taskRateRemarks[task.taskId] || ""
            : "";

          console.log(`Creating work order line for task ${task.taskName}:`, {
            taskId: task.taskId,
            taskOriginalRate,
            rate,
            isRateChanged,
            rateRemarks,
            taskRateRemarksState: taskRateRemarks[task.taskId],
          });

          // Get location fields for Project Management tasks
          // Priority: Task location data > Form values > Existing lines
          const isVilla = workLevelWatch === "Villa";

          // First, try to get location from task data itself (Project Management tasks have location)
          let towerVillaId =
            task.siteStructureUnitId !== undefined &&
              task.siteStructureUnitId !== null
              ? task.siteStructureUnitId.toString()
              : null;
          let towerVillaName = task.siteStructureUnitName || undefined;
          let floorName = isVilla ? null : task.floorNo || null;
          let flatId =
            isVilla ||
              task.siteStructureSubUnitId === undefined ||
              task.siteStructureSubUnitId === null
              ? null
              : task.siteStructureSubUnitId.toString();
          let flatNameDisplay = isVilla
            ? undefined
            : task.siteStructureSubUnitName || undefined;
          let locationId =
            task.locationId !== undefined && task.locationId !== null
              ? task.locationId.toString()
              : null;
          let locationName = task.locationName || undefined;

          console.log(`Extracted location values for task ${task.taskId}:`, {
            towerVillaId,
            floorName,
            flatId,
            locationId,
          });

          // If task doesn't have location, try form values (fallback)
          if (!towerVillaId || !floorName || !flatId) {
            const formValues = getValues() as any;
            towerVillaId = towerVillaId || formValues.towerVilla || null;
            towerVillaName = towerVillaName || undefined;
            floorName =
              floorName || (isVilla ? null : formValues.floorName || null);
            flatId = flatId || (isVilla ? null : formValues.flatName || null);
            flatNameDisplay = flatNameDisplay || undefined;
            locationId = locationId || formValues.locationId || null;
            locationName = locationName || undefined;
          }

          // If still missing, try to inherit from existing lines (last fallback)
          if (
            (!towerVillaId || !floorName || !flatId) &&
            currentLines.length > 0
          ) {
            const firstLineWithLocation = currentLines.find(
              (line: any) => line.towerVilla
            );
            if (firstLineWithLocation) {
              towerVillaId =
                towerVillaId || firstLineWithLocation.towerVilla || null;
              towerVillaName =
                towerVillaName ||
                firstLineWithLocation.towerVillaName ||
                undefined;
              floorName =
                floorName ||
                (isVilla ? null : firstLineWithLocation.floorName || null);
              flatId =
                flatId ||
                (isVilla ? null : firstLineWithLocation.flatName || null);
              flatNameDisplay =
                flatNameDisplay ||
                (isVilla
                  ? undefined
                  : firstLineWithLocation.flatNameDisplay || undefined);
              locationId =
                locationId || firstLineWithLocation.locationId || null;
              locationName =
                locationName || firstLineWithLocation.locationName || undefined;
            }
          }

          return {
            id: `wol-${Date.now()}-${index}`,
            lineNumber: `WOL-${(currentLines.length + index + 1)
              .toString()
              .padStart(3, "0")}`,
            workCategoryId: task.workCategoryId,
            workCategory: task.workCategory,
            taskTitle: task.taskName,
            taskDescription: task.description || task.taskName,
            quantity: requestedQuantity,
            uomId: task.workUomId ? Number(task.workUomId) : null,
            uom: task.uom,
            weightagePercentage: weightage,
            rate: rate,
            taskRate: taskOriginalRate, // Store original task rate for reference
            rateChangeRemarks: rateRemarks, // Store rate change remarks
            remarks: rateRemarks, // Store in remarks key for next steps
            taxType: selectedTaxTypeId, // Store ID for API submission
            taxTypeName: taxTypeName, // Store label for display
            taxPercentage: taxPercentage,
            taxAmount: taxAmount,
            totalAmount: lineAmount + taxAmount,
            source: WorkOrderLineSource.ProjectManagement,
            sourceId: task.taskId,
            executedQuantity: 0,
            remainingQuantity: requestedQuantity,
            completionPercentage: 0,
            // Budget allocation data for Project Management source
            budgetLineId: task.budgetLineId,
            budgetLineName: task.budgetLineName,
            budgetAllocation: taskBudgetAllocations[task.taskId] || 0,
            // Work type from header level form
            workType: workTypeWatch as WorkType,
            // Work level and location fields for Project Management tasks
            workLevel: workLevelWatch || "",
            towerVilla: towerVillaId,
            towerVillaName: towerVillaName,
            floorName: floorName,
            flatName: flatId,
            flatNameDisplay: flatNameDisplay,
            locationId: locationId,
            locationName: locationName,
            // Store files for this specific task
            files: taskFiles[task.taskId] || [],
          };

          console.log(`Final work order line location fields:`, {
            taskTitle: task.taskName,
            towerVilla: towerVillaId,
            floorName: floorName,
            flatName: flatId,
            locationId: locationId,
          });
        }
      );

      const updatedLines = [...currentLines, ...newLines];
      setValue("workOrderLines", updatedLines);
      console.log("Updated work order lines:", updatedLines);

      // Clear file list after adding lines
      setProjectManagementFileList([]);
      setValue("projectManagementFiles" as any, []);

      setSelectedTaskIds([]);

      // Clear the rates, weightages, requested quantities, tax types, and rate remarks for the added tasks
      const newTaskRates = { ...taskRates };
      const newTaskWeightages = { ...taskWeightages };
      const newTaskRequestedQuantities = { ...taskRequestedQuantities };
      const newTaskTaxTypes = { ...taskTaxTypes };
      const newTaskRateRemarks = { ...taskRateRemarks };
      selectedTaskIds.forEach((taskId) => {
        delete newTaskRates[taskId];
        delete newTaskWeightages[taskId];
        delete newTaskRequestedQuantities[taskId];
        delete newTaskTaxTypes[taskId];
        delete newTaskRateRemarks[taskId];
      });
      setTaskRates(newTaskRates);
      setTaskWeightages(newTaskWeightages);
      setTaskRequestedQuantities(newTaskRequestedQuantities);
      setTaskTaxTypes(newTaskTaxTypes);
      setTaskRateRemarks(newTaskRateRemarks);

      setIsTaskSelectionModalVisible(false);
      message.success(`${selectedTasks.length} task(s) added to work order`);
    } catch (error) {
      console.error("Error adding tasks:", error);
      message.error("Failed to add tasks. Please try again.");
    }
  };

  // Work BOQ Line Selection
  const openBoqSelectionModal = () => {
    console.log("Opening BOQ selection modal");

    if (!getValues("workBoqId")) {
      message.warning("Please select Work BOQ first");
      return;
    }

    console.log("Fetching BOQ lines before opening modal...");
    fetchSourceData().then(() => {
      // Initialize rates, weightages, and requested quantities for all available BOQ lines after they're loaded
      setTimeout(() => {
        const newBoqLineRates = { ...boqLineRates };
        const newBoqLineWeightages = { ...boqLineWeightages };
        const newBoqLineRequestedQuantities = { ...boqLineRequestedQuantities };

        availableBoqLines.forEach((line) => {
          if (!newBoqLineRates[line.lineId]) {
            newBoqLineRates[line.lineId] = 0;
          }
          if (!newBoqLineWeightages[line.lineId]) {
            newBoqLineWeightages[line.lineId] = 0;
          }
          if (!newBoqLineRequestedQuantities[line.lineId]) {
            newBoqLineRequestedQuantities[line.lineId] = 0;
          }
        });

        setBoqLineRates(newBoqLineRates);
        setBoqLineWeightages(newBoqLineWeightages);
        setBoqLineRequestedQuantities(newBoqLineRequestedQuantities);
        console.log(
          "Initialized rates and requested quantities for",
          availableBoqLines.length,
          "BOQ lines"
        );
      }, 100);
    });
    setIsBoqSelectionModalVisible(true);
  };

  const handleBoqSelectionChange = (selectedRowKeys: React.Key[]) => {
    console.log("BOQ selection changed:", selectedRowKeys);
    setSelectedBoqLineIds(selectedRowKeys as string[]);
  };

  const addSelectedBoqLines = () => {
    console.log("Adding selected BOQ lines:", selectedBoqLineIds);

    if (selectedBoqLineIds.length === 0) {
      message.warning("Please select at least one BOQ line");
      return;
    }

    // Validate rates and requested quantities
    const invalidRateLines = selectedBoqLineIds.filter((lineId) => {
      const rate = boqLineRates[lineId];
      return rate === undefined || rate <= 0;
    });

    if (invalidRateLines.length > 0) {
      message.error("Please specify valid rates for all selected BOQ lines");
      return;
    }

    const invalidQuantityLines = selectedBoqLineIds.filter((lineId) => {
      const requestedQty = boqLineRequestedQuantities[lineId];
      return requestedQty === undefined || requestedQty <= 0;
    });

    if (invalidQuantityLines.length > 0) {
      message.error(
        "Please specify valid requested quantities for all selected BOQ lines"
      );
      return;
    }

    // Validate rate change remarks
    const linesNeedingRemarks = selectedBoqLineIds.filter((lineId) => {
      const boqLine: any = availableBoqLines.find(
        (line: any) => line.lineId === lineId
      );
      const boqRate = Number(boqLine?.rate) || 0;
      const enteredRate = Number(boqLineRates[lineId]) || 0;

      // Only require remarks if BOQ has a rate AND entered rate is different
      const isRateChanged =
        boqRate > 0 &&
        enteredRate > 0 &&
        Math.abs(enteredRate - boqRate) > 0.01;
      return isRateChanged && !boqLineRateRemarks[lineId];
    });

    if (linesNeedingRemarks.length > 0) {
      message.error(
        "Please provide remarks for all BOQ lines where rate has been changed from the target rate"
      );
      return;
    }

    // Check total weightage for BOQ lines
    const totalWeightage = selectedBoqLineIds.reduce((sum, lineId) => {
      return sum + (boqLineWeightages[lineId] || 0);
    }, 0);

    if (totalWeightage > 100) {
      message.error(
        `Total weightage (${totalWeightage.toFixed(1)}%) cannot exceed 100%`
      );
      return;
    }

    // Validate budget allocations for Work BOQ source
    const invalidBudgetLines = selectedBoqLineIds.filter((lineId) => {
      const line = availableBoqLines.find((l) => l.lineId === lineId);
      const allocatedBudget = boqLineBudgetAllocations[lineId] || 0;
      const availableBudget =
        (line?.budgetAllocation || 0) - (line?.workOrderAllocatedBudget || 0);

      return allocatedBudget > availableBudget;
    });

    if (invalidBudgetLines.length > 0) {
      message.error(
        "Budget allocation exceeds available budget for some BOQ lines. Please check and adjust."
      );
      return;
    }

    try {
      const currentLines = getValues("workOrderLines") || [];
      const selectedLines = availableBoqLines.filter((line) =>
        selectedBoqLineIds.includes(line.lineId)
      );
      console.log("Selected BOQ lines to add:", selectedLines);

      if (selectedLines.length === 0) {
        message.error("No valid BOQ lines found to add");
        return;
      }

      const newLines: WorkOrderLine[] = selectedLines.map(
        (line: any, index) => {
          // Use the rates, weightages, and requested quantities inputted in the modal
          const rate = boqLineRates[line.lineId] || 0;
          const requestedQuantity =
            boqLineRequestedQuantities[line.lineId] || 0;
          const weightage = boqLineWeightages[line.lineId] || 0;
          const lineAmount = requestedQuantity * rate;

          // Get tax information from selected tax type
          const selectedTaxTypeId = boqLineTaxTypes[line.lineId] || "";
          const selectedTaxOption = taxOptions.find(
            (tax) => tax.value === selectedTaxTypeId
          );
          const taxTypeName = selectedTaxOption ? selectedTaxOption.label : "";

          // Extract tax percentage from label (e.g., "GST - 18%" -> 18)
          const taxPercentage = selectedTaxOption
            ? parseFloat(
              selectedTaxOption.label.split(" - ")[1]?.replace("%", "") || "0"
            )
            : 0;

          const taxAmount =
            enableTaxWatch === "true" &&
              (taxApplicationLevelWatch === TaxApplicationLevel.Line ||
                taxApplicationLevelWatch === TaxApplicationLevel.Both) &&
              taxPercentage > 0
              ? (lineAmount * taxPercentage) / 100
              : 0;

          // Check if rate was changed and get remarks
          const boqOriginalRate = Number(line.rate) || 0;
          const isRateChanged =
            boqOriginalRate > 0 &&
            rate > 0 &&
            Math.abs(rate - boqOriginalRate) > 0.01;
          const rateRemarks = isRateChanged
            ? boqLineRateRemarks[line.lineId] || ""
            : "";

          return {
            id: `wol-${Date.now()}-${index}`,
            lineNumber: `WOL-${(currentLines.length + index + 1)
              .toString()
              .padStart(3, "0")}`,
            workCategoryId: line.workCategoryId,
            workCategory: line.workCategory,
            taskTitle: line.taskName,
            taskDescription: `BOQ Line: ${line.taskName}`,
            quantity: requestedQuantity,
            uom: line.uom,
            uomId: line.uomId,
            weightagePercentage: weightage,
            rate: rate, // Execution Rate (for API submission)
            boqRate: boqOriginalRate, // Target Rate (original BOQ rate)
            rateChangeRemarks: rateRemarks, // Store rate change remarks
            remarks: rateRemarks, // Store in remarks key for next steps
            taxType: selectedTaxTypeId, // Store ID for API submission
            taxTypeName: taxTypeName, // Store label for display
            taxPercentage: taxPercentage,
            taxAmount: taxAmount,
            totalAmount: lineAmount + taxAmount,
            source: WorkOrderLineSource.WorkBOQ,
            sourceId: line.lineId,
            executedQuantity: 0,
            remainingQuantity: requestedQuantity,
            completionPercentage: 0,
            // Budget allocation data for Work BOQ source
            budgetLineId: line.budgetLineId,
            budgetLineName: line.budgetLineName,
            budgetAllocation: boqLineBudgetAllocations[line.lineId] || 0,
            towerVillaId: line.towerVillaId,
            towerVillaName: line.towerVillaName,
            floorName: line.floorName,
            flatId: line.flatId,
            flatName: line.flatName,
            locationId: line.locationId || "",
            locationName: line.locationName,
            // Work BOQ is always quantity based
            workType: WorkType.QTY,
            // Store files for this specific BOQ line
            files: boqLineFiles[line.lineId] || [],
          };
        }
      );

      const updatedLines = [...currentLines, ...newLines];
      setValue("workOrderLines", updatedLines);
      console.log("Updated work order lines:", updatedLines);

      // Clear BOQ line files for added lines
      const newBoqLineFiles = { ...boqLineFiles };
      selectedBoqLineIds.forEach((lineId) => {
        delete newBoqLineFiles[lineId];
      });
      setBoqLineFiles(newBoqLineFiles);

      setSelectedBoqLineIds([]);

      // Clear the rates, weightages, requested quantities, tax types, and rate remarks for the added BOQ lines
      const newBoqLineRates = { ...boqLineRates };
      const newBoqLineWeightages = { ...boqLineWeightages };
      const newBoqLineRequestedQuantities = { ...boqLineRequestedQuantities };
      const newBoqLineTaxTypes = { ...boqLineTaxTypes };
      const newBoqLineRateRemarks = { ...boqLineRateRemarks };
      selectedBoqLineIds.forEach((lineId) => {
        delete newBoqLineRates[lineId];
        delete newBoqLineWeightages[lineId];
        delete newBoqLineRequestedQuantities[lineId];
        delete newBoqLineTaxTypes[lineId];
        delete newBoqLineRateRemarks[lineId];
      });
      setBoqLineRates(newBoqLineRates);
      setBoqLineWeightages(newBoqLineWeightages);
      setBoqLineRequestedQuantities(newBoqLineRequestedQuantities);
      setBoqLineTaxTypes(newBoqLineTaxTypes);
      setBoqLineRateRemarks(newBoqLineRateRemarks);

      setIsBoqSelectionModalVisible(false);
      message.success(
        `${selectedLines.length} BOQ line(s) added to work order`
      );
    } catch (error) {
      console.error("Error adding BOQ lines:", error);
      message.error("Failed to add BOQ lines. Please try again.");
    }
  };

  // Helper function to calculate available budget for a budget line
  const calculateAvailableBudget = (
    budgetLineId: number,
    budgetValue: number,
    allocatedLineValue: number,
    excludeLineIndex?: number
  ) => {
    // Start with backend allocated amount
    let available = budgetValue - allocatedLineValue;

    const isEditMode = formTypeWatch === "edit";

    // In edit mode, the allocatedLineValue already includes allocations from THIS work order
    // So we should NOT subtract current work order allocations again
    if (!isEditMode) {
      // CREATE MODE: Subtract allocations from current work order lines
      const currentLines = getValues("workOrderLines") || [];
      const allocationsForThisLine = currentLines
        .filter((line: any, index: number) => {
          // When editing a line, exclude its current allocation from the calculation
          if (
            excludeLineIndex !== undefined &&
            excludeLineIndex >= 0 &&
            index === excludeLineIndex
          ) {
            return false;
          }
          return line.budgetLineId === budgetLineId;
        })
        .reduce(
          (sum: number, line: any) => sum + (line.budgetAllocation || 0),
          0
        );

      available -= allocationsForThisLine;
    } else {
      // EDIT MODE: Only exclude the line being edited (to allow increasing its allocation)
      if (excludeLineIndex !== undefined && excludeLineIndex >= 0) {
        const currentLines = getValues("workOrderLines") || [];
        const lineBeingEdited = currentLines[excludeLineIndex];
        if (lineBeingEdited && lineBeingEdited.budgetLineId === budgetLineId) {
          // Add back the line's current allocation so user can reallocate it
          available += lineBeingEdited.budgetAllocation || 0;
        }
      }
    }

    return available;
  };

  // New Work Line Creation
  const openNewWorkLineModal = () => {
    console.log("Opening new work line modal");

    const workLevel = getValues("workLevel");
    const workType = getValues("workType");

    if (!workLevel && !workType) {
      message.error(
        "Work Level is not configured for the selected site. Please select Work Type."
      );
      return;
    }

    if (!workLevel) {
      message.error(
        "Work Level is not configured for the selected site. Please check site configuration."
      );
      return;
    }

    if (!workType) {
      message.error("Please select Work Type");
      return;
    }
    newWorkLineFormMethods.reset({
      workCategory: "",
      subCategory: "",
      taskTitle: "",
      taskDescription: "",
      quantity: "",
      uom: "",
      weightagePercentage: "",
      rate: "",
      taxType: "",
      taxPercentage: "",
      budgetLineId: "",
      budgetAllocation: "",
      towerVilla: "",
      floorName: "",
      flatName: "",
    });
    setIsNewWorkLineModalVisible(true);
  };

  const handleNewWorkLineSubmit = async () => {
    try {
      console.log("Submitting new work line");

      // Use react-hook-form handleSubmit for validation and data
      const isValid = await newWorkLineFormMethods.trigger();
      if (!isValid) {
        message.error("Please fix the form validation errors");
        return;
      }

      const values = newWorkLineFormMethods.getValues();
      console.log("New work line values:", values);

      // Get workType from main form (header level)
      const workType = getValues("workType");


      // Validate dates for New Work Line source
      if (selectedSourceWatch === WorkOrderLineSource.NewWorkLine) {
        const startDate = values.scheduledStartDate;
        const endDate = values.scheduledEndDate;

        // Only validate if both dates are provided
        if (startDate && endDate) {
          const dateValidation = validateTaskDates(startDate, endDate);
          if (!dateValidation.valid) {
            message.error(dateValidation.message);
            return;
          }
        } else if (startDate || endDate) {
          // If only one date is provided, validate that single date against work order dates
          const workOrderStartDate = getValues("plannedStartDate");
          const workOrderEndDate = getValues("plannedEndDate");

          if (startDate && workOrderStartDate && dayjs(startDate).isBefore(dayjs(workOrderStartDate), 'day')) {
            message.error(`Planned Start Date cannot be before Work Order Start Date (${dayjs(workOrderStartDate).format('DD-MMM-YYYY')})`);
            return;
          }

          if (endDate && workOrderEndDate && dayjs(endDate).isAfter(dayjs(workOrderEndDate), 'day')) {
            message.error(`Planned End Date cannot be after Work Order End Date (${dayjs(workOrderEndDate).format('DD-MMM-YYYY')})`);
            return;
          }
        }
      }

      // Additional validation for required fields - quantity not required for Open workType
      if (
        !workType ||
        !values.workCategory ||
        !values.taskTitle ||
        (workType !== WorkType.Open && !values.quantity) ||
        !values.uom ||
        !values.rate
      ) {
        const missingFields = [];
        if (!workType) missingFields.push("Work Type (from header)");
        if (!values.workCategory) missingFields.push("Work Category");
        if (!values.taskTitle) missingFields.push("Task Title");
        if (workType !== WorkType.Open && !values.quantity)
          missingFields.push("Quantity");
        if (!values.uom) missingFields.push("UOM");
        if (!values.rate) missingFields.push("Rate");

        message.error(
          `Please fill in all required fields: ${missingFields.join(", ")}`
        );
        return;
      }

      // For Open workType, set quantity to 0
      const quantity =
        workType === WorkType.Open ? 0 : parseFloat(values.quantity);
      const rate = parseFloat(values.rate);

      if (
        (workType !== WorkType.Open && (isNaN(quantity) || quantity <= 0)) ||
        isNaN(rate) ||
        rate <= 0
      ) {
        message.error(
          workType === WorkType.Open
            ? "Rate must be greater than 0"
            : "Quantity and Rate must be greater than 0"
        );
        return;
      }

      // Validate work level location fields based on workLevel
      const workLevel = getValues("workLevel");
      if (workLevel === "Tower / Block") {
        if (!values.towerVilla) {
          message.error("Please select Tower / Block / Villa");
          return;
        }
      } else if (workLevel === "Floor") {
        if (!values.towerVilla || !values.floorName) {
          message.error("Please select Tower / Block / Villa and Floor");
          return;
        }
      }
      else if (workLevel === "Flat") {
        if (!values.towerVilla || !values.floorName) {
          message.error("Please select Tower / Block / Villa, Floor");
          return;
        }
      }
      else if (workLevel === "Villa") {
        if (!values.flatName) {
          message.error("Please select Villa");
          return;
        }
      }

      // Validate budget allocation does not exceed available budget
      if (values.budgetLineId && values.budgetAllocation) {
        const budgetLine = budgetLines.find(
          (bl: any) => bl.lineId.toString() === values.budgetLineId
        );
        if (budgetLine) {
          const availableBudget = calculateAvailableBudget(
            budgetLine.lineId,
            budgetLine.budgetValue,
            budgetLine.allocatedLineValue,
            editingLineIndex !== null ? editingLineIndex : undefined
          );

          const allocatedAmount = parseFloat(values.budgetAllocation);
          if (allocatedAmount > availableBudget) {
            Swal.fire({
              icon: "error",
              title: "Budget Exceeded",
              text: `Allocated budget (₹${allocatedAmount.toLocaleString("en-IN")}) exceeds available budget (₹${availableBudget.toLocaleString("en-IN")}). Please reduce the allocation or select a different budget line.`,
              confirmButtonText: "OK",
            });
            return; // Don't add the line
          }
        }
      }

      const currentLines = getValues("workOrderLines") || [];
      console.log("Current lines before adding:", currentLines);

      const lineAmount = quantity * rate;

      // Extract tax percentage from selected tax option
      const newLineTaxOption = values.taxType
        ? taxOptions.find((tax) => tax.value === values.taxType)
        : null;
      const taxPercentage = newLineTaxOption
        ? parseFloat(
          newLineTaxOption.label.split(" - ")[1]?.replace("%", "") || "0"
        )
        : 0;

      const taxAmount =
        enableTaxWatch === "true" &&
          (taxApplicationLevelWatch === TaxApplicationLevel.Line ||
            taxApplicationLevelWatch === TaxApplicationLevel.Both) &&
          taxPercentage > 0
          ? (lineAmount * taxPercentage) / 100
          : 0;
      console.log("taxAmount", taxAmount);
      // Get display names for Category, UOM, and Tax Type
      const workCategoryLabel =
        workCategoryOptions.find((cat) => cat.value === values.workCategory)
          ?.label || values.workCategory;

      const uomLabel =
        uomOptions.find((uom) => uom.value === values.uom)?.label || values.uom;

      // Extract just the UOM name (remove the code part in parentheses)
      const uomDisplayName = uomLabel.includes("(")
        ? uomLabel.split("(")[0].trim()
        : uomLabel;

      // Get tax type display name and keep ID separate
      const selectedTaxOption = values.taxType
        ? taxOptions.find((tax) => tax.value === values.taxType)
        : null;
      const taxTypeLabel = selectedTaxOption ? selectedTaxOption.label : "";
      const taxTypeId = values.taxType || "";

      // Handle work level location fields
      // workLevel already declared above in validation section
      const isVilla = workLevel === "Villa";

      const newLine: any = {
        id: `wol-${Date.now()}`,
        lineNumber: `WOL-${(currentLines.length + 1)
          .toString()
          .padStart(3, "0")}`,
        workType: workType, // Add workType field
        workCategory: values.workCategory,
        workCategoryName: workCategoryLabel,
        subCategory: values.subCategory || "",
        taskTitle: values.taskTitle || "",
        taskDescription: values.taskDescription || values.taskTitle || "",
        quantity: quantity,
        uom: values.uom,
        uomName: uomDisplayName,
        weightagePercentage: values.weightagePercentage
          ? parseFloat(values.weightagePercentage)
          : 0,
        rate: rate,
        taxType: taxTypeId, // Store ID for API submission
        taxTypeName: taxTypeLabel, // Store label for display
        taxPercentage: taxPercentage,
        taxAmount: taxAmount,
        totalAmount: lineAmount + taxAmount,
        source: WorkOrderLineSource.NewWorkLine,
        executedQuantity: 0,
        remainingQuantity: quantity,
        completionPercentage: 0,
        // Budget allocation fields
        budgetLineId: values.budgetLineId ? parseInt(values.budgetLineId) : 0,
        budgetLineName: (() => {
          if (values.budgetLineId) {
            const budgetLine = budgetLines.find(
              (bl: any) => bl.lineId.toString() === values.budgetLineId
            );
            return budgetLine?.budgetLineName || "";
          }
          return "";
        })(),
        budgetAllocation: values.budgetAllocation
          ? parseFloat(values.budgetAllocation)
          : 0,
        // Work Level Settings location fields
        // For Villa: villa number from flatName goes in towerVilla for storage
        workLevel: workLevel,
        towerVilla: isVilla ? values.flatName || "" : values.towerVilla || "",
        towerVillaName: isVilla
          ? towerVillaOptions.find((opt) => opt.value === values.flatName)
            ?.label ||
          values.flatName ||
          ""
          : towerVillaOptions.find((opt) => opt.value === values.towerVilla)
            ?.label ||
          values.towerVilla ||
          "",
        floorName: isVilla ? "" : values.floorName || "",
        flatName: isVilla ? null : values.flatName || "",
        flatNameDisplay: isVilla
          ? null
          : flatOptions.find((opt) => opt.value === values.flatName)?.label ||
          values.flatName ||
          "",
        locationId: values.locationId || "",
        locationName:
          locationOptions.find((opt) => opt.value === values.locationId)
            ?.label || "",
        // Schedule breakdown stages
        stages: newLineScheduleEnabled
          ? newLineStages.map((stage) => ({
            ...(stage.workOrderLineStageId
              ? { workOrderLineStageId: stage.workOrderLineStageId }
              : {}),
            stageTitle: stage.stageTitle,
            splitPercentage: stage.splitPercentage,
          }))
          : [],
        deletedStageIds: newLineDeletedStageIds,
        // Store files for this line
        files: [...newWorkLineFileList],
        scheduledStartDate: values.scheduledStartDate
          ? dayjs(values.scheduledStartDate).format("YYYY-MM-DD")
          : "",
        scheduledEndDate: values.scheduledEndDate
          ? dayjs(values.scheduledEndDate).format("YYYY-MM-DD")
          : "",
      };

      // Store budget header ID for submission if budget line is selected
      if (values.budgetLineId && selectedBudgetId) {
        setBudgetHeaderId(parseInt(selectedBudgetId));
        setValue("budgetHeaderId", selectedBudgetId);
        console.log(
          "Stored budget header ID for New Work Line:",
          selectedBudgetId
        );
      }

      console.log("New line created:", newLine);
      console.log("Tax details:", {
        taxType: taxTypeLabel,
        taxPercentage: taxPercentage,
        taxAmount: taxAmount,
        enableTax: enableTaxWatch,
        taxApplicationLevel: taxApplicationLevelWatch,
      });

      let updatedLines;
      // Check if we're in edit mode (editingLineIndex is set)
      if (editingLineIndex >= 0) {
        // Edit mode: update existing line
        const existingLine = currentLines[editingLineIndex];
        updatedLines = [...currentLines];

        // For PM and BOQ lines, preserve source-specific fields that shouldn't change
        if (existingLine.source === WorkOrderLineSource.ProjectManagement) {
          // Preserve PM-specific fields
          updatedLines[editingLineIndex] = {
            ...existingLine, // Keep all original fields first
            ...newLine, // Update with form values (allows editing attachments, budget, etc.)
            id: existingLine.id, // Keep original ID
            lineNumber: existingLine.lineNumber, // Keep original line number
            source: existingLine.source, // Keep source type
            sourceId: existingLine.sourceId, // Keep source ID (taskId)
            taskRate: existingLine.taskRate, // Keep original task rate
            // Update files from current file list
            files: [...newWorkLineFileList],
          };
        } else if (existingLine.source === WorkOrderLineSource.WorkBOQ) {
          // Preserve BOQ-specific fields
          updatedLines[editingLineIndex] = {
            ...existingLine, // Keep all original fields first
            ...newLine, // Update with form values
            id: existingLine.id, // Keep original ID
            lineNumber: existingLine.lineNumber, // Keep original line number
            source: existingLine.source, // Keep source type
            sourceId: existingLine.sourceId, // Keep source ID (lineId)
            boqRate: existingLine.boqRate, // Keep original BOQ rate
            // Update files from current file list
            files: [...newWorkLineFileList],
          };
        } else {
          // New Work Line - can update all fields
          updatedLines[editingLineIndex] = {
            ...existingLine,
            ...newLine,
            id: existingLine.id,
            lineNumber: existingLine.lineNumber,
            // Update files from current file list
            files: [...newWorkLineFileList],
          };
        }
        console.log("Updated line at index:", updatedLines);
      } else {
        // Add mode: append new line
        updatedLines = [...currentLines, newLine];
        console.log("Added new line");
      }

      setValue("workOrderLines", updatedLines, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      console.log("Updated lines:", updatedLines);

      // Clear file list after adding/updating line
      setNewWorkLineFileList([]);
      setValue("newWorkLineFiles" as any, []);

      // Close modal and reset form
      setIsNewWorkLineModalVisible(false);
      setEditingLineIndex(-1); // Reset editing index
      // Reset cascading dropdown options
      setFloorOptions([]);
      setFlatOptions([]);
      prevTowerVillaRef.current = "";
      prevFloorNameRef.current = "";
      newWorkLineFormMethods.reset({
        workCategory: "",
        subCategory: "",
        taskTitle: "",
        taskDescription: "",
        quantity: "",
        uom: "",
        weightagePercentage: "",
        rate: "",
        taxType: "",
        taxPercentage: "",
        budgetLineId: "",
        budgetAllocation: "",
        towerVilla: "",
        floorName: "",
        flatName: "",
        locationId: "",
      });
      // Reset schedule breakdown state
      setNewLineStages([]);
      setNewLineScheduleEnabled(false);
      setNewLineDeletedStageIds([]);

      message.success(
        editingLineIndex >= 0
          ? "Work line updated successfully"
          : "New work line added successfully"
      );
    } catch (error: any) {
      console.error("Error adding new work line:", error);
      if (error.errorFields) {
        message.error("Please fill in all required fields correctly");
      } else {
        message.error("Failed to add work line. Please try again.");
      }
    }
  };

  // File upload handlers - handle file list changes
  // Handler for per-task file changes
  const handleTaskFileChange = (taskId: string, info: any) => {
    let fileList = [...info.fileList];
    fileList = fileList.filter((file) => file.status !== "error");

    const processedFileList = fileList.map((file, index) => {
      const actualFile = file.originFileObj || file;
      const fileUid = file.uid || `${Date.now()}-${Math.random()}-${index}`;

      return {
        uid: fileUid,
        name: file.name,
        status:
          file.status === "uploading" || file.status === "done"
            ? ("done" as const)
            : file.status,
        url:
          file.url ||
          (actualFile instanceof File
            ? URL.createObjectURL(actualFile)
            : undefined),
        originFileObj: actualFile instanceof File ? actualFile : undefined,
      };
    });

    setTaskFiles((prev) => ({
      ...prev,
      [taskId]: processedFileList,
    }));
  };

  // Handler for per-task file removal
  const handleTaskFileRemove = (taskId: string, file: any) => {
    setTaskFiles((prev) => {
      const taskFileList = prev[taskId] || [];
      const updatedList = taskFileList.filter((f) => f.uid !== file.uid);
      return {
        ...prev,
        [taskId]: updatedList,
      };
    });
  };

  // Handler for per-BOQ line file changes
  const handleBoqLineFileChange = (lineId: string, info: any) => {
    let fileList = [...info.fileList];
    fileList = fileList.filter((file) => file.status !== "error");

    const processedFileList = fileList.map((file, index) => {
      const actualFile = file.originFileObj || file;
      const fileUid = file.uid || `${Date.now()}-${Math.random()}-${index}`;

      return {
        uid: fileUid,
        name: file.name,
        status:
          file.status === "uploading" || file.status === "done"
            ? ("done" as const)
            : file.status,
        url:
          file.url ||
          (actualFile instanceof File
            ? URL.createObjectURL(actualFile)
            : undefined),
        originFileObj: actualFile instanceof File ? actualFile : undefined,
      };
    });

    setBoqLineFiles((prev) => ({
      ...prev,
      [lineId]: processedFileList,
    }));
  };

  // Handler for per-BOQ line file removal
  const handleBoqLineFileRemove = (lineId: string, file: any) => {
    setBoqLineFiles((prev) => {
      const boqLineFileList = prev[lineId] || [];
      const updatedList = boqLineFileList.filter((f) => f.uid !== file.uid);
      return {
        ...prev,
        [lineId]: updatedList,
      };
    });
  };

  const handleFileChange = (
    info: any,
    sourceType: "projectManagement" | "workBoq" | "newWorkLine"
  ) => {
    let fileList = [...info.fileList];

    // Filter out files that failed validation (status === "error")
    fileList = fileList.filter((file) => file.status !== "error");

    // Process each file and ensure it has originFileObj and proper status
    const processedFileList = fileList.map((file, index) => {
      // Get the actual File object - prioritize originFileObj which contains the real File
      const actualFile = file.originFileObj || file;

      // Ensure we have a unique uid for each file
      const fileUid = file.uid || `${Date.now()}-${Math.random()}-${index}`;

      // Create processed file object
      const processedFile = {
        uid: fileUid,
        name: file.name,
        // Set status to "done" for uploading or done files (customRequest simulates success)
        status:
          file.status === "uploading" || file.status === "done"
            ? ("done" as const)
            : file.status,
        url:
          file.url ||
          (actualFile instanceof File
            ? URL.createObjectURL(actualFile)
            : undefined),
        // Ensure originFileObj is set - this is crucial for form submission
        originFileObj:
          actualFile instanceof File ? actualFile : file.originFileObj || file,
        response: file.response || {
          fileId: `file_${Date.now()}_${index}`,
          fileName: file.name,
          fileSize:
            actualFile instanceof File ? actualFile.size : file.size || 0,
          fileType:
            actualFile instanceof File ? actualFile.type : file.type || "",
          uploadDate: new Date().toISOString(),
        },
      };

      return processedFile;
    });

    // Update the appropriate file list based on source type
    if (sourceType === "projectManagement") {
      setProjectManagementFileList(processedFileList);
      // Store File objects in form context for submission
      const fileObjects = processedFileList
        .map((f) => f.originFileObj)
        .filter((f) => f instanceof File);
      setValue("projectManagementFiles" as any, fileObjects);
    } else if (sourceType === "workBoq") {
      setWorkBoqFileList(processedFileList);
      // Store File objects in form context for submission
      const fileObjects = processedFileList
        .map((f) => f.originFileObj)
        .filter((f) => f instanceof File);
      setValue("workBoqFiles" as any, fileObjects);
    } else if (sourceType === "newWorkLine") {
      setNewWorkLineFileList(processedFileList);
      // Store File objects in form context for submission
      const fileObjects = processedFileList
        .map((f) => f.originFileObj)
        .filter((f) => f instanceof File);
      setValue("newWorkLineFiles" as any, fileObjects);
    }
  };

  const handleFileRemove = (
    file: any,
    sourceType: "projectManagement" | "workBoq" | "newWorkLine"
  ) => {
    if (sourceType === "projectManagement") {
      const updatedList = projectManagementFileList.filter(
        (f) => f.uid !== file.uid
      );
      setProjectManagementFileList(updatedList);
      // Update form context with File objects
      setValue(
        "projectManagementFiles" as any,
        updatedList.map((f) => f.originFileObj)
      );
    } else if (sourceType === "workBoq") {
      const updatedList = workBoqFileList.filter((f) => f.uid !== file.uid);
      setWorkBoqFileList(updatedList);
      // Update form context with File objects
      setValue(
        "workBoqFiles" as any,
        updatedList.map((f) => f.originFileObj)
      );
    } else if (sourceType === "newWorkLine") {
      const updatedList = newWorkLineFileList.filter((f) => f.uid !== file.uid);
      setNewWorkLineFileList(updatedList);
      // Update form context with File objects
      setValue(
        "newWorkLineFiles" as any,
        updatedList.map((f) => f.originFileObj)
      );
    }
  };

  const validateFileBeforeUpload = (file: any) => {
    const isValidType = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ].includes(file.type);
    if (!isValidType) {
      Swal.fire(
        "Error!",
        "You can only upload JPG/PNG/PDF/DOC/DOCX/XLS/XLSX files!",
        "error"
      );
      return false;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      Swal.fire("Error!", "File must be smaller than 10MB!", "error");
      return false;
    }
    return true;
  };

  // Bulk Upload Handler
  const handleBulkUpload = (uploadedLines: any[]) => {
    try {
      console.log("Processing bulk uploaded lines:", uploadedLines);

      const currentLines = getValues("workOrderLines") || [];
      console.log("Current lines before bulk upload:", currentLines);

      // Process uploaded lines to match the required format
      const processedLines = uploadedLines.map((line: any, index: number) => {
        // Calculate line number based on existing lines
        const lineNumber = currentLines.length + index + 1;

        // Calculate amounts
        const lineAmount = (line.quantity || 0) * (line.rate || 0);
        const taxAmount =
          enableTaxWatch === "true" &&
            (taxApplicationLevelWatch === TaxApplicationLevel.Line ||
              taxApplicationLevelWatch === TaxApplicationLevel.Both) &&
            line.taxPercentage > 0
            ? (lineAmount * line.taxPercentage) / 100
            : 0;

        return {
          ...line,
          id: line.id || `bulk_${Date.now()}_${index}`,
          lineNumber: `WOL-${lineNumber.toString().padStart(3, "0")}`,
          source: WorkOrderLineSource.NewWorkLine,
          totalAmount: lineAmount + taxAmount,
          taxAmount: taxAmount,
          executedQuantity: 0,
          remainingQuantity: line.quantity || 0,
          completionPercentage: 0,
        };
      });

      // Count lines with errors but add ALL lines (including errors)
      const errorLines = processedLines.filter((line: any) => line.errorFlag);

      if (errorLines.length > 0) {
        message.warning(
          `${errorLines.length} line(s) have errors. Please review and fix them before proceeding.`
        );
        console.warn("Lines with errors:", errorLines);
      }

      // Add all lines including those with errors
      const updatedLines = [...currentLines, ...processedLines];
      setValue("workOrderLines", updatedLines, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      console.log("Updated lines after bulk upload:", updatedLines);

      if (errorLines.length > 0) {
        message.info(
          `Added ${processedLines.length} line(s) (${errorLines.length} with errors). Fix errors before proceeding.`
        );
      } else {
        message.success(
          `Successfully added ${processedLines.length} valid work line(s) from bulk upload`
        );
      }
    } catch (error: any) {
      console.error("Error processing bulk upload:", error);
      message.error("Failed to process bulk upload. Please try again.");
    }
  };

  const handleDeleteLine = (index: number) => {
    console.log("Deleting line at index:", index);

    Swal.fire({
      title: "Delete Work Line?",
      text: "This action cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Delete",
    }).then((result) => {
      if (result.isConfirmed) {
        const currentLines = getValues("workOrderLines") || [];
        const updatedLines = currentLines.filter((_, i) => i !== index);
        setValue("workOrderLines", updatedLines);
        message.success("Work order line deleted");
      }
    });
  };

  const handleEditLine = async (index: number) => {
    console.log("Editing line at index:", index);

    const currentLines = getValues("workOrderLines") || [];
    const lineToEdit = currentLines[index];

    if (!lineToEdit) {
      message.error("Line not found");
      return;
    }

    // Get workLevel from main form
    const workLevel = getValues("workLevel") || "";
    const isVilla = workLevel === "Villa";

    // Set isInitializingEdit to prevent cascading resets during edit initialization
    setIsInitializingEdit(true);

    // Ensure towerVillaOptions are loaded before setting form values
    let currentTowerVillaOptions = towerVillaOptions;
    if (siteIdWatch) {
      if (towerVillaOptions.length === 0) {
        const fetchedOptions = await fetchTowerVillaOptions();
        currentTowerVillaOptions = fetchedOptions || [];
      }
      // Also ensure location options are loaded
      if (locationOptions.length === 0) {
        await fetchLocationOptions();
      }
    }

    // Load cascading dropdown options based on saved values
    // Convert to string to match FormSelect value format
    const towerVillaValue = isVilla
      ? ""
      : lineToEdit.towerVilla
        ? lineToEdit.towerVilla.toString()
        : "";
    const floorNameValue = isVilla ? "" : lineToEdit.floorName || "";

    console.log("Edit mode - Extracted values:", {
      lineToEditTowerVilla: lineToEdit.towerVilla,
      towerVillaValue,
      floorNameValue,
      isVilla,
      workLevel,
    });

    // Fetch floor options if tower/villa is selected
    if (towerVillaValue && siteIdWatch) {
      try {
        const { data, status } = await dropdownApis.fetchFloorNumbersByUnitId(
          parseInt(siteIdWatch),
          parseInt(towerVillaValue)
        );
        if (status && data) {
          const options = data.map((item: any) => ({
            label: item?.floorNo,
            value: item?.floorNo,
          }));
          setFloorOptions(options);
        }
      } catch (error) {
        console.log("Error fetching floor options for edit:", error);
      }
    }

    // Fetch flat options if both tower and floor are selected
    if (towerVillaValue && floorNameValue && siteIdWatch) {
      try {
        const { data, status } =
          await dropdownApis.fetchFlatsBySiteIdUnitIdFloor(
            parseInt(siteIdWatch),
            parseInt(towerVillaValue),
            floorNameValue
          );
        if (status && data) {
          const options = data.map((item: any) => ({
            label: item?.subUnitName,
            value: item?.id?.toString(),
          }));
          setFlatOptions(options);
        }
      } catch (error) {
        console.log("Error fetching flat options for edit:", error);
      }
    }

    // Update refs to match the values being set
    prevTowerVillaRef.current = towerVillaValue;
    prevFloorNameRef.current = floorNameValue;

    // Populate form with line data
    // Ensure flatName and locationId are converted to strings for FormSelect compatibility
    const flatNameValue = isVilla
      ? lineToEdit.towerVilla?.toString() || ""
      : lineToEdit.flatName?.toString() || "";
    const locationIdValue = lineToEdit.locationId?.toString() || "";

    newWorkLineFormMethods.reset({
      workCategory: lineToEdit.workCategory || "",
      subCategory: lineToEdit.subCategory || "",
      taskTitle: lineToEdit.taskTitle || "",
      taskDescription: lineToEdit.taskDescription || "",
      quantity: lineToEdit.quantity?.toString() || "",
      uom: lineToEdit.uom || "",
      weightagePercentage: lineToEdit.weightagePercentage?.toString() || "",
      rate: lineToEdit.rate?.toString() || "",
      taxType: lineToEdit.taxType || "",
      budgetLineId: lineToEdit.budgetLineId?.toString() || "",
      budgetAllocation: lineToEdit.budgetAllocation?.toString() || "",
      // For Villa: flatName contains the villa number stored in towerVilla
      // Ensure all values are strings for FormSelect compatibility
      towerVilla: towerVillaValue,
      floorName: floorNameValue,
      flatName: flatNameValue,
      locationId: locationIdValue,
      scheduledStartDate: lineToEdit.scheduledStartDate
        ? dayjs(lineToEdit.scheduledStartDate).format("YYYY-MM-DD")
        : "",
      scheduledEndDate: lineToEdit.scheduledEndDate
        ? dayjs(lineToEdit.scheduledEndDate).format("YYYY-MM-DD")
        : "",
    });

    // Verify that tower.Villa value exists in options
    if (towerVillaValue && currentTowerVillaOptions.length > 0) {
      const valueExists = currentTowerVillaOptions.some(
        (opt) => opt.value === towerVillaValue
      );
      if (!valueExists) {
        console.warn(
          "Tower/Villa value not found in options:",
          towerVillaValue,
          "Available options:",
          currentTowerVillaOptions
        );
      }
    }

    console.log("Edit form populated with values:", {
      towerVilla: towerVillaValue,
      floorName: floorNameValue,
      flatName: flatNameValue,
      locationId: locationIdValue,
      towerVillaOptionsCount: currentTowerVillaOptions.length,
      towerVillaValueExists: towerVillaValue
        ? currentTowerVillaOptions.some((opt) => opt.value === towerVillaValue)
        : false,
    });

    // Load stages if they exist for editing
    if (lineToEdit.stages && lineToEdit.stages.length > 0) {
      setNewLineStages(
        lineToEdit.stages.map((stage: any) => ({
          workOrderLineStageId: stage.workOrderLineStageId,
          stageTitle: stage.stageTitle || "",
          splitPercentage: stage.splitPercentage || 0,
          amount: (lineToEdit.rate * (stage.splitPercentage || 0)) / 100,
        }))
      );
      setNewLineScheduleEnabled(true);
    } else {
      setNewLineStages([]);
      setNewLineScheduleEnabled(false);
    }
    setNewLineDeletedStageIds([]);

    // Load files for this line if they exist (works for all source types: PM, BOQ, New Work Line)
    const lineFiles = (lineToEdit as any).files || [];
    if (lineFiles.length > 0) {
      // Format files for Upload component
      const formattedFiles = lineFiles.map((file: any, fileIndex: number) => {
        // If file already has the right format, use it
        if (file.uid && file.name && file.status) {
          return file;
        }
        // Otherwise, format it
        if (file instanceof File) {
          return {
            uid: `${Date.now()}-${Math.random()}-${fileIndex}`,
            name: file.name,
            status: "done" as const,
            originFileObj: file,
            url: URL.createObjectURL(file),
          };
        } else if (file.apiFileData) {
          // Existing file from API (already formatted from workOrderCreationMain)
          return {
            uid:
              file.apiFileData.dmsId?.toString() ||
              file.apiFileData.fileId?.toString() ||
              file.uid ||
              `${Date.now()}-${Math.random()}-${fileIndex}`,
            name:
              file.apiFileData.fileName ||
              file.apiFileData.originalFileName ||
              file.name ||
              `File ${file.apiFileData.dmsId || file.apiFileData.fileId}`,
            status: "done" as const,
            url:
              file.apiFileData.fileUrl ||
              file.apiFileData.url ||
              file.apiFileData.filePath ||
              file.url ||
              file.filePath,
            filePath:
              file.apiFileData.filePath ||
              file.apiFileData.fileUrl ||
              file.apiFileData.url ||
              file.filePath,
            originFileObj: null,
            response: {
              fileId: file.apiFileData.dmsId || file.apiFileData.fileId,
              fileName:
                file.apiFileData.fileName || file.apiFileData.originalFileName,
              fileSize: file.apiFileData.fileSize || 0,
              fileType:
                file.apiFileData.fileType || file.apiFileData.mimeType || "",
              uploadDate:
                file.apiFileData.uploadDate ||
                file.apiFileData.createdDate ||
                new Date().toISOString(),
              filePath:
                file.apiFileData.filePath ||
                file.apiFileData.fileUrl ||
                file.apiFileData.url,
            },
            apiFileData: file.apiFileData,
          };
        } else {
          // Fallback formatting - file might be directly from API response format
          return {
            uid:
              file.dmsId?.toString() ||
              file.fileId?.toString() ||
              file.uid ||
              `${Date.now()}-${Math.random()}-${fileIndex}`,
            name:
              file.fileName ||
              file.originalFileName ||
              file.name ||
              `File ${file.dmsId || file.fileId || fileIndex}`,
            status: "done" as const,
            url: file.fileUrl || file.url || file.filePath || undefined,
            filePath: file.filePath || file.fileUrl || file.url || undefined,
            originFileObj: file.originFileObj || null,
            response: file.response || {
              fileId: file.dmsId || file.fileId,
              fileName: file.fileName || file.originalFileName,
              fileSize: file.fileSize || 0,
              fileType: file.fileType || file.mimeType || "",
              uploadDate:
                file.uploadDate || file.createdDate || new Date().toISOString(),
              filePath: file.filePath || file.fileUrl || file.url,
            },
            apiFileData: file,
          };
        }
      });
      setNewWorkLineFileList(formattedFiles);
    } else {
      // Clear file list if no files
      setNewWorkLineFileList([]);
    }

    // Set editing index and open modal
    setEditingLineIndex(index);
    setIsNewWorkLineModalVisible(true);

    // Reset isInitializingEdit after a short delay to allow form to settle
    setTimeout(() => {
      setIsInitializingEdit(false);
    }, 100);
  };

  // Handler to open schedule breakdown drawer for a line
  const handleOpenScheduleDrawer = (index: number) => {
    const currentLines = getValues("workOrderLines") || [];
    const lineToEdit = currentLines[index];

    if (!lineToEdit) {
      message.error("Line not found");
      return;
    }

    // Load existing stages if any
    if (lineToEdit.stages && lineToEdit.stages.length > 0) {
      setScheduleLineStages(
        lineToEdit.stages.map((stage: any) => ({
          workOrderLineStageId: stage.workOrderLineStageId,
          stageTitle: stage.stageTitle || "",
          splitPercentage: stage.splitPercentage || 0,
          amount: (lineToEdit.rate * (stage.splitPercentage || 0)) / 100,
        }))
      );
    } else {
      setScheduleLineStages([]);
    }

    setScheduleDeletedStageIds([]);
    setScheduleEditingLineIndex(index);
    setScheduleDrawerVisible(true);
  };

  // Handler to save schedule breakdown changes
  const handleSaveScheduleBreakdown = () => {
    const currentLines = [...(getValues("workOrderLines") || [])];
    const lineIndex = scheduleEditingLineIndex;

    if (lineIndex < 0 || lineIndex >= currentLines.length) {
      message.error("Invalid line index");
      return;
    }

    // Validate total percentage doesn't exceed 100%
    const totalPercentage = scheduleLineStages.reduce(
      (sum, stage) => sum + (stage.splitPercentage || 0),
      0
    );

    if (totalPercentage > 100) {
      message.error("Total stage percentage cannot exceed 100%");
      return;
    }

    // Update the line with new stages
    currentLines[lineIndex] = {
      ...currentLines[lineIndex],
      stages: scheduleLineStages.map((stage) => ({
        ...(stage.workOrderLineStageId
          ? { workOrderLineStageId: stage.workOrderLineStageId }
          : {}),
        stageTitle: stage.stageTitle,
        splitPercentage: stage.splitPercentage,
      })),
      deletedStageIds:
        scheduleDeletedStageIds.length > 0
          ? scheduleDeletedStageIds
          : undefined,
    };

    setValue("workOrderLines", currentLines);
    message.success("Schedule breakdown saved successfully");
    setScheduleDrawerVisible(false);
    setScheduleEditingLineIndex(-1);
    setScheduleLineStages([]);
    setScheduleDeletedStageIds([]);
  };

  // Handler to close schedule drawer without saving
  const handleCloseScheduleDrawer = () => {
    setScheduleDrawerVisible(false);
    setScheduleEditingLineIndex(-1);
    setScheduleLineStages([]);
    setScheduleDeletedStageIds([]);
  };

  const validateForm = (data: any) => {
    console.log("Validating form:", data);

    if (!data.workOrderLines || data.workOrderLines.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "At least one work order line is required",
      });
      return false;
    }

    // Check if any line has errors from bulk upload
    const errorLines = data.workOrderLines.filter(
      (line: any) => line.errorFlag === true
    );
    if (errorLines.length > 0) {
      Swal.fire({
        icon: "error",
        title: "Error Lines Found",
        html: `There are ${errorLines.length} work order line(s) with errors from bulk upload.<br/><br/>Please review the error messages in the table and fix or remove the error lines before proceeding.`,
        confirmButtonText: "OK",
      });
      return false;
    }

    // Check if any line has invalid data
    const linesWithMissingSiteHierarchy: string[] = [];

    for (const line of data.workOrderLines) {
      // For Open workType, quantity can be 0; for QTY workType, quantity must be > 0
      const isQuantityValid =
        line.workType === WorkType.Open || line.quantity > 0;

      if (
        !line.taskTitle ||
        !line.workCategory ||
        !isQuantityValid ||
        line.rate <= 0
      ) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "All work order lines must have valid task title, category, quantity (for QTY type), and rate",
        });
        return false;
      }

      // Validate site hierarchy fields
      if (!hasValidSiteHierarchy(line)) {
        linesWithMissingSiteHierarchy.push(
          `${line.taskTitle} - ${getSiteHierarchyValidationMessage(line)}`
        );
      }
    }

    // Show error for lines with missing site hierarchy
    if (linesWithMissingSiteHierarchy.length > 0) {
      Swal.fire({
        icon: "error",
        title: "Missing fields Validation Error",
        html: `
        <div style="text-align: left;">
          <p style="margin-top: 15px; font-weight: bold;">Please configure the required fields in lines before proceeding to the next step.</p>
        </div>
      `,
        confirmButtonText: "OK",
      });
      return false;
    }

    const totalWeightage = data.workOrderLines.reduce(
      (sum: number, l: WorkOrderLine) => sum + (l.weightagePercentage || 0),
      0
    );

    // Block navigation if total weightage exceeds 100%
    if (totalWeightage > 100) {
      Swal.fire({
        icon: "error",
        title: "Weightage Error",
        text: `Total weightage percentage is ${Math.round(
          totalWeightage
        )}%. Total weightage cannot exceed 100%. Please adjust the weightage of individual work order lines before proceeding.`,
        confirmButtonText: "OK",
      });
      return false;
    }

    return true;
  };

  const submitForm = async (data: WorkOrderFormData) => {
    console.log("Submitting work order details:", data);

    if (!validateForm(data)) {
      return;
    }

    const payload = {
      ...data,
      step: "workOrderDetails",
    };

    console.log("Work Order Details budgetHeaderId state:", budgetHeaderId);
    console.log(
      "Work Order Details budgetHeaderId in form:",
      data.budgetHeaderId
    );
    console.log("Work Order Details Payload:", payload);

    if (props.next) {
      props.next(payload);
    }
  };

  const handlePrevious = () => {
    console.log("Going to previous step");
    if (props.prev) {
      props.prev();
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    const totalValue = workOrderLinesWatch.reduce(
      (sum, line) => sum + line.quantity * line.rate,
      0
    );
    const totalTax = workOrderLinesWatch.reduce(
      (sum, line) => sum + (line.taxAmount || 0),
      0
    );
    const totalWeightage = workOrderLinesWatch.reduce(
      (sum, line) => sum + (line.weightagePercentage || 0),
      0
    );

    return { totalValue, totalTax, totalWeightage };
  };

  const { totalValue, totalTax, totalWeightage } = calculateTotals();

  // Task Selection Table Columns
  const taskColumns = [
    // {
    //   title: "Task ID",
    //   dataIndex: "taskId",
    //   key: "taskId",
    //   width: 100,
    // },
    {
      title: "Task Name",
      dataIndex: "taskName",
      key: "taskName",
      width: 250,
      render: (taskName: string, record: any) => {
        // Check if this task is already added to work order lines
        const isAlreadyAdded = workOrderLinesWatch.some(
          (line: any) =>
            line.source === WorkOrderLineSource.ProjectManagement &&
            line.sourceId === record.taskId
        );
        return (
          <div>
            <span>{taskName}</span>
            {isAlreadyAdded && (
              <Tag color="green" style={{ marginLeft: 8, fontSize: "10px" }}>
                Already Added
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Work Category",
      dataIndex: "workCategory",
      key: "workCategory",
      width: 200,
    },
    {
      title: "Task Qty",
      dataIndex: "workQuantity",
      key: "workQuantity",
      width: 120,
      render: (qty: number, record: any) => `${qty || 0}`,
    },
    {
      title: "Allocated Qty",
      dataIndex: "workOrderQty",
      key: "workOrderQty",
      width: 120,
      render: (qty: number, record: any) => `${qty || 0}`,
    },
    {
      title: "Pending Qty",
      dataIndex: "availableForWorkOrderQty",
      key: "availableForWorkOrderQty",
      width: 120,
      render: (qty: number, record: any) => `${qty || 0}`,
    },
    ...(workTypeWatch !== WorkType.Open
      ? [

        {

          title: "Qty",
          key: "requestedQty",
          width: 180,
          render: (_: any, record: any) => {
            const currentValue = taskRequestedQuantities[record.taskId] || 0;
            const pendingQty = record.availableForWorkOrderQty || 0;
            const isInvalid = currentValue > pendingQty;
            const isDisabled = workTypeWatch === WorkType.Open;

            return (
              <InputNumber
                min={0}
                step={0.01}
                value={currentValue}
                onChange={(value) =>
                  handleTaskRequestedQuantityChange(record.taskId, value || 0)
                }
                style={{
                  width: "100%",
                  borderColor: isInvalid ? "red" : undefined,
                }}
                size="small"
                placeholder="Enter qty"
                status={isInvalid ? "error" : undefined}
                disabled={isDisabled}
              />
            );
          },
        },
      ]
      : []),
    {
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
      width: 150,
    },
    {
      title: "Target Rate",
      dataIndex: "taskRate",
      key: "taskRate",
      width: 150,
      render: (rate: number) => `₹ ${rate}`,
    },
    {
      title: "Execution Rate",
      dataIndex: "rate",
      key: "rate",
      width: 250,
      render: (rate: number, record: any) => {
        const taskRate = Number(record.taskRate) || 0;
        const executionRate = Number(taskRates[record.taskId]) || 0;

        // Calculate difference
        const difference = executionRate - taskRate;
        const percentageChange =
          taskRate > 0 ? (difference / taskRate) * 100 : 0;

        // Show indicator only if both rates are set and different
        const showIndicator =
          taskRate > 0 && executionRate > 0 && Math.abs(difference) > 0.01;

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <InputNumber
              min={0}
              step={0.01}
              value={taskRates[record.taskId] || 0}
              onChange={(value) =>
                handleTaskRateChange(record.taskId, value || 0)
              }
              style={{ width: "100%" }}
              size="small"
              placeholder="Enter rate"
              formatter={indianCurrencyFormatter}
              parser={indianCurrencyParser}
            />
            {showIndicator && (
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: "500",
                  color: difference > 0 ? "#cf1322" : "#389e0d",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <strong> {difference > 0 ? "↑" : "↓"}</strong>
                <span>
                  {difference > 0 ? "Increased" : "Decreased"} by ₹
                  {Math.abs(difference).toLocaleString("en-IN")}
                  {/* ({Math.abs(percentageChange).toFixed(2)}%) */}
                </span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Rate Change Remarks",
      key: "rateRemarks",
      width: 250,
      render: (_: any, record: any) => {
        const taskRate = Number(record.taskRate) || 0;
        const enteredRate = Number(taskRates[record.taskId]) || 0;
        // Only ask for remarks if task has a rate AND entered rate is different
        // Use Math.abs for floating point comparison
        const isRateChanged =
          taskRate > 0 &&
          enteredRate > 0 &&
          Math.abs(enteredRate - taskRate) > 0.01;

        return (
          <div>
            {isRateChanged && (
              <TextArea
                rows={2}
                value={taskRateRemarks[record.taskId] || ""}
                onChange={(e) =>
                  handleTaskRateRemarksChange(record.taskId, e.target.value)
                }
                placeholder="Required: Why was rate changed?"
                style={{
                  borderColor: !taskRateRemarks[record.taskId]
                    ? "#ff4d4f"
                    : undefined,
                }}
                size="small"
              />
            )}
            {isRateChanged && !taskRateRemarks[record.taskId] && (
              <small style={{ color: "#ff4d4f", fontSize: "11px" }}>
                * Remarks required for rate change
              </small>
            )}
          </div>
        );
      },
    },
    {
      title: "Weightage %",
      dataIndex: "weightagePercentage",
      key: "weightagePercentage",
      width: 120,
      render: (weightage: number, record: ProjectTask) => (
        <InputNumber
          min={0}
          max={100}
          step={0.1}
          value={taskWeightages[record.taskId] || 0}
          onChange={(value) =>
            handleTaskWeightageChange(record.taskId, value || 0)
          }
          style={{ width: "100%" }}
          size="small"
          placeholder="Enter %"
          formatter={(value) => `${value}%`}
          parser={(value) => +value!.replace(/%\s?|(,*)/g, "")}
        />
      ),
    },
    // Tax Type column - only show if tax is enabled and level is Line or Both
    ...(enableTaxWatch === "true" &&
      (taxApplicationLevelWatch === TaxApplicationLevel.Line ||
        taxApplicationLevelWatch === TaxApplicationLevel.Both)
      ? [
        {
          title: "Tax Slab",
          key: "taxType",
          width: 250,
          render: (_: any, record: any) => (
            <Select
              value={taskTaxTypes[record.taskId] || undefined}
              placeholder="Select Tax Slab"
              onChange={(value) =>
                handleTaskTaxTypeChange(record.taskId, value)
              }
              style={{ width: "100%", minWidth: "200px" }}
              size="small"
              showSearch
              optionFilterProp="children"
            >
              {taxOptions.map((tax) => (
                <Select.Option key={tax.value} value={tax.value}>
                  {tax.label}
                </Select.Option>
              ))}
            </Select>
          ),
        },
      ]
      : []),
    // Budget allocation columns for Project Management source
    // {
    //   title: "Budget Line",
    //   key: "budgetLine",
    //   width: 150,
    //   render: (_: any, record: any) => (
    //     <div>
    //       <div style={{ fontWeight: "bold", fontSize: "12px" }}>
    //         {record.budgetLineName || "Not Set"}
    //       </div>
    //       {/* <small className="text-muted">
    //         ID: {record.budgetLineId || "N/A"}
    //       </small> */}
    //     </div>
    //   ),
    // },
    {
      title: "Available Budget",
      key: "availableBudget",
      width: 220,
      render: (_: any, record: any) => {
        const availableBudget =
          (record.budgetValue || 0) - (record.WorkOrderAllocatedBudget || 0);
        return (
          <div style={{ textAlign: "right" }}>
            <div
              style={{
                fontWeight: "bold",
                color: availableBudget > 0 ? "#52c41a" : "#ff4d4f",
              }}
            >
              ₹{availableBudget.toLocaleString("en-IN")}
            </div>
            <small className="text-muted">
              Total: ₹{(record.budgetValue || 0).toLocaleString("en-IN")}
            </small>
          </div>
        );
      },
    },
    {
      title: "Allocate Budget",
      key: "allocateBudget",
      width: 230,
      render: (_: any, record: any) => {
        const availableBudget =
          (record.budgetValue || 0) - (record.WorkOrderAllocatedBudget || 0);
        const allocatedBudget = taskBudgetAllocations[record.taskId] || 0;
        const isExceeded = allocatedBudget > availableBudget;

        return (
          <div>
            <InputNumber
              min={0}
              step={1}
              value={allocatedBudget}
              onChange={(value) =>
                handleTaskBudgetAllocationChange(record.taskId, value || 0)
              }
              style={{
                width: "100%",
                borderColor: isExceeded ? "#ff4d4f" : undefined,
              }}
              size="small"
              placeholder="Enter amount"
              status={isExceeded ? "error" : undefined}
              formatter={indianCurrencyFormatter}
              parser={indianCurrencyParser}
            />
            {isExceeded && (
              <small style={{ color: "#ff4d4f", fontSize: "11px" }}>
                Exceeds available budget by ₹
                {(allocatedBudget - availableBudget).toLocaleString("en-IN")}
              </small>
            )}
          </div>
        );
      },
    },
    {
      title: "Attachments",
      key: "attachments",
      width: 200,
      render: (_: any, record: any) => {
        const taskFileList = taskFiles[record.taskId] || [];
        const fileCount = taskFileList.length;

        // If no files, show upload directly
        if (fileCount === 0) {
          return (
            <Upload
              name="file"
              listType="text"
              fileList={taskFileList}
              beforeUpload={(file) => {
                const isValid = validateFileBeforeUpload(file);
                return isValid;
              }}
              onChange={(info) => {
                handleTaskFileChange(record.taskId, info);
              }}
              onRemove={(file) => {
                handleTaskFileRemove(record.taskId, file);
              }}
              multiple
              customRequest={({ file, onSuccess }) => {
                if (onSuccess) {
                  setTimeout(() => onSuccess("ok"), 0);
                }
              }}
            >
              <AntdButton
                icon={<UploadOutlined />}
                size="small"
                type="link"
                style={{ padding: 0 }}
              >
                Upload
              </AntdButton>
            </Upload>
          );
        }

        // If files exist, show compact view with popover
        const uploadContent = (
          <div style={{ minWidth: "300px", maxWidth: "400px" }}>
            <Upload
              name="file"
              listType="text"
              fileList={taskFileList}
              beforeUpload={(file) => {
                const isValid = validateFileBeforeUpload(file);
                return isValid;
              }}
              onChange={(info) => {
                handleTaskFileChange(record.taskId, info);
              }}
              onRemove={(file) => {
                handleTaskFileRemove(record.taskId, file);
              }}
              multiple
              customRequest={({ file, onSuccess }) => {
                if (onSuccess) {
                  setTimeout(() => onSuccess("ok"), 0);
                }
              }}
            >
              <AntdButton
                icon={<UploadOutlined />}
                size="small"
                type="primary"
                style={{ marginBottom: 8, width: "100%" }}
              >
                Upload More Files
              </AntdButton>
            </Upload>
            <div style={{ marginTop: 8, fontSize: "12px", color: "#666" }}>
              {fileCount} file{fileCount > 1 ? "s" : ""} attached
            </div>
          </div>
        );

        return (
          <Popover
            content={uploadContent}
            title="Task Attachments"
            trigger="click"
            placement="left"
            overlayStyle={{ zIndex: 1050 }}
          >
            <Badge count={fileCount} size="small">
              <AntdButton
                icon={<UploadOutlined />}
                size="small"
                type="link"
                style={{ padding: 0 }}
              >
                {fileCount} file{fileCount > 1 ? "s" : ""}
              </AntdButton>
            </Badge>
          </Popover>
        );
      },
    },
  ];

  // BOQ Line Selection Table Columns
  const boqLineColumns = [
    // {
    //   title: "Line ID",
    //   dataIndex: "lineId",
    //   key: "lineId",
    //   width: 100,
    // },
    {
      title: "Task Name",
      dataIndex: "taskName",
      key: "taskName",
      width: 200,
    },
    {
      title: "Work Category",
      dataIndex: "workCategory",
      key: "workCategory",
      width: 200,
    },
    {
      title: "BOQ Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 120,
      render: (qty: number, record: any) => `${qty || 0}`,
    },
    {
      title: "Allocated Qty",
      dataIndex: "allocatedQuantity",
      key: "allocatedQuantity",
      width: 120,
      render: (qty: number, record: any) => `${qty || 0}`,
    },
    {
      title: "Pending Qty",
      dataIndex: "quantityAvailableForWorkOrder",
      key: "quantityAvailableForWorkOrder",
      width: 120,
      render: (qty: number, record: any) => (
        // <Tag color={qty > 0 ? "green" : "red"}>
        <>{qty || 0}</>
        // </Tag>
      ),
    },
    {
      title: "Qty",
      key: "requestedQty",
      width: 120,
      render: (_: any, record: any) => {
        const currentValue = boqLineRequestedQuantities[record.lineId] || 0;
        const pendingQty = record.quantityAvailableForWorkOrder || 0;
        const isInvalid = currentValue > pendingQty;

        return (
          <InputNumber
            min={0}
            step={0.01}
            value={currentValue}
            onChange={(value) =>
              handleBoqLineRequestedQuantityChange(record.lineId, value || 0)
            }
            style={{
              width: "100%",
              borderColor: isInvalid ? "red" : undefined,
            }}
            size="small"
            placeholder="Enter qty"
            status={isInvalid ? "error" : undefined}
          />
        );
      },
    },
    {
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
      width: 100,
    },
    {
      title: "Target Rate",
      dataIndex: "rate",
      key: "rate",
      width: 150,
      render: (rate: any) => `₹ ${rate}`,
    },
    {
      title: "Execution Rate",
      key: "executionRate",
      width: 250,
      render: (_: any, record: WorkBoqLine) => {
        const boqRate = Number(record.rate) || 0;
        const executionRate = Number(boqLineRates[record.lineId]) || 0;

        // Calculate difference
        const difference = executionRate - boqRate;
        const percentageChange = boqRate > 0 ? (difference / boqRate) * 100 : 0;

        // Show indicator only if both rates are set and different
        const showIndicator =
          boqRate > 0 && executionRate > 0 && Math.abs(difference) > 0.01;

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <InputNumber
              min={0}
              step={0.01}
              value={boqLineRates[record.lineId] || 0}
              onChange={(value) =>
                handleBoqLineRateChange(record.lineId, value || 0)
              }
              style={{ width: "100%" }}
              size="small"
              placeholder="Enter rate"
              formatter={indianCurrencyFormatter}
              parser={indianCurrencyParser}
            />
            {showIndicator && (
              <small
                style={{
                  color: difference > 0 ? "#52c41a" : "#ff4d4f",
                  fontSize: "11px",
                }}
              >
                {difference > 0 ? "+" : ""}
                {difference.toFixed(2)} ({percentageChange > 0 ? "+" : ""}
                {percentageChange.toFixed(1)}%)
              </small>
            )}
          </div>
        );
      },
    },
    {
      title: "Weightage %",
      dataIndex: "weightagePercentage",
      key: "weightagePercentage",
      width: 120,
      render: (weightage: number, record: any) => (
        <InputNumber
          min={0}
          max={100}
          step={0.1}
          value={boqLineWeightages[record.lineId] || 0}
          onChange={(value) =>
            handleBoqLineWeightageChange(record.lineId, value || 0)
          }
          style={{ width: "100%" }}
          size="small"
          placeholder="Enter %"
          formatter={(value) => `${value}%`}
          parser={(value) => +value!.replace(/%\s?|(,*)/g, "")}
        />
      ),
    },
    // Tax Type column - only show if tax is enabled and level is Line or Both
    ...(enableTaxWatch === "true" &&
      (taxApplicationLevelWatch === TaxApplicationLevel.Line ||
        taxApplicationLevelWatch === TaxApplicationLevel.Both)
      ? [
        {
          title: "Tax Slab",
          key: "taxType",
          width: 250,
          render: (_: any, record: any) => (
            <Select
              value={boqLineTaxTypes[record.lineId] || undefined}
              placeholder="Select Tax Slab"
              onChange={(value) =>
                handleBoqLineTaxTypeChange(record.lineId, value)
              }
              style={{ width: "100%", minWidth: "200px" }}
              size="small"
              showSearch
              optionFilterProp="children"
            >
              {taxOptions.map((tax) => (
                <Select.Option key={tax.value} value={tax.value}>
                  {tax.label}
                </Select.Option>
              ))}
            </Select>
          ),
        },
      ]
      : []),
    // Rate Change Remarks column
    {
      title: "Rate Change Remarks",
      key: "rateRemarks",
      width: 250,
      render: (_: any, record: WorkBoqLine) => {
        const boqRate = Number(record.rate) || 0;
        const enteredRate = Number(boqLineRates[record.lineId]) || 0;
        // Only ask for remarks if BOQ has a rate AND entered rate is different
        const isRateChanged =
          boqRate > 0 &&
          enteredRate > 0 &&
          Math.abs(enteredRate - boqRate) > 0.01;

        return (
          <div>
            {isRateChanged && (
              <TextArea
                rows={2}
                value={boqLineRateRemarks[record.lineId] || ""}
                onChange={(e) =>
                  handleBoqLineRateRemarksChange(record.lineId, e.target.value)
                }
                placeholder="Required: Why was rate changed?"
                style={{
                  borderColor: !boqLineRateRemarks[record.lineId]
                    ? "#ff4d4f"
                    : undefined,
                }}
                size="small"
              />
            )}
            {isRateChanged && !boqLineRateRemarks[record.lineId] && (
              <small style={{ color: "#ff4d4f", fontSize: "11px" }}>
                * Remarks required for rate change
              </small>
            )}
          </div>
        );
      },
    },
    // Budget allocation columns
    // {
    //   title: "Budget Line",
    //   key: "budgetLine",
    //   width: 150,
    //   render: (_: any, record: any) => (
    //     <div>
    //       <div style={{ fontWeight: "bold", fontSize: "12px" }}>
    //         {record.budgetLineName || "Not Set"}
    //       </div>
    //       {/* <small className="text-muted">
    //         ID: {record.budgetLineId || "N/A"}
    //       </small> */}
    //     </div>
    //   ),
    // },
    {
      title: "Available Budget",
      key: "availableBudget",
      width: 120,
      render: (_: any, record: any) => {
        const availableBudget =
          (record.budgetAllocation || 0) -
          (record.workOrderAllocatedBudget || 0);
        return (
          <div style={{ textAlign: "right" }}>
            <div
              style={{
                fontWeight: "bold",
                color: availableBudget > 0 ? "#52c41a" : "#ff4d4f",
              }}
            >
              ₹{availableBudget.toLocaleString("en-IN")}
            </div>
            <small className="text-muted">
              Total: ₹{(record.budgetAllocation || 0).toLocaleString("en-IN")}
            </small>
          </div>
        );
      },
    },
    {
      title: "Allocate Budget",
      key: "allocateBudget",
      width: 230,
      render: (_: any, record: any) => {
        const availableBudget =
          (record.budgetAllocation || 0) -
          (record.workOrderAllocatedBudget || 0);
        const allocatedBudget = boqLineBudgetAllocations[record.lineId] || 0;
        const isExceeded = allocatedBudget > availableBudget;

        return (
          <div>
            <InputNumber
              min={0}
              step={1}
              value={allocatedBudget}
              onChange={(value) =>
                handleBoqLineBudgetAllocationChange(record.lineId, value || 0)
              }
              style={{
                width: "100%",
                borderColor: isExceeded ? "#ff4d4f" : undefined,
              }}
              size="small"
              placeholder="Enter amount"
              status={isExceeded ? "error" : undefined}
              formatter={indianCurrencyFormatter}
              parser={indianCurrencyParser}
            />
            {isExceeded && (
              <small style={{ color: "#ff4d4f", fontSize: "11px" }}>
                Exceeds available budget by ₹
                {(allocatedBudget - availableBudget).toLocaleString("en-IN")}
              </small>
            )}
          </div>
        );
      },
    },
    {
      title: "Attachments",
      key: "attachments",
      width: 120,
      render: (_: any, record: any) => {
        const boqLineFileList = boqLineFiles[record.lineId] || [];
        const fileCount = boqLineFileList.length;

        // If no files, show upload directly
        if (fileCount === 0) {
          return (
            <Upload
              name="file"
              listType="text"
              fileList={boqLineFileList}
              beforeUpload={(file) => {
                const isValid = validateFileBeforeUpload(file);
                return isValid;
              }}
              onChange={(info) => {
                handleBoqLineFileChange(record.lineId, info);
              }}
              onRemove={(file) => {
                handleBoqLineFileRemove(record.lineId, file);
              }}
              multiple
              customRequest={({ file, onSuccess }) => {
                if (onSuccess) {
                  setTimeout(() => onSuccess("ok"), 0);
                }
              }}
            >
              <AntdButton
                icon={<UploadOutlined />}
                size="small"
                type="link"
                style={{ padding: 0 }}
              >
                Upload
              </AntdButton>
            </Upload>
          );
        }

        // If files exist, show compact view with popover
        const uploadContent = (
          <div style={{ minWidth: "300px", maxWidth: "400px" }}>
            <Upload
              name="file"
              listType="text"
              fileList={boqLineFileList}
              beforeUpload={(file) => {
                const isValid = validateFileBeforeUpload(file);
                return isValid;
              }}
              onChange={(info) => {
                handleBoqLineFileChange(record.lineId, info);
              }}
              onRemove={(file) => {
                handleBoqLineFileRemove(record.lineId, file);
              }}
              multiple
              customRequest={({ file, onSuccess }) => {
                if (onSuccess) {
                  setTimeout(() => onSuccess("ok"), 0);
                }
              }}
            >
              <AntdButton
                icon={<UploadOutlined />}
                size="small"
                type="primary"
                style={{ marginBottom: 8, width: "100%" }}
              >
                Upload More Files
              </AntdButton>
            </Upload>
            <div style={{ marginTop: 8, fontSize: "12px", color: "#666" }}>
              {fileCount} file{fileCount > 1 ? "s" : ""} attached
            </div>
          </div>
        );

        return (
          <Popover
            content={uploadContent}
            title="BOQ Line Attachments"
            trigger="click"
            placement="left"
            overlayStyle={{ zIndex: 1050 }}
          >
            <Badge count={fileCount} size="small">
              <AntdButton
                icon={<UploadOutlined />}
                size="small"
                type="link"
                style={{ padding: 0 }}
              >
                {fileCount} file{fileCount > 1 ? "s" : ""}
              </AntdButton>
            </Badge>
          </Popover>
        );
      },
    },
  ];

  // Dynamic Work Order Lines Table Columns
  const getWorkOrderLinesColumns = () => {
    // Check if any line has a source other than WorkBOQ (to show Weightage column)
    const hasNonBoqLines = workOrderLinesWatch.some(
      (line) => line.source !== WorkOrderLineSource.WorkBOQ
    );

    const baseColumns = [
      // {
      //   title: "Line #",
      //   dataIndex: "lineNumber",
      //   key: "lineNumber",
      //   width: 100,
      // },
      {
        title: "Task Title",
        dataIndex: "taskTitle",
        key: "taskTitle",
        width: 250,
        render: (text: string, record: WorkOrderLine) => (
          <div>
            <div className="font-weight-bold">{text}</div>
            {record.taskDescription && record.taskDescription !== text && (
              <Tooltip title={record.taskDescription}>
                <small
                  className="text-muted"
                  style={{
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "200px",
                  }}
                >
                  {record.taskDescription}
                </small>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: "Category",
        key: "category",
        width: 150,
        render: (_: any, record: any) => (
          <div>
            <div>{record.workCategoryName || record.workCategory}</div>
            {record.subCategory && (
              <small className="text-muted">{record.subCategory}</small>
            )}
          </div>
        ),
      },
      // Show Work Type column only for New Work Line source
      ...(workOrderLinesWatch.some(
        (line) => line.source === WorkOrderLineSource.NewWorkLine
      )
        ? [
          {
            title: "Work Type",
            dataIndex: "workType",
            key: "workType",
            width: 120,
            render: (workType: string) => (
              <Tag color={workType === WorkType.Open ? "blue" : "green"}>
                {workType === WorkType.Open ? "Open" : "Quantity"}
              </Tag>
            ),
          },
        ]
        : []),
      {
        title: "Quantity",
        dataIndex: "quantity",
        key: "quantity",
        width: 150,
        render: (qty: number, record: any, index: number) => {
          const isOpenType = record.workType === WorkType.Open;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {isOpenType ? (
                <span className="text-muted">N/A</span>
              ) : (
                <>
                  <InputNumber
                    min={0}
                    step={0.01}
                    value={qty}
                    onChange={(value) =>
                      handleQuantityChange(index, value || 0)
                    }
                    style={{ width: "100px", border: "none" }}
                    size="small"
                    placeholder="Qty"
                    bordered={false}
                  />
                  <small
                    className="text-muted"
                    style={{ fontSize: "11px", whiteSpace: "nowrap" }}
                  >
                    {record.uomName || record.uom}
                  </small>
                </>
              )}
            </div>
          );
        },
      },
      // Show Task Rate for Project Management source or BOQ Rate for Work BOQ source
      ...(workOrderLinesWatch.some(
        (line) =>
          line.source === WorkOrderLineSource.ProjectManagement ||
          line.source === WorkOrderLineSource.WorkBOQ
      )
        ? [
          {
            title: "Target Rate",
            key: "targetRate",
            width: 220,
            render: (_: any, record: any) => {
              if (record.source === WorkOrderLineSource.ProjectManagement) {
                const taskRate = Number(record.taskRate) || 0;
                return (
                  <div style={{ textAlign: "right" }}>
                    <span>₹{taskRate.toLocaleString("en-IN")}</span>
                  </div>
                );
              } else if (record.source === WorkOrderLineSource.WorkBOQ) {
                const boqRate = Number(record.boqRate) || 0;
                return (
                  <div style={{ textAlign: "right" }}>
                    <span>₹{boqRate.toLocaleString("en-IN")}</span>
                  </div>
                );
              }
              return <span className="text-muted">-</span>;
            },
          },
        ]
        : []),
      {
        title: workOrderLinesWatch.some(
          (line) =>
            line.source === WorkOrderLineSource.ProjectManagement ||
            line.source === WorkOrderLineSource.WorkBOQ
        )
          ? "Execution Rate"
          : "Rate",
        dataIndex: "rate",
        key: "rate",
        width: 150,
        render: (rate: number, record: WorkOrderLine, index: number) => {
          const hasRemarks =
            (record.source === WorkOrderLineSource.ProjectManagement ||
              record.source === WorkOrderLineSource.WorkBOQ) &&
            (record.rateChangeRemarks || record.remarks);

          return (
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <InputNumber
                min={0}
                step={0.01}
                value={rate}
                onChange={(value) => handleRateChange(index, value || 0)}
                style={{ width: "100%", border: "none" }}
                size="small"
                placeholder="Enter rate"
                bordered={false}
                formatter={indianCurrencyFormatter}
                parser={indianCurrencyParser}
              />
              {hasRemarks && (
                <Tooltip
                  title={record.rateChangeRemarks || record.remarks}
                  placement="top"
                >
                  <InfoCircleOutlined
                    style={{
                      color: "#1890ff",
                      cursor: "pointer",
                      fontSize: "16px",
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: "Tax",
        key: "tax",
        width: 280,
        render: (_: any, record: any, index: number) => {
          // Only show tax editing if tax is enabled and level is Line or Both
          const showTaxEdit =
            enableTaxWatch === "true" &&
            (taxApplicationLevelWatch === TaxApplicationLevel.Line ||
              taxApplicationLevelWatch === TaxApplicationLevel.Both);

          if (!showTaxEdit) {
            const hasTax = record.taxType && record.taxPercentage > 0;
            if (hasTax) {
              const gstRates = calculateGSTRates(record.taxPercentage || 0);
              return (
                <div>
                  <div>{record.taxTypeName || record.taxType}</div>
                  {gstRates.igstRate > 0 ? (
                    // Different states - show IGST
                    <div>
                      <small>IGST: {gstRates.igstRate}%</small>
                      {record.taxAmount > 0 && (
                        <div>
                          <small className="text-success">
                            ₹{Math.round(record.taxAmount).toLocaleString("en-IN")}
                          </small>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Same state - show CGST + SGST
                    <div>
                      <small>CGST: {gstRates.cgstRate}%</small>
                      <small> | SGST: {gstRates.sgstRate}%</small>
                      {record.taxAmount > 0 && (
                        <div>
                          <small className="text-success">
                            ₹{Math.round(record.taxAmount / 2).toLocaleString("en-IN")}{" "}
                            + ₹
                            {Math.round(record.taxAmount / 2).toLocaleString("en-IN")}
                          </small>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            } else {
              return <span className="text-muted">-</span>;
            }
          }

          return (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              <Select
                value={record.taxType || undefined}
                placeholder="Select Tax"
                onChange={(value) => handleWorkOrderLineTaxChange(index, value)}
                style={{ width: "100%", minWidth: "180px" }}
                size="small"
                showSearch
                optionFilterProp="children"
                allowClear
              >
                {taxOptions.map((tax) => (
                  <Select.Option key={tax.value} value={tax.value}>
                    {tax.label}
                  </Select.Option>
                ))}
              </Select>
              {record.taxAmount > 0 && record.taxPercentage > 0 && (
                <div style={{ fontSize: "12px" }}>
                  {(() => {
                    const gstRates = calculateGSTRates(
                      record.taxPercentage || 0
                    );
                    return gstRates.igstRate > 0 ? (
                      // Different states - show IGST
                      <div>
                        <small className="text-info">
                          IGST: {gstRates.igstRate}%
                        </small>
                        <div>
                          <small className="text-success">
                            ₹{Math.round(record.taxAmount).toLocaleString("en-IN")}
                          </small>
                        </div>
                      </div>
                    ) : (
                      // Same state - show CGST + SGST
                      <div>
                        <small className="text-info">
                          CGST: {gstRates.cgstRate}% | SGST: {gstRates.sgstRate}
                          %
                        </small>
                        <div>
                          <small className="text-success">
                            ₹{Math.round(record.taxAmount / 2).toLocaleString("en-IN")}{" "}
                            + ₹
                            {Math.round(record.taxAmount / 2).toLocaleString("en-IN")}
                          </small>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: "Total Amount",
        dataIndex: "totalAmount",
        key: "totalAmount",
        width: 120,
        render: (amount: number) => `₹${Math.round(amount).toLocaleString("en-IN")}`,
      },
      {
        title: "Location",
        key: "location",
        width: 180,
        render: (text: any, record: any) => {
          // Helper function to check if a value is meaningful (not null, undefined, or empty string)
          const hasValue = (val: any): boolean => {
            if (val === null || val === undefined) return false;
            const str = String(val).trim();
            return str !== "" && str !== "null" && str !== "undefined";
          };

          // Show only locationName
          const locationName = hasValue(record.locationName)
            ? String(record.locationName).trim()
            : "-";

          const isValidSiteHierarchy = hasValidSiteHierarchy(record);

          return (
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span className="fw-bold">{locationName}</span>
              {!isValidSiteHierarchy && (
                <Tooltip title={getSiteHierarchyValidationMessage(record)}>
                  <InfoCircleOutlined style={{ color: "#ff4d4f", cursor: "pointer" }} />
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: "Source",
        dataIndex: "source",
        key: "source",
        width: 100,
        render: (source: WorkOrderLineSource) => (
          <Tag
            color={
              source === WorkOrderLineSource.ProjectManagement
                ? "green"
                : source === WorkOrderLineSource.WorkBOQ
                  ? "blue"
                  : "orange"
            }
          >
            {source === WorkOrderLineSource.ProjectManagement
              ? "PM"
              : source === WorkOrderLineSource.WorkBOQ
                ? "BOQ"
                : "New"}
          </Tag>
        ),
      },
      {
        title: "Status",
        key: "status",
        width: 100,
        render: (_: any, record: any) => {
          // Only show status for New Work Lines that might have errors
          if (
            record.source === WorkOrderLineSource.NewWorkLine &&
            record.errorFlag !== undefined
          ) {
            if (record.errorFlag) {
              // For error lines, show tooltip with error messages as list
              const errorMessages = Array.isArray(record.errorMessage)
                ? record.errorMessage
                : record.errorMessage
                  ? [record.errorMessage]
                  : ["Unknown error"];

              const tooltipContent = (
                <div style={{ margin: "5px 0" }}>
                  {errorMessages.map((msg: string, idx: number) => (
                    <div
                      key={idx}
                      style={{
                        marginBottom:
                          idx < errorMessages.length - 1 ? "4px" : 0,
                      }}
                    >
                      • {msg}
                    </div>
                  ))}
                </div>
              );

              return (
                <Tooltip title={tooltipContent}>
                  <Tag color="red">Error</Tag>
                </Tooltip>
              );
            } else {
              // For valid lines, no tooltip
              return <Tag color="green">Valid</Tag>;
            }
          }
          return <span className="text-muted">-</span>;
        },
      },
      {
        title: "Actions",
        key: "actions",
        width: 130,
        render: (_: any, record: WorkOrderLine, index: number) => {
          const hasStages = record.stages && record.stages.length > 0;

          // Show schedule button for:
          // 1. Project Management with workType = Quantity
          // 2. New Work Line with workType = Quantity
          // 3. Work BOQ (always quantity based)
          // 4. If line already has stages (from bulk upload or edit)
          const showScheduleButton =
            hasStages ||
            record.source === WorkOrderLineSource.WorkBOQ ||
            ((record.source === WorkOrderLineSource.ProjectManagement ||
              record.source === WorkOrderLineSource.NewWorkLine) &&
              record.workType === WorkType.QTY);

          return (
            <Space>
              {/* Show schedule breakdown button for eligible lines */}
              {showScheduleButton && (
                <Tooltip
                  title={
                    hasStages
                      ? "Edit Schedule Breakdown"
                      : "Add Schedule Breakdown"
                  }
                >
                  <AntdButton
                    type="link"
                    size="small"
                    icon={<ScheduleOutlined />}
                    onClick={() => handleOpenScheduleDrawer(index)}
                    style={{ color: hasStages ? "#52c41a" : undefined }}
                  />
                </Tooltip>
              )}
              {/* Show edit button for all line types to allow editing attachments */}
              <Tooltip title="Edit Line">
                <AntdButton
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEditLine(index)}
                />
              </Tooltip>
              <Tooltip title="Delete Line">
                <AntdButton
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteLine(index)}
                />
              </Tooltip>
            </Space>
          );
        },
      },
    ];

    // Add Weightage column only if there are non-BOQ lines
    if (hasNonBoqLines) {
      // Find the index to insert Weightage column (after Rate, before Tax)
      const rateColumnIndex = baseColumns.findIndex(
        (col) => col.key === "rate"
      );
      const weightageColumn = {
        title: "Weightage %",
        dataIndex: "weightagePercentage",
        key: "weightagePercentage",
        width: 120,
        render: (weightage: number, record: WorkOrderLine, index: number) => {
          // Show dash for BOQ sources (no weightage)
          if (record.source === WorkOrderLineSource.WorkBOQ) {
            return <span className="text-muted">-</span>;
          }
          return (
            <InputNumber
              min={0}
              max={100}
              step={0.1}
              value={weightage}
              onChange={(value) => handleWeightageChange(index, value || 0)}
              style={{ width: "100%", border: "none" }}
              size="small"
              placeholder="Enter %"
              bordered={false}
              formatter={(value) => `${value}%`}
              parser={(value) => +value!.replace(/%\s?|(,*)/g, "")}
            />
          );
        },
      };
      baseColumns.splice(rateColumnIndex + 1, 0, weightageColumn);
    }

    // Add budget allocation columns
    const budgetLineColumn = {
      title: "Budget Line",
      key: "budgetLine",
      width: 180,
      render: (_: any, record: any) => {
        if (!record.budgetLineName) {
          return <span className="text-muted">No Budget Line</span>;
        }

        // Calculate available budget based on source
        let availableBudget = 0;

        if (record.source === WorkOrderLineSource.NewWorkLine) {
          // For New Work Line source: show current available budget
          const budgetLine = budgetLines.find(
            (bl: any) => bl.lineId === record.budgetLineId
          );
          if (budgetLine) {
            availableBudget = calculateAvailableBudget(
              budgetLine.lineId,
              budgetLine.budgetValue,
              budgetLine.allocatedLineValue
            );
          }
        } else if (record.source === WorkOrderLineSource.WorkBOQ) {
          // For Work BOQ source: budgetAllocation - workOrderAllocatedBudget
          const boqLine = availableBoqLines.find(
            (line) => line.lineId === record.sourceId
          );
          const totalBudget = boqLine?.budgetAllocation || 0;
          const allocatedBudget = boqLine?.workOrderAllocatedBudget || 0;
          availableBudget = totalBudget - allocatedBudget;
        } else if (record.source === WorkOrderLineSource.ProjectManagement) {
          // For Project Management source: budgetValue - WorkOrderAllocatedBudget
          const task = availableTasks.find(
            (task) => task.taskId === record.sourceId
          );
          const totalBudget = task?.budgetValue || 0;
          const allocatedBudget = task?.WorkOrderAllocatedBudget || 0;
          availableBudget = totalBudget - allocatedBudget;
        }

        return (
          <div>
            <div style={{ fontWeight: "bold", fontSize: "12px" }}>
              {record.budgetLineName}
            </div>
            <small
              className={`${availableBudget > 0 ? "text-success" : "text-danger"
                }`}
            >
              Available: ₹{availableBudget.toLocaleString("en-IN")}
            </small>
          </div>
        );
      },
    };

    const budgetAllocationColumn = {
      title: "Allocated Budget",
      key: "budgetAllocation",
      width: 250,
      render: (_: any, record: any, index: number) => {
        // Calculate max budget available for this line
        let maxBudget = 0;
        if (record.source === WorkOrderLineSource.NewWorkLine) {
          // For New Work Line source: use calculateAvailableBudget
          const budgetLine = budgetLines.find(
            (bl: any) => bl.lineId === record.budgetLineId
          );
          if (budgetLine) {
            maxBudget = calculateAvailableBudget(
              budgetLine.lineId,
              budgetLine.budgetValue,
              budgetLine.allocatedLineValue
            );
            // Add back this record's current allocation to allow editing
            maxBudget += record.budgetAllocation || 0;
          }
        } else if (record.source === WorkOrderLineSource.WorkBOQ) {
          const boqLine = availableBoqLines.find(
            (line) => line.lineId === record.sourceId
          );
          const totalBudget = boqLine?.budgetAllocation || 0;
          const allocatedBudget = boqLine?.workOrderAllocatedBudget || 0;
          maxBudget = totalBudget - allocatedBudget;
        } else if (record.source === WorkOrderLineSource.ProjectManagement) {
          const task = availableTasks.find(
            (task) => task.taskId === record.sourceId
          );
          const totalBudget = task?.budgetValue || 0;
          const allocatedBudget = task?.WorkOrderAllocatedBudget || 0;
          maxBudget = totalBudget - allocatedBudget;
        }

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <InputNumber
              min={0}
              max={maxBudget}
              step={1}
              value={record.budgetAllocation || 0}
              onChange={(value) =>
                handleWorkOrderLineBudgetChange(index, value || 0)
              }
              style={{ width: "100%", border: "none" }}
              size="small"
              placeholder="Enter amount"
              bordered={false}
              formatter={indianCurrencyFormatter}
              parser={indianCurrencyParser}
            />
            {record.budgetLineName && (
              <>
                <small className="text-muted" style={{ fontSize: "10px" }}>
                  {record.budgetLineName}
                </small>
                <small className="text-muted" style={{ fontSize: "10px" }}>
                  Available: ₹{maxBudget.toLocaleString("en-IN")}
                </small>
              </>
            )}
          </div>
        );
      },
    };

    // Insert budget columns before the total amount column
    const totalAmountIndex = baseColumns.findIndex(
      (col) => col.key === "totalAmount"
    );
    if (totalAmountIndex !== -1) {
      baseColumns.splice(
        totalAmountIndex,
        0,
        // budgetLineColumn,
        budgetAllocationColumn
      );
    }

    return baseColumns;
  };

  const hasValidSiteHierarchy = (line: any): boolean => {
    const workLevel = line.workLevel || getValues("workLevel");

    if (!workLevel) return true;
    switch (workLevel) {
      case "Tower / Block":
        // Need towerVillaId
        return !!line.towerVilla;

      case "Floor":
        // Need towerVillaId and floorName
        return !!(line.towerVilla && line.floorName);

      case "Flat":
        // Need towerVillaId, floorName, and flatName
        // return !!(line.towerVilla && line.floorName && line.flatName);
        return !!(line.towerVilla && line.floorName);

      case "Villa":
        // Need flatName (which maps to towerVilla)
        return !!line.flatName || !!line.towerVilla;

      default:
        return true;
    }
  };

  // Helper function to get validation message based on work level
  const getSiteHierarchyValidationMessage = (line: any): string => {
    const workLevel = line.workLevel || getValues("workLevel");

    switch (workLevel) {
      case "Tower / Block":
        return "Tower/Block/Villa is required";
      case "Floor":
        return "Tower/Block/Villa and Floor are required";
      case "Flat":
        return "Tower/Block/Villa, Floor, and Flat are required";
      case "Villa":
        return "Villa selection is required";
      default:
        return "Site hierarchy fields are missing";
    }
  };

  return (
    <>
      <style>
        {`
          .table-row-error {
            background-color: #fff2f0 !important;
          }
          .table-row-error:hover > td {
            background-color: #ffe7e6 !important;
          }
        `}
      </style>
      <form className="content clearfix" onSubmit={handleSubmit(submitForm)}>
        <Card className="mb-4">
          <Row className="gy-4">
            {/* Single Row with Source Selection and Conditional Fields */}
            <Col lg="12">
              <Row className="gy-3">
                <Col lg="2">
                  <FormSelect
                    name="selectedSource"
                    label="Select Source "
                    options={sourceOptions}
                    required={true}
                    placeholder="Choose source"
                    tooltipText="Select the source from which to add work lines"
                  />
                </Col>

                {/* Project Management Fields */}
                {selectedSourceWatch ===
                  WorkOrderLineSource.ProjectManagement && (
                    <>
                      <Col lg="3">
                        <FormSelect
                          name="projectId"
                          label="Project Selection"
                          options={projectOptions}
                          placeholder="Select Project"
                          required={true}
                          disabled={!siteIdWatch || siteIdWatch === ""}
                          tooltipText="Select project from the site"
                          onChange={handleProjectChange}
                        />
                      </Col>
                      <Col lg="2">
                        <FormSelect
                          name="activityId"
                          label="Activity "
                          options={activityOptions}
                          placeholder="Select Activity"
                          disabled={!projectIdWatch}
                          // required={true}
                          tooltipText="Select activity from the chosen project"
                        />
                      </Col>
                      <Col lg="2">
                        <FormSelect
                          name="workType"
                          label="Work Type"
                          options={[
                            { label: "Quantity", value: WorkType.QTY },
                            { label: "Open", value: WorkType.Open },
                          ]}
                          required={true}
                          placeholder="Select Work Type"
                          disabled={!getValues("activityId")}
                          tooltipText="Select work type: QTY (quantity-based) or Open (no quantity)"
                        />
                      </Col>
                      <Col lg="2" className="d-flex align-items-end">
                        <div className="w-100">
                          <label className="form-label d-block">&nbsp;</label>
                          <AntdButton
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openTaskSelectionModal}
                            disabled={
                              !projectIdWatch ||
                              !getValues("activityId") ||
                              !workTypeWatch ||
                              !getValues("contractorId")
                            }
                            className="w-100"
                            size="middle"
                            style={{ height: "38px", fontSize: "14px" }}
                          >
                            Load Tasks ({availableTasks.length})
                          </AntdButton>
                        </div>
                      </Col>
                    </>
                  )}

                {/* Work BOQ Fields */}
                {selectedSourceWatch === WorkOrderLineSource.WorkBOQ && (
                  <>
                    <Col lg="3">
                      <FormSelect
                        name="workBoqId"
                        label="Work BOQ"
                        options={workBoqOptions}
                        placeholder="Select BOQ"
                        // required={true}
                        tooltipText="Select approved Work BOQ"
                      />
                    </Col>
                    <Col lg="3">
                      <FormSelect
                        name="workCategoryId"
                        label="Work Category"
                        options={workCategoryOptions}
                        placeholder="Select Category"
                        tooltipText="Filter BOQ lines by work category (optional)"
                      />
                    </Col>
                    <Col lg="3" className="d-flex align-items-end">
                      <div className="w-100">
                        <label className="form-label d-block">&nbsp;</label>
                        <AntdButton
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={openBoqSelectionModal}
                          disabled={!getValues("workBoqId")}
                          className="w-100"
                          size="middle"
                          style={{ height: "38px", fontSize: "14px" }}
                        >
                          Load BOQ ({availableBoqLines.length})
                        </AntdButton>
                      </div>
                    </Col>
                  </>
                )}

                {/* New Work Line Fields */}
                {selectedSourceWatch === WorkOrderLineSource.NewWorkLine && (
                  <>
                    <Col lg="3">
                      <div className="form-group">
                        <label className="form-label">Budget Selection</label>
                        <Select
                          placeholder="Select Budget"
                          value={selectedBudgetId || undefined}
                          onChange={(value) => {
                            setSelectedBudgetId(value);
                            // Save to form state immediately
                            setValue("budgetHeaderId", value || "");
                            // Also save budget name
                            const selectedBudget = budgetOptions.find(
                              (b) => b.value === value
                            );
                            setValue(
                              "budgetHeaderName",
                              selectedBudget?.label || ""
                            );
                            if (value) {
                              fetchBudgetLines(parseInt(value));
                            } else {
                              setBudgetLines([]);
                            }
                          }}
                          disabled={hasNewWorkLineEntries}
                          style={{ width: "100%", minHeight: "38px" }}
                          dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                          showSearch
                          allowClear
                          optionFilterProp="children"
                          filterOption={(input, option: any) =>
                            (option?.children as string)
                              ?.toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                        >
                          {budgetOptions.map((budget) => (
                            <Select.Option
                              key={budget.value}
                              value={budget.value}
                            >
                              {budget.label}
                            </Select.Option>
                          ))}
                        </Select>
                        {budgetOptions.length === 0 && (
                          <small className="form-text text-muted">
                            No budgets available for the selected site
                          </small>
                        )}
                        {/* {hasNewWorkLineEntries && (
                          <small className="form-text text-muted">
                            Cannot change budget after adding work lines
                          </small>
                        )} */}
                      </div>
                    </Col>
                    <Col lg="2" md="3">
                      <FormInput
                        name="workLevel"
                        label="Work Level Settings"
                        required={true}
                        disabled={true}
                        placeholder="Auto-filled based on site"
                        tooltipText="Work level is determined by the selected site configuration"
                      />
                    </Col>
                    <Col lg="2" md="3">
                      <FormSelect
                        name="workType"
                        label="Work Type"
                        options={[
                          { label: "Quantity", value: WorkType.QTY },
                          { label: "Open", value: WorkType.Open },
                        ]}
                        required={true}
                        placeholder="Select Work Type"
                        disabled={hasNewWorkLineEntries}
                        tooltipText="Select work type for all New Work Lines: QTY (quantity-based) or Open (no quantity)"
                      />
                    </Col>
                    <Col lg="1" md="3" className="d-flex align-items-end">
                      <div className="w-100">
                        <label className="form-label d-block">&nbsp;</label>
                        <AntdButton
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={openNewWorkLineModal}
                          className="w-100"
                          size="middle"
                          style={{ height: "38px", fontSize: "14px" }}
                          disabled={!workLevelWatch || !workTypeWatch}
                        >
                          Add
                        </AntdButton>
                      </div>
                    </Col>
                    <Col lg="2" md="3" className="d-flex align-items-end">
                      <div className="w-100">
                        <label className="form-label d-block">&nbsp;</label>
                        <WorkOrderLineBulkUpload
                          onUpload={handleBulkUpload}
                          workCategoryOptions={workCategoryOptions}
                          uomOptions={uomOptions}
                          taxOptions={taxOptions}
                          budgetHeaderId={selectedBudgetId}
                          siteId={siteIdWatch}
                          workType={workTypeWatch}
                          workLevelWatch={workLevelWatch}
                          enableTaxWatch={enableTaxWatch}
                          taxApplicationLevelWatch={taxApplicationLevelWatch}
                          existingLines={workOrderLinesWatch}
                          disabled={!workLevelWatch || !workTypeWatch}
                        />
                      </div>
                    </Col>
                  </>
                )}
              </Row>
            </Col>
          </Row>

          <Divider />

          {/* Work Order Lines Table */}
          <div className="mt-4">
            <h6 className="mb-3">Work Order Lines</h6>

            {/* Weightage Alert */}
            {totalWeightage > 100 && (
              <div className="alert alert-danger mb-3">
                <strong>❌ Weightage Error:</strong> Total weightage is{" "}
                {Math.round(totalWeightage)}% which exceeds the maximum allowed
                (100%). Please adjust the weightage of individual work order
                lines. You cannot proceed to the next step until this is
                resolved.
              </div>
            )}

            {totalWeightage === 100 && (
              <div className="alert alert-success mb-3">
                <strong>✅ Perfect Weightage:</strong> Total weightage is
                exactly 100%. Ready to proceed!
              </div>
            )}

            {totalWeightage > 80 && totalWeightage < 100 && (
              <div className="alert alert-warning mb-3">
                <strong>⚠️ Weightage Notice:</strong> Total weightage is{" "}
                {Math.round(totalWeightage)}%. Consider adding more work lines
                to reach 100% for complete project coverage.
              </div>
            )}
          </div>
          {/* <div className=""> */}
          <div
            style={{
              width: window.innerWidth - 170 + "px",
            }}
          >
            <Table
              dataSource={workOrderLinesWatch}
              columns={getWorkOrderLinesColumns()}
              rowKey="id"
              pagination={false}
              size="small"
              bordered
              scroll={{ x: 1800 }}
              locale={{
                emptyText:
                  "No work order lines added yet. Please select a source and add work lines.",
              }}
              rowClassName={(record: any) => {
                return record.errorFlag === true ? "table-row-error" : "";
              }}
            />
          </div>
          {/* </div> */}

          {/* Summary Section */}
          {workOrderLinesWatch.length > 0 && (
            <div
              className="mt-3 p-3 bg-light rounded"
              style={{
                maxWidth: "600px",
                marginLeft: "auto",
                marginRight: "20px",
              }}
            >
              <h6 className="mb-3">Summary</h6>
              <div className="d-flex justify-content-between mb-2">
                <span>Total Lines:</span>
                <strong>{workOrderLinesWatch.length}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Total Value:</span>
                <strong className="text-success">
                  ₹{Math.round(totalValue).toLocaleString("en-IN")}
                </strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Total Tax:</span>
                <strong className="text-info">
                  ₹{Math.round(totalTax).toLocaleString("en-IN")}
                </strong>
              </div>
              <hr className="my-2" />
              <div className="d-flex justify-content-between mb-2">
                <strong style={{ fontSize: "16px" }}>Gross Total:</strong>
                <strong style={{ fontSize: "16px", color: "#28a745" }}>
                  ₹{Math.round(totalValue + totalTax).toLocaleString("en-IN")}
                </strong>
              </div>
              <div className="d-flex justify-content-between">
                <span>Total Weightage:</span>
                <strong
                  className={
                    totalWeightage > 100
                      ? "text-danger"
                      : totalWeightage === 100
                        ? "text-success"
                        : totalWeightage > 80
                          ? "text-warning"
                          : ""
                  }
                >
                  {Math.round(totalWeightage)}%
                </strong>
              </div>
            </div>
          )}
        </Card>

        <div className="actions clearfix">
          <ul>
            <li>
              <Button
                color="primary"
                type="submit"
                disabled={totalWeightage > 100}
                title={
                  totalWeightage > 100
                    ? `Cannot proceed: Total weightage is ${Math.round(
                      totalWeightage
                    )}% (exceeds 100%)`
                    : ""
                }
              >
                Next
              </Button>
            </li>
            <li>
              <Button type="button" onClick={handlePrevious}>
                Previous
              </Button>
            </li>
          </ul>
        </div>
      </form>

      {/* Task Selection Modal */}
      <Modal
        title="Select Tasks from Project Management"
        visible={isTaskSelectionModalVisible}
        onCancel={() => {
          setIsTaskSelectionModalVisible(false);
          setSelectedTaskIds([]);
        }}
        width={1400}
        footer={[
          <AntdButton
            key="cancel"
            onClick={() => setIsTaskSelectionModalVisible(false)}
          >
            Cancel
          </AntdButton>,
          <AntdButton
            key="add"
            type="primary"
            icon={<PlusOutlined />}
            onClick={addSelectedTasks}
            disabled={selectedTaskIds.length === 0}
          >
            Add Selected Tasks ({selectedTaskIds.length})
          </AntdButton>,
        ]}
      >
        <div className="mb-3">
          <p>
            Available tasks for the selected activity. Select tasks to add to
            the work order.
          </p>
          <div className="alert alert-info">
            <small>
              <strong>Note:</strong> Please enter Requested Qty, Rate and
              Weightage % for each task before selecting them. You can input
              values directly in the Requested Qty, Rate and Weightage columns.
              <br />
              <strong>
                ⚠️ Tasks marked as "Already Added" are disabled
              </strong>{" "}
              and cannot be selected again to prevent duplicate entries.
            </small>
          </div>
        </div>
        <Table
          dataSource={availableTasks}
          columns={taskColumns}
          rowKey="taskId"
          rowSelection={{
            selectedRowKeys: selectedTaskIds,
            onChange: handleTaskSelectionChange,
            getCheckboxProps: (record: any) => {
              // Check if this task is already added to work order lines
              const isAlreadyAdded = workOrderLinesWatch.some(
                (line: any) =>
                  line.source === WorkOrderLineSource.ProjectManagement &&
                  line.sourceId === record.taskId
              );
              return {
                disabled: isAlreadyAdded,
              };
            },
          }}
          pagination={{ pageSize: 10 }}
          size="small"
          scroll={{ x: 2000 }}
        />
        <div className="mt-3">
          <small className="form-text text-muted">
            <strong>Note:</strong> You can upload attachments for each task
            individually using the "Upload" button in the Attachments column.
            Supported formats: JPG, PNG, PDF, DOC, DOCX, XLS, XLSX (Max size:
            10MB each)
          </small>
        </div>
      </Modal>

      {/* BOQ Line Selection Modal */}
      <Modal
        title="Select BOQ Lines"
        visible={isBoqSelectionModalVisible}
        onCancel={() => {
          setIsBoqSelectionModalVisible(false);
          setSelectedBoqLineIds([]);
        }}
        width={1400}
        footer={[
          <AntdButton
            key="cancel"
            onClick={() => {
              setIsBoqSelectionModalVisible(false);
              setSelectedBoqLineIds([]);
            }}
          >
            Cancel
          </AntdButton>,
          <AntdButton
            key="add"
            type="primary"
            icon={<PlusOutlined />}
            onClick={addSelectedBoqLines}
            disabled={selectedBoqLineIds.length === 0}
          >
            Add Selected Lines ({selectedBoqLineIds.length})
          </AntdButton>,
        ]}
      >
        <div className="mb-3">
          <p>
            Available BOQ lines with pending quantities. Only lines with pending
            quantity {">"} 0 can be selected.
          </p>
          <div className="alert alert-info">
            <small>
              <strong>Note:</strong> Please enter Requested Qty, Rate and
              Weightage % for each BOQ line before selecting them. You can input
              values directly in the Requested Qty, Rate and Weightage columns.
            </small>
          </div>
        </div>
        <Table
          dataSource={availableBoqLines}
          columns={boqLineColumns}
          rowKey="lineId"
          rowSelection={{
            selectedRowKeys: selectedBoqLineIds,
            onChange: handleBoqSelectionChange,
            getCheckboxProps: (record: any) => ({
              disabled: record.quantityAvailableForWorkOrder <= 0,
            }),
          }}
          pagination={{ pageSize: 10 }}
          size="small"
          scroll={{ x: 1800 }}
        />
        <div className="mt-3">
          <small className="form-text text-muted">
            <strong>Note:</strong> You can upload attachments for each BOQ line
            individually using the "Upload" button in the Attachments column.
            Supported formats: JPG, PNG, PDF, DOC, DOCX, XLS, XLSX (Max size:
            10MB each)
          </small>
        </div>
      </Modal>

      {/* Edit/Add Work Line Modal (for all source types: PM, BOQ, New Work Line) */}
      <Modal
        title={
          editingLineIndex >= 0
            ? "Edit Work Line (Attachments & Details)"
            : "Add New Work Line"
        }
        visible={isNewWorkLineModalVisible}
        onCancel={() => {
          setIsNewWorkLineModalVisible(false);
          setEditingLineIndex(-1); // Reset editing index on cancel
          // Reset cascading dropdown options
          setFloorOptions([]);
          setFlatOptions([]);
          prevTowerVillaRef.current = "";
          prevFloorNameRef.current = "";
          newWorkLineFormMethods.reset({
            workCategory: "",
            subCategory: "",
            taskTitle: "",
            taskDescription: "",
            quantity: "",
            uom: "",
            weightagePercentage: "",
            rate: "",
            taxType: "",
            taxPercentage: "",
            budgetLineId: "",
            budgetAllocation: "",
            towerVilla: "",
            floorName: "",
            flatName: "",
            locationId: "",
            scheduledStartDate: "",
            scheduledEndDate: "",
          });
          // Reset schedule breakdown state
          setNewLineStages([]);
          setNewLineScheduleEnabled(false);
          setNewLineDeletedStageIds([]);
          // Clear file list on cancel
          setNewWorkLineFileList([]);
        }}
        width={800}
        footer={[
          <AntdButton
            key="cancel"
            onClick={() => {
              setIsNewWorkLineModalVisible(false);
              setEditingLineIndex(-1); // Reset editing index on cancel
              // Reset cascading dropdown options
              setFloorOptions([]);
              setFlatOptions([]);
              prevTowerVillaRef.current = "";
              prevFloorNameRef.current = "";
              newWorkLineFormMethods.reset({
                workCategory: "",
                subCategory: "",
                taskTitle: "",
                taskDescription: "",
                quantity: "",
                uom: "",
                weightagePercentage: "",
                rate: "",
                taxType: "",
                taxPercentage: "",
                budgetLineId: "",
                budgetAllocation: "",
                towerVilla: "",
                floorName: "",
                flatName: "",
                locationId: "",
                scheduledStartDate: "",
                scheduledEndDate: "",
              });
              // Reset schedule breakdown state
              setNewLineStages([]);
              setNewLineScheduleEnabled(false);
              setNewLineDeletedStageIds([]);
            }}
          >
            Cancel
          </AntdButton>,
          <AntdButton
            key="save"
            type="primary"
            icon={<CheckOutlined />}
            onClick={handleNewWorkLineSubmit}
          >
            {editingLineIndex >= 0 ? "Update Work Line" : "Add Work Line"}
          </AntdButton>,
        ]}
      >
        <FormProvider {...newWorkLineFormMethods}>
          <div className="p-3">
            <Row>
              <Col lg="6">
                <FormSelect
                  name="workCategory"
                  label="Work Category"
                  options={workCategoryOptions}
                  required={true}
                  placeholder="Select Work Category"
                />
              </Col>
              <Col lg="6">
                <FormInput
                  name="taskTitle"
                  label="Task Title"
                  placeholder="Enter task title"
                  required={true}
                />
              </Col>
              {/* <Col lg="6">
                <FormInput
                  name="subCategory"
                  label="Sub Category"
                  placeholder="Enter sub category"
                />
              </Col> */}
            </Row>

            <Row className="mt-4">
              <Col lg="12">
                <FormInput
                  name="taskDescription"
                  label="Task Description"
                  placeholder="Enter task description"
                />
              </Col>
            </Row>

            {/* Date Fields Section - Only show for New Work Line source */}
            {selectedSourceWatch === WorkOrderLineSource.NewWorkLine && (
              <Row className="mt-4">
                <Col lg="6">
                  <FormDatePicker
                    name="scheduledStartDate"
                    label="Planned Start Date"
                    required={false}
                    minDate={(() => {
                      const workOrderStartDate = getValues("plannedStartDate");
                      return workOrderStartDate ? dayjs(workOrderStartDate).toDate() : undefined;
                    })()}
                    maxDate={(() => {
                      const workOrderEndDate = getValues("plannedEndDate");
                      return workOrderEndDate ? dayjs(workOrderEndDate).toDate() : undefined;
                    })()}
                    customValidation={{
                      validate: (value: any) => {
                        // Skip validation if no value provided (optional field)
                        if (!value) return true;

                        const endDate = newWorkLineFormMethods.getValues("scheduledEndDate");
                        const workOrderStartDate = getValues("plannedStartDate");
                        const workOrderEndDate = getValues("plannedEndDate");

                        // If end date is provided, validate the range
                        if (endDate) {
                          const validation = validateTaskDates(value, endDate);
                          if (!validation.valid) {
                            return validation.message || "Invalid date range";
                          }
                        }

                        // If only start date is provided, validate it's within work order dates
                        if (workOrderStartDate && dayjs(value).isBefore(dayjs(workOrderStartDate), 'day')) {
                          return `Planned Start Date cannot be before Work Order Start Date (${dayjs(workOrderStartDate).format('DD-MMM-YYYY')})`;
                        }

                        if (workOrderEndDate && dayjs(value).isAfter(dayjs(workOrderEndDate), 'day')) {
                          return `Planned Start Date cannot be after Work Order End Date (${dayjs(workOrderEndDate).format('DD-MMM-YYYY')})`;
                        }

                        return true;
                      },
                    }}
                  />
                </Col>
                <Col lg="6">
                  <FormDatePicker
                    name="scheduledEndDate"
                    label="Planned End Date"
                    required={false}
                    minDate={(() => {
                      const startDate = newWorkLineFormMethods.watch("scheduledStartDate");
                      const workOrderStartDate = getValues("plannedStartDate");

                      if (startDate) {
                        // If start date is selected, min date is the later of start date and work order start date
                        const start = dayjs(startDate);
                        const woStart = workOrderStartDate ? dayjs(workOrderStartDate) : null;

                        if (woStart && start.isBefore(woStart)) {
                          return woStart.toDate();
                        }
                        return start.toDate();
                      }
                      return workOrderStartDate ? dayjs(workOrderStartDate).toDate() : undefined;
                    })()}
                    maxDate={(() => {
                      const workOrderEndDate = getValues("plannedEndDate");
                      return workOrderEndDate ? dayjs(workOrderEndDate).toDate() : undefined;
                    })()}
                    customValidation={{
                      validate: (value: any) => {
                        // Skip validation if no value provided (optional field)
                        if (!value) return true;

                        const startDate = newWorkLineFormMethods.watch("scheduledStartDate");
                        const workOrderStartDate = getValues("plannedStartDate");
                        const workOrderEndDate = getValues("plannedEndDate");

                        // Validate with start date if both are provided
                        if (startDate) {
                          const validation = validateTaskDates(startDate, value);
                          if (!validation.valid) {
                            return validation.message || "Invalid date range";
                          }
                        }

                        // Validate against work order end date
                        if (workOrderEndDate && dayjs(value).isAfter(dayjs(workOrderEndDate), 'day')) {
                          return `Planned End Date cannot be after Work Order End Date (${dayjs(workOrderEndDate).format('DD-MMM-YYYY')})`;
                        }

                        // Validate against work order start date (if no start date provided)
                        if (!startDate && workOrderStartDate && dayjs(value).isBefore(dayjs(workOrderStartDate), 'day')) {
                          return `Planned End Date cannot be before Work Order Start Date (${dayjs(workOrderStartDate).format('DD-MMM-YYYY')})`;
                        }

                        return true;
                      },
                    }}
                  />
                </Col>
              </Row>
            )}

            <Row className="mt-4">
              {workTypeWatch !== WorkType.Open && (
                <Col lg="4">
                  <FormInput
                    name="quantity"
                    label="Quantity"
                    type="number"
                    placeholder="Enter quantity"
                    required={true}
                    min={0.01}
                    disabled={workTypeWatch === WorkType.Open}
                    customValidation={{
                      validate: (value: string) => {
                        if (workTypeWatch === WorkType.Open) return true;
                        const num = parseFloat(value);
                        if (isNaN(num) || num <= 0)
                          return "Quantity must be greater than 0";
                        return true;
                      },
                    }}
                  />
                </Col>
              )}
              <Col lg={workTypeWatch === WorkType.Open ? "6" : "4"}>
                <FormSelect
                  name="uom"
                  label="UOM"
                  options={uomOptions}
                  required={true}
                  placeholder="Select UOM"
                />
              </Col>
              <Col lg={workTypeWatch === WorkType.Open ? "6" : "4"}>
                <FormInput
                  name="weightagePercentage"
                  label="Weightage %"
                  type="number"
                  placeholder="Weightage %"
                  min={0}
                />
              </Col>
            </Row>

            <Row className="mt-4">
              <Col lg="6">
                <FormInput
                  name="rate"
                  label="Rate"
                  type="number"
                  placeholder="Enter rate"
                  required={true}
                  min={0.01}
                  customValidation={{
                    validate: (value: string) => {
                      const num = parseFloat(value);
                      if (isNaN(num) || num <= 0)
                        return "Rate must be greater than 0";
                      return true;
                    },
                  }}
                />
              </Col>
              {enableTaxWatch === "true" &&
                (taxApplicationLevelWatch === TaxApplicationLevel.Line ||
                  taxApplicationLevelWatch === TaxApplicationLevel.Both) && (
                  <Col lg="6">
                    <FormSelect
                      name="taxType"
                      label="Tax"
                      options={taxOptions}
                      placeholder="Select Tax Type"
                      required={enableTaxWatch === "true"}
                    />
                  </Col>
                )}
            </Row>

            {/* Schedule Breakdown Section - Only for Quantity work type */}
            {workTypeWatch === WorkType.QTY && (
              <Row className="mt-4">
                <Col lg="12">
                  <StageBreakdown
                    rate={parseFloat(newWorkLineFormMethods.watch("rate")) || 0}
                    stages={newLineStages}
                    onChange={setNewLineStages}
                    showToggle={true}
                    isEnabled={newLineScheduleEnabled}
                    onToggleChange={setNewLineScheduleEnabled}
                    deletedStageIds={newLineDeletedStageIds}
                    onDeletedStageIdsChange={setNewLineDeletedStageIds}
                  />
                </Col>
              </Row>
            )}

            {/* Work Level Settings Section */}
            {/* <Row className="mt-4">

            </Row> */}

            {/* Conditional Location Fields Based on Work Level */}
            {workLevelWatch === "Tower / Block" && (
              <Row className="mt-4">
                <Col lg="6">
                  <FormSelect
                    name="towerVilla"
                    label="Tower / Block / Villa"
                    options={towerVillaOptions}
                    required={true}
                    placeholder="Select Tower / Block / Villa"
                  />
                </Col>
                <Col lg="6">
                  <FormSelect
                    name="locationId"
                    label="Location"
                    options={locationOptions}
                    placeholder="Select Location (Optional)"
                  />
                </Col>
              </Row>
            )}

            {workLevelWatch === "Floor" && (
              <Row className="mt-4">
                <Col lg="4">
                  <FormSelect
                    name="towerVilla"
                    label="Tower / Block / Villa"
                    options={towerVillaOptions}
                    required={true}
                    placeholder="Select Tower / Block / Villa"
                  />
                </Col>
                <Col lg="4">
                  <FormSelect
                    name="floorName"
                    label="Floor"
                    options={floorOptions}
                    required={true}
                    placeholder="Select Floor"
                  />
                </Col>
                <Col lg="4">
                  <FormSelect
                    name="locationId"
                    label="Location"
                    options={locationOptions}
                    placeholder="Select Location (Optional)"
                  />
                </Col>
              </Row>
            )}

            {workLevelWatch === "Flat" && (
              <Row className="mt-4">
                <Col lg="3">
                  <FormSelect
                    name="towerVilla"
                    label="Tower / Block / Villa"
                    options={towerVillaOptions}
                    required={true}
                    placeholder="Select Tower / Block / Villa"
                  />
                </Col>
                <Col lg="3">
                  <FormSelect
                    name="floorName"
                    label="Floor"
                    options={floorOptions}
                    required={true}
                    placeholder="Select Floor"
                  />
                </Col>
                <Col lg="3">
                  <FormSelect
                    name="flatName"
                    label="Flat"
                    options={flatOptions}
                    required={false}
                    placeholder="Select Flat"
                  />
                </Col>
                <Col lg="3">
                  <FormSelect
                    name="locationId"
                    label="Location"
                    options={locationOptions}
                    placeholder="Select Location (Optional)"
                  />
                </Col>
              </Row>
            )}

            {workLevelWatch === "Villa" && (
              <Row className="mt-4">
                <Col lg="6">
                  <FormSelect
                    name="flatName"
                    label="Villa"
                    options={towerVillaOptions}
                    required={true}
                    placeholder="Select Villa"
                  />
                </Col>
                <Col lg="6">
                  <FormSelect
                    name="locationId"
                    label="Location"
                    options={locationOptions}
                    placeholder="Select Location (Optional)"
                  />
                </Col>
              </Row>
            )}

            {/* Budget Line Selection - Show only if budget is selected */}
            {selectedBudgetId && budgetLines.length > 0 && (
              <Row className="mt-4">
                <Col lg="6">
                  <div className="form-group">
                    <label className="form-label">
                      Budget Line <span className="text-danger">*</span>
                    </label>
                    <Select
                      placeholder="Select Budget Line"
                      value={
                        newWorkLineFormMethods.watch("budgetLineId") ||
                        undefined
                      }
                      onChange={(value) => {
                        newWorkLineFormMethods.setValue("budgetLineId", value);
                        // Don't clear budgetAllocation - let auto-calculation handle it
                      }}
                      loading={budgetLinesLoading}
                      style={{ width: "100%", minHeight: "38px" }}
                      dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                    >
                      {budgetLines.map((budgetLine: any) => {
                        const availableBudget = calculateAvailableBudget(
                          budgetLine.lineId,
                          budgetLine.budgetValue,
                          budgetLine.allocatedLineValue,
                          editingLineIndex // Exclude current line's allocation when editing
                        );
                        return (
                          <Select.Option
                            key={budgetLine.lineId}
                            value={budgetLine.lineId.toString()}
                            disabled={availableBudget <= 0}
                          >
                            {budgetLine.budgetLineName}
                            {availableBudget <= 0 && (
                              <span
                                style={{
                                  color: "#ff4d4f",
                                  fontSize: "11px",
                                  marginLeft: "8px",
                                }}
                              >
                                (No budget available)
                              </span>
                            )}
                          </Select.Option>
                        );
                      })}
                    </Select>
                    {budgetLines.length === 0 && !budgetLinesLoading && (
                      <small className="form-text text-muted">
                        No budget lines available for the selected budget
                      </small>
                    )}
                    {newWorkLineFormMethods.watch("budgetLineId") && (
                      <div
                        className="mt-2"
                        style={{
                          display: "flex",
                          gap: "16px",
                          fontSize: "12px",
                        }}
                      >
                        <span className="text-muted">
                          Budget:{" "}
                          <strong>
                            ₹
                            {(() => {
                              const selectedBudgetLineId =
                                newWorkLineFormMethods.watch("budgetLineId");
                              const budgetLine = budgetLines.find(
                                (bl: any) =>
                                  bl.lineId.toString() === selectedBudgetLineId
                              );
                              return budgetLine
                                ? budgetLine.budgetValue.toLocaleString("en-IN")
                                : "0";
                            })()}
                          </strong>
                        </span>
                        <span
                          style={{
                            color: (() => {
                              const selectedBudgetLineId =
                                newWorkLineFormMethods.watch("budgetLineId");
                              const budgetLine = budgetLines.find(
                                (bl: any) =>
                                  bl.lineId.toString() === selectedBudgetLineId
                              );
                              if (budgetLine) {
                                const available = calculateAvailableBudget(
                                  budgetLine.lineId,
                                  budgetLine.budgetValue,
                                  budgetLine.allocatedLineValue,
                                  editingLineIndex // Exclude current line's allocation when editing
                                );
                                return available <= 0 ? "#ff4d4f" : "#52c41a";
                              }
                              return "#666";
                            })(),
                            fontWeight: "600",
                          }}
                        >
                          Available: ₹
                          {(() => {
                            const selectedBudgetLineId =
                              newWorkLineFormMethods.watch("budgetLineId");
                            const budgetLine = budgetLines.find(
                              (bl: any) =>
                                bl.lineId.toString() === selectedBudgetLineId
                            );
                            if (budgetLine) {
                              return calculateAvailableBudget(
                                budgetLine.lineId,
                                budgetLine.budgetValue,
                                budgetLine.allocatedLineValue,
                                editingLineIndex // Exclude current line's allocation when editing
                              ).toLocaleString("en-IN");
                            }
                            return "0";
                          })()}
                        </span>
                      </div>
                    )}
                  </div>
                </Col>
                <Col lg="6">
                  <FormInput
                    name="budgetAllocation"
                    label="Allocated Budget"
                    type="number"
                    placeholder="Enter allocated amount"
                    min={0}
                    customValidation={{
                      validate: (value: string) => {
                        const selectedBudgetLineId =
                          newWorkLineFormMethods.watch("budgetLineId");
                        if (!selectedBudgetLineId || !value) return true; // Optional if no budget line selected

                        const num = parseFloat(value);
                        if (isNaN(num) || num <= 0)
                          return "Allocated amount must be greater than 0";

                        const budgetLine = budgetLines.find(
                          (bl: any) =>
                            bl.lineId.toString() === selectedBudgetLineId
                        );
                        if (budgetLine) {
                          const availableBudget = calculateAvailableBudget(
                            budgetLine.lineId,
                            budgetLine.budgetValue,
                            budgetLine.allocatedLineValue,
                            editingLineIndex // Exclude current line's allocation when editing
                          );
                          if (num > availableBudget) {
                            return `Allocated amount cannot exceed available budget (₹${availableBudget.toLocaleString("en-IN")})`;
                          }
                        }
                        return true;
                      },
                    }}
                  />
                </Col>
              </Row>
            )}

            <Row className="mt-4">
              <Col lg="12">
                <div className="form-group">
                  <label className="form-label">
                    <strong>Attachments</strong>
                  </label>
                  <Upload
                    name="file"
                    listType="text"
                    fileList={newWorkLineFileList}
                    beforeUpload={(file) => {
                      // Validate file - return false if invalid (prevents adding), true if valid (allows adding)
                      const isValid = validateFileBeforeUpload(file);
                      // If valid, return true to allow file to be added to fileList (will be handled by customRequest)
                      // If invalid, validation already showed error message, return false to prevent adding
                      return isValid;
                    }}
                    onChange={(info) => {
                      handleFileChange(info, "newWorkLine");
                    }}
                    onRemove={(file) => handleFileRemove(file, "newWorkLine")}
                    multiple
                    customRequest={({ file, onSuccess }) => {
                      // Custom request handler - prevent actual upload, just simulate success
                      // This allows files to be added to fileList but doesn't actually upload
                      if (onSuccess) {
                        setTimeout(() => onSuccess("ok"), 0);
                      }
                    }}
                  >
                    <AntdButton
                      icon={<UploadOutlined />}
                      loading={uploadLoading}
                      disabled={uploadLoading}
                      size="middle"
                    >
                      {uploadLoading ? "Uploading..." : "Upload Attachments"}
                    </AntdButton>
                  </Upload>
                  <small className="form-text text-muted">
                    Supported formats: JPG, PNG, PDF, DOC, DOCX, XLS, XLSX (Max
                    size: 10MB each)
                  </small>
                </div>
              </Col>
            </Row>
          </div>
        </FormProvider>
      </Modal>

      {/* Schedule Breakdown Drawer for PM/BOQ/New Work Lines */}
      <Drawer
        title={
          scheduleEditingLineIndex >= 0
            ? `Schedule Breakdown - ${workOrderLinesWatch[scheduleEditingLineIndex]?.taskTitle ||
            "Line"
            }`
            : "Schedule Breakdown"
        }
        placement="right"
        width={600}
        onClose={handleCloseScheduleDrawer}
        open={scheduleDrawerVisible}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <AntdButton onClick={handleCloseScheduleDrawer}>Cancel</AntdButton>
            <AntdButton type="primary" onClick={handleSaveScheduleBreakdown}>
              Save Changes
            </AntdButton>
          </div>
        }
      >
        {scheduleEditingLineIndex >= 0 && (
          <div>
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                backgroundColor: "#f5f5f5",
                borderRadius: 4,
              }}
            >
              <Row>
                <Col md={6}>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ fontWeight: 500, color: "#666" }}>
                      Task:
                    </span>{" "}
                    {workOrderLinesWatch[scheduleEditingLineIndex]?.taskTitle ||
                      "-"}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ fontWeight: 500, color: "#666" }}>
                      Source:
                    </span>{" "}
                    {workOrderLinesWatch[scheduleEditingLineIndex]?.source ||
                      "-"}
                  </div>
                </Col>
                <Col md={6}>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ fontWeight: 500, color: "#666" }}>
                      Rate:
                    </span>{" "}
                    ₹
                    {(
                      workOrderLinesWatch[scheduleEditingLineIndex]?.rate || 0
                    ).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ fontWeight: 500, color: "#666" }}>
                      Quantity:
                    </span>{" "}
                    {workOrderLinesWatch[scheduleEditingLineIndex]?.quantity ||
                      0}{" "}
                    {workOrderLinesWatch[scheduleEditingLineIndex]?.uomName ||
                      workOrderLinesWatch[scheduleEditingLineIndex]?.uom ||
                      ""}
                  </div>
                </Col>
              </Row>
            </div>

            <StageBreakdown
              rate={workOrderLinesWatch[scheduleEditingLineIndex]?.rate || 0}
              stages={scheduleLineStages}
              onChange={setScheduleLineStages}
              showToggle={false}
              isEnabled={true}
              deletedStageIds={scheduleDeletedStageIds}
              onDeletedStageIdsChange={setScheduleDeletedStageIds}
            />
          </div>
        )}
      </Drawer>
    </>
  );
};

export default WorkOrderDetails;
